import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChartLayout, EquityContent, MethodologyContent, OverviewContent, RelevanceContent } from '../charts/ChartLayout';
// ChartRendererSupabase is already imported above in this file; keep single import
import { useLocation } from '../../contexts/LocationContext';
import { useAdminPageSettings } from '../../hooks/useAdminPageSettings';
import { fetchHeatPumps } from '../../services/heatPumpService';
import { supabaseClient } from '../../services/supabase';
import { setBuildingsEnergyOverrideFromEnergyPage } from '../../services/buildingsEnergyDataService';
import { downloadChartAsPNG, downloadDataAsCSV, formatFilename } from '../../utils/downloadUtils';
import { ChartRendererSupabase } from '../charts/ChartRendererSupabase';
import { DataTable } from '../charts/DataTable';
import {
  COMMERCIAL_MODEL_OPTIONS,
  CommercialExtrapolationModel,
  RESIDENTIAL_MODEL_OPTIONS,
  ResidentialExtrapolationModel,
  classifyEnergyRowProvenance,
  formatCommercialModelLabelWithSmape,
  fetchElectricitySeries,
  fetchNaturalGasSeries
} from '../charts/EnergyUseSupabase';
import { HeatPumpCostChart } from '../charts/HeatPumpCostChart';
import { HeatPumpTechnologyChart } from '../charts/HeatPumpTechnologyChart';
import { HeatPumpUnitsChart } from '../charts/HeatPumpUnitsChart';
import { HeatPumpPerCapitaChart } from '../charts/HeatPumpPerCapitaChart';
import { KeyMetric, KeyMetrics } from '../charts/KeyMetrics';
import fetchSolarPVCapacitySupabase from '../charts/SolarPVCapacitySupabase';
import fetchSolarPVCostSupabase from '../charts/SolarPVCostSupabase';
import fetchSolarPVProjectCountSupabase from '../charts/SolarPVProjectCountSupabase';
import fetchSolarPVBatteryCostSupabase from '../charts/SolarPVBatteryCostSupabase';
import fetchSolarPVInstallersSupabase from '../charts/SolarPVInstallersSupabase';
import fetchSolarPVInstallersCostSupabase from '../charts/SolarPVInstallersCostSupabase';
import { PageHeader } from '../layout/PageHeader';
import { fetchAllPvSize } from '../../services/allPvSizeService';
import { fetchSolarTechnicalPotential, type SolarPotentialResult } from '../../services/solarTechnicalPotentialService';
import { CumulativeSolarCapacityChart } from '../charts/CumulativeSolarCapacityChart';
import { SolarPerCapitaComparisonChart } from '../charts/SolarPerCapitaComparisonChart';
import CarbonIntensityChart from '../charts/CarbonIntensityChart';
import { ElectricityPricesByGeographyChart, type ElectricityPricesChartView } from '../charts/ElectricityPricesByGeographyChart';
import { getLatestAveragePrice, type ElectricityPricesByGeographyResult } from '../../services/electricityPricesByGeographyService';
import CaliforniaBayRENTool from '../../domains/bayren/components/CaliforniaBayRENTool';
import { CustomSectionsForPage } from '../customContent/CustomSectionsForPage';
import { CustomDatasetsForPage } from '../customContent/CustomDatasetsForPage';
import { DataReviewBanner } from '../charts/DataReviewBanner';
import { isElectricityDataUnderReview, getLocationNameUnderReview, isElectricityDataUnderReviewByName, getGeofipsFromLocationName } from '../../utils/dataReviewLocations';
import BusinessEstablishmentsSection from '../charts/BusinessEstablishmentsSection';
import { HousingToEmployees } from '../charts/HousingToEmployees';
import GenericMapTiered from '../charts/GenericMapTiered';
import UrbanHeatIslandMap from '../charts/UrbanHeatIslandMap';
import SingleFamilyDetached from '../charts/SingleFamilyDetached';
import MarketRentChart from '../charts/MarketRentChart';
import HouseHeatingFuelSupabase, { type HeatingFuelMetrics } from '../charts/HouseHeatingFuelSupabase';
import LeadEnergyCostBurdenSection from '../charts/LeadEnergyCostBurdenSection';
import SectionWrapper, { useSectionReveal } from './SectionWrapper';
import AffordableHousingAprSection from '../charts/AffordableHousingAprSection';

import electricityBg from '../../assets/buildings/electricity-bg.jpg';
import businessEstablishmentsBg from '../../assets/buildings/business-establishments-bg.jpg';
import solarPvBg from '../../assets/buildings/solar-pv-bg.jpg';
import heatPumpBg from '../../assets/buildings/heat-pump-bg.jpg';
import carbonIntensityBg from '../../assets/buildings/carbon-intensity-real-bg.jpg';
import electricityPricesBg from '../../assets/buildings/electricity-prices-real-bg.jpg';
import naturalGasRealBg from '../../assets/buildings/natural-gas-consumption-bg.jpg';
import heatingFuelBg from '../../assets/buildings/heating-fuel-real-bg.jpg';
import housingToEmployeesBg from '../../assets/buildings/housing-to-employees-bg.jpg';
import singleFamilyBg from '../../assets/buildings/single-family-bg.jpg';
import ownerOccupiedBg from '../../assets/buildings/owner-occupied-bg.jpg';
import marketRentBg from '../../assets/buildings/market-rent-bg.jpg';
import californiaBg from '../../assets/buildings/california-bg.jpg';

const BUILDINGS_SECTION_BG: Record<string, string> = {
  electricity_consumption: electricityBg,
  carbon_intensity: carbonIntensityBg,
  electricity_prices: electricityPricesBg,
  energy_cost_burden: electricityPricesBg,
  natural_gas_consumption: naturalGasRealBg,
  heating_fuel: heatingFuelBg,
  solar_pv: solarPvBg,
  heat_pump: heatPumpBg,
  business_establishments: businessEstablishmentsBg,
  housing_to_employees: housingToEmployeesBg,
  single_family_detached: singleFamilyBg,
  owner_occupied_map: ownerOccupiedBg,
  urban_heat_island_map: heatPumpBg,
  housing_rent_category: marketRentBg,
  affordable_housing_production: californiaBg,
  bayren_tool: californiaBg,
};

const BUILDINGS_SECTION_IDS = Object.keys(BUILDINGS_SECTION_BG);


const sumDatasetsByYear = (labels: number[], datasets: any[]): Map<number, number> => {
  const map = new Map<number, number>();
  labels.forEach((year: number, i: number) => {
    let total = 0;
    datasets.forEach((dataset: any) => { total += (dataset.data?.[i] || 0); });
    map.set(year, total);
  });
  return map;
};

const makeDownloadPNG = (selector: string, label: string, location: string) => {
  const canvas = document.querySelector(`${selector} canvas`) as HTMLCanvasElement;
  if (canvas) {
    const filename = formatFilename(location, label);
    const link = document.createElement('a');
    link.download = `${filename}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  }
};

const buildChartOptions = (overrides: {
  type?: string;
  title: string;
  tooltipSuffix?: string;
  tooltipFormat?: (v: number) => string;
  xStacked?: boolean;
  yStacked?: boolean;
  yTitle?: string;
  yTickFormat?: (v: any) => string;
}) => ({
  ...(overrides.type ? { type: overrides.type } : {}),
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom' as const,
      labels: { padding: 10, usePointStyle: true, pointStyle: 'rect' }
    },
    title: { display: true, text: overrides.title },
    tooltip: {
      callbacks: {
        label: (ctx: any) => {
          const v = Number(ctx.parsed.y);
          if (overrides.tooltipFormat) {
            return `${ctx.dataset.label}: ${Number.isFinite(v) ? overrides.tooltipFormat(v) : v}`;
          }
          const formatted = Number.isFinite(v) ? v.toLocaleString() : v;
          return overrides.tooltipSuffix
            ? `${ctx.dataset.label}: ${formatted} ${overrides.tooltipSuffix}`
            : `${ctx.dataset.label}: ${formatted}`;
        }
      }
    }
  },
  scales: {
    x: {
      ...(overrides.xStacked ? { stacked: true } : {}),
      title: { display: true, text: 'Year' }
    },
    y: {
      ...(overrides.yStacked ? { stacked: true } : {}),
      beginAtZero: true,
      title: { display: true, text: overrides.yTitle || '' },
      ...(overrides.yTickFormat ? { ticks: { callback: overrides.yTickFormat } } : {})
    }
  },
  layout: { padding: { bottom: 20 } }
});

const buildTableData = (
  resChartData: any,
  comChartData: any,
  location: string
) => {
  const resLabels = (resChartData.labels || []) as number[];
  const comLabels = (comChartData.labels || []) as number[];
  const resYears = new Set(resLabels);
  const comYears = new Set(comLabels);
  const years: number[] = Array.from(resYears)
    .filter((year: number) => comYears.has(year))
    .sort();
  const resMap = sumDatasetsByYear(resLabels, resChartData.datasets || []);
  const comMap = sumDatasetsByYear(comLabels, comChartData.datasets || []);
  return years.map((year) => ({
    year,
    residential: resMap.get(year) || 0,
    commercial: comMap.get(year) || 0,
    total: (resMap.get(year) || 0) + (comMap.get(year) || 0),
    location
  }));
};

function useFilteredChartOptions(
  sectionId: string,
  options: { id: string; value: string; label: string }[],
  view: string,
  setView: (v: any) => void,
  isChartEnabled: (section: string, chart: string) => boolean,
  getChartOrder: (section: string, chart: string) => number
): { id: string; value: string; label: string }[] {
  const allowed = options
    .filter((option) => isChartEnabled(sectionId, option.id))
    .sort((a, b) => getChartOrder(sectionId, a.id) - getChartOrder(sectionId, b.id));
  React.useEffect(() => {
    if (allowed.length && !allowed.some((opt) => opt.value === view)) {
      setView(allowed[0].value);
    }
  }, [allowed, view, setView]);
  return allowed;
}

function useCommercialModelOptions(
  comData: any,
  energyType: 'electricity' | 'naturalGas',
  extrapModel: CommercialExtrapolationModel,
  setExtrapModel: (v: CommercialExtrapolationModel) => void
) {
  const available = React.useMemo(() => {
    const allowed = comData?.availableCommercialModels;
    const allowSet =
      Array.isArray(allowed) && allowed.length > 0 ? new Set(allowed) : null;
    let opts = COMMERCIAL_MODEL_OPTIONS.filter((o) =>
      allowSet ? allowSet.has(o.value) : true
    );
    if (opts.length === 0) {
      opts = COMMERCIAL_MODEL_OPTIONS.filter((o) => o.value === 'average');
    }
    return opts.map((o) => ({
      ...o,
      label: formatCommercialModelLabelWithSmape(
        o.value,
        energyType,
        comData?.commercialObservedInterpolatedYears
      )
    }));
  }, [comData, energyType]);
  React.useEffect(() => {
    if (!available.length) return;
    if (!available.some((o) => o.value === extrapModel)) {
      const fallback =
        available.find((o) => o.value === 'average')?.value ||
        available[0].value;
      setExtrapModel(fallback);
    }
  }, [available, extrapModel, setExtrapModel]);
  return available;
}

function useEditableDataTable(
  energyType: 'electricity' | 'naturalGas',
  resData: any,
  comData: any,
  stackedData: any,
  setResData: (v: any) => void,
  setComData: (v: any) => void,
  setStackedData: (v: any) => void,
  currentLocation: string
) {
  const [showDataTable, setShowDataTable] = React.useState(false);
  const [tableData, setTableData] = React.useState<any[]>([]);
  const [originalData, setOriginalData] = React.useState<any[]>([]);
  const [modifiedYears, setModifiedYears] = React.useState<Set<number>>(new Set());

  const toggleDataTable = () => setShowDataTable((prev) => !prev);

  const scaleDataToModifiedValue = (data: any[], modifiedYear: number, modifiedResidential: number, modifiedCommercial: number) => {
    const originalModifiedData = originalData.find((d) => d.year === modifiedYear);
    if (!originalModifiedData) return data;
    const resScale = modifiedResidential / originalModifiedData.residential;
    const comScale = modifiedCommercial / originalModifiedData.commercial;
    return data.map((item) => {
      if (modifiedYears.has(item.year)) return item;
      const orig = originalData.find((d) => d.year === item.year);
      if (!orig) return item;
      return {
        ...item,
        residential: Math.round(orig.residential * resScale),
        commercial: Math.round(orig.commercial * comScale),
        total: Math.round(orig.residential * resScale) + Math.round(orig.commercial * comScale)
      };
    });
  };

  const updateCharts = (data: any[]) => {
    if (!resData || !comData) return;
    const years = data.map((d) => d.year);
    const resValues = data.map((d) => d.residential);
    const comValues = data.map((d) => d.commercial);
    const colorize = (vals: any[], dataset: any) => ({
      ...dataset,
      data: vals,
      backgroundColor: vals.map((_: any, i: number) =>
        modifiedYears.has(years[i]) ? '#ef4444' : dataset.backgroundColor
      ),
      borderColor: vals.map((_: any, i: number) =>
        modifiedYears.has(years[i]) ? '#dc2626' : dataset.borderColor
      )
    });
    setResData({
      ...resData,
      chartData: { ...resData.chartData, labels: years, datasets: resData.chartData.datasets.map((ds: any) => colorize(resValues, ds)) }
    });
    setComData({
      ...comData,
      chartData: { ...comData.chartData, labels: years, datasets: comData.chartData.datasets.map((ds: any) => colorize(comValues, ds)) }
    });
    setStackedData({
      ...stackedData,
      chartData: {
        ...stackedData.chartData,
        labels: years,
        datasets: stackedData.chartData.datasets.map((ds: any) =>
          colorize(ds.label === 'Residential' ? resValues : comValues, ds)
        )
      }
    });
  };

  const handleDataTableChange = (newData: any[]) => {
    const newModifiedYears = new Set(modifiedYears);
    let modYear: number | null = null;
    let modRes: number | null = null;
    let modCom: number | null = null;
    newData.forEach((newItem) => {
      const cur = tableData.find((d) => d.year === newItem.year);
      if (cur && (Math.abs(newItem.residential - cur.residential) > 1 || Math.abs(newItem.commercial - cur.commercial) > 1)) {
        newModifiedYears.add(newItem.year);
        modYear = newItem.year;
        modRes = newItem.residential;
        modCom = newItem.commercial;
      }
    });
    let finalData = newData;
    if (modYear !== null && modRes !== null && modCom !== null) {
      finalData = scaleDataToModifiedValue(newData, modYear, modRes, modCom);
    }
    setModifiedYears(newModifiedYears);
    setTableData(finalData);
    setBuildingsEnergyOverrideFromEnergyPage(currentLocation, energyType, finalData);
    updateCharts(finalData);
  };

  const restoreDefaults = () => {
    if (originalData.length > 0) {
      setTableData([...originalData]);
      setModifiedYears(new Set());
      if (resData && comData) {
        setResData(resData);
        setComData(comData);
        setStackedData(stackedData);
      }
    }
  };

  return {
    showDataTable,
    tableData,
    setTableData,
    originalData,
    setOriginalData,
    modifiedYears,
    toggleDataTable,
    handleDataTableChange,
    restoreDefaults
  };
}

// ─── End extracted helpers ───────────────────────────────────────────

interface EnergyProps {
  location?: string;
}

interface HeatPumpRecord {
  year: number;
  system: string;
  units_installed: number;
  project_cost_per_unit_usd: number;
  annual_ghg_savings_mtco2e: number;
}

// Function to calculate stacked data as sum of residential + commercial
const calculateStackedData = (resData: any, comData: any, type: string, locationName?: string) => {
  if (!resData?.chartData || !comData?.chartData) return null;

  const resLabels = (resData.chartData.labels || []) as number[];
  const comLabels = (comData.chartData.labels || []) as number[];
  
  // Get years that exist in both residential and commercial data
  const resYears = new Set(resLabels);
  const comYears = new Set(comLabels);
  const allYears: number[] = Array.from(resYears).filter((year: number) => comYears.has(year)).sort();
  
  // Extract data from all datasets (observed, interpolated, extrapolated) for each sector
  const resDatasets = resData.chartData.datasets || [];
  const comDatasets = comData.chartData.datasets || [];
  
  const resMap = sumDatasetsByYear(resLabels, resDatasets);
  const comMap = sumDatasetsByYear(comLabels, comDatasets);

  // Calculate stacked values
  const stackedValues = allYears.map(year => 
    (resMap.get(year) as number || 0) + (comMap.get(year) as number || 0)
  );

  // Create stacked chart data
  const stackedChartData = {
    labels: allYears,
    datasets: [
      {
        label: 'Residential',
        data: allYears.map(year => resMap.get(year) || 0),
        backgroundColor: type === 'Electricity' ? '#1e40af' : '#166534',
        borderColor: type === 'Electricity' ? '#1e40af' : '#166534',
        borderWidth: 1,
        stack: 'total'
      },
      {
        label: 'Commercial',
        data: allYears.map(year => comMap.get(year) || 0),
        backgroundColor: type === 'Electricity' ? '#3b82f6' : '#22c55e',
        borderColor: type === 'Electricity' ? '#3b82f6' : '#22c55e',
        borderWidth: 1,
        stack: 'total'
      }
    ]
  };

  const stackedChartOptions = buildChartOptions({
    type: 'bar',
    title: locationName
      ? `Total ${type} Consumption${type === 'Electricity' ? ' (kWh)' : ' (Therms)'} - ${locationName}`
      : `Total ${type} (Stacked)`,
    xStacked: true,
    yStacked: true,
    yTitle: type === 'Electricity' ? 'kWh' : 'Therms',
    yTickFormat: (v: any) => Number(v).toLocaleString()
  });

  return {
    chartData: stackedChartData,
    chartOptions: stackedChartOptions,
    latestYear: Math.max(...allYears),
    latestValue: stackedValues[stackedValues.length - 1] || 0
  };
};

export const Energy: React.FC<EnergyProps> = ({ location: propLocation }) => {
  const { selectedLocation, selectedGeofips, coreDataLoading } = useLocation();
  const [marketRentChartView, setMarketRentChartView] = useState<'line' | 'stacked'>('stacked');
  const [marketRentNarrative, setMarketRentNarrative] = useState<string | null>(null);
  const [marketRentNarrativeStacked, setMarketRentNarrativeStacked] = useState<string | null>(null);
  const [marketRentTrend, setMarketRentTrend] = useState<{ higherRentIncreased: boolean; lowerRentDeclined: boolean } | null>(null);
  const [capacityData, setCapacityData] = useState<any>(null);
  const [costData, setCostData] = useState<any>(null);
  const [projectCountData, setProjectCountData] = useState<any>(null);
  const [elecRes, setElecRes] = useState<any>(null);
  const [elecCom, setElecCom] = useState<any>(null);
  const [elecStacked, setElecStacked] = useState<any>(null);
  const [gasRes, setGasRes] = useState<any>(null);
  const [gasCom, setGasCom] = useState<any>(null);
  const [gasStacked, setGasStacked] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<string>('overview');
  const [activeHeatingFuelView, setActiveHeatingFuelView] = useState<string>('overview');
  const [heatingFuelChartMode, setHeatingFuelChartMode] = useState<'main' | 'other' | 'fossil' | 'compare'>('main');
  const [heatingFuelMetrics, setHeatingFuelMetrics] = useState<HeatingFuelMetrics | null>(null);

  // Scroll-driven fade-in for each section
  const { visible: sectionVisible, refs: sectionObserverRefs } = useSectionReveal(
    BUILDINGS_SECTION_IDS,
    !loading && !coreDataLoading
  );
  const [elecView, setElecView] = useState<'res' | 'com' | 'total' | 'perCapita'>('total');
  const [commercialExtrapModel, setCommercialExtrapModel] = useState<CommercialExtrapolationModel>('average');
  const [commercialGasExtrapModel, setCommercialGasExtrapModel] = useState<CommercialExtrapolationModel>('average');
  const [residentialExtrapModel, setResidentialExtrapModel] = useState<ResidentialExtrapolationModel>('average');
  const [gasView, setGasView] = useState<'res' | 'com' | 'total' | 'perCapita'>('total');
  const [elecMethodology, setElecMethodology] = useState<{dataSources: string[], methods: string[]} | null>(null);
  const [gasMethodology, setGasMethodology] = useState<{dataSources: string[], methods: string[]} | null>(null);
  
  // Chart refs for download functionality
  const elecChartRef = useRef<HTMLCanvasElement>(null);
  const gasChartRef = useRef<HTMLCanvasElement>(null);
  const [pvView, setPvView] = useState<'capacity' | 'cost' | 'cumulative' | 'perCapita' | 'batteryCost' | 'installers' | 'installersCost' | 'potential'>('capacity');
  const [batteryCostData, setBatteryCostData] = useState<any>(null);
  const [installersData, setInstallersData] = useState<any>(null);
  const [installersCostData, setInstallersCostData] = useState<any>(null);
  const [solarPotentialData, setSolarPotentialData] = useState<SolarPotentialResult | null>(null);
  const [solarPerCapitaTrend, setSolarPerCapitaTrend] = useState<'increasing' | 'decreasing' | 'stable' | null>(null);
  
  // Data table state + handlers via shared hook (electricity & gas)
  // NOTE: hooks called here; destructured names match the original variable names used throughout JSX below.
  const elecDataTable = useEditableDataTable('electricity', elecRes, elecCom, elecStacked, setElecRes, setElecCom, setElecStacked, propLocation || selectedLocation || '');
  const gasDataTable = useEditableDataTable('naturalGas', gasRes, gasCom, gasStacked, setGasRes, setGasCom, setGasStacked, propLocation || selectedLocation || '');
  
  // Per capita data state
  const [elecPerCapitaData, setElecPerCapitaData] = useState<any>(null);
  const [gasPerCapitaData, setGasPerCapitaData] = useState<any>(null);
  const [californiaElecPerCapita, setCaliforniaElecPerCapita] = useState<any>(null);
  const [californiaGasPerCapita, setCaliforniaGasPerCapita] = useState<any>(null);
  const [heatPumpMetrics, setHeatPumpMetrics] = useState<any>(null);
  const [heatPumpView, setHeatPumpView] = useState<'cost' | 'technology' | 'units' | 'perCapita'>('units');
  const [activeCarbonIntensityView, setActiveCarbonIntensityView] = useState<string>('overview');
  const [ownerOccupiedHousingMetrics, setOwnerOccupiedHousingMetrics] = useState<{
    currentValue: number;
    californiaAverage: number;
    year: number;
    variable: string;
  } | null>(null);
  const [activeOwnerOccupiedView, setActiveOwnerOccupiedView] = useState<string>('overview');
  const [activeUrbanHeatIslandView, setActiveUrbanHeatIslandView] = useState<string>('overview');
  const [carbonIntensityData, setCarbonIntensityData] = useState<any>(null);
  const [electricityPricesData, setElectricityPricesData] = useState<ElectricityPricesByGeographyResult | null>(null);
  const [electricityPricesView, setElectricityPricesView] = useState<string>('overview');
  const [electricityPricesChartView, setElectricityPricesChartView] = useState<'byUtility' | 'locationVsCA'>('locationVsCA');
  const [electricityPricesSector, setElectricityPricesSector] = useState<string>('Residential');
  const [marketRentData, setMarketRentData] = useState<{ year: number; rent_category: string; estimate: number | null; percent: number | null }[]>([]);
  const [heatPumpChartData, setHeatPumpChartData] = useState<Record<string, string | number>[]>([]);

  const currentLocation = propLocation || selectedLocation;
  const marketRentKeyInsights = React.useMemo(() => {
    if (!marketRentData.length || !currentLocation) return null;

    const years = Array.from(new Set(marketRentData.map((r) => r.year))).filter((y) => Number.isFinite(y)).sort((a, b) => a - b);
    if (years.length < 2) return null;

    const earliestYear = years[0];
    const latestYear = years[years.length - 1];

    const formatCategoryLabel = (category: string): string => {
      const labelMap: Record<string, string> = {
        no_rent_paid: 'No Rent Paid',
        under_500: 'Under $500',
        '500_to_999': '$500-$999',
        '1000_to_1499': '$1,000-$1,499',
        '1500_and_over': '$1,500 and Over',
        '1500_to_1999': '$1,500-$1,999',
        '2000_to_2499': '$2,000-$2,499',
        '2500_to_2999': '$2,500-$2,999',
        '3000_and_over': '$3,000 and Over',
      };
      return labelMap[category] || category.replace(/_/g, ' ');
    };

    const getRowsForYear = (year: number) => marketRentData.filter((r) => r.year === year && r.rent_category && r.rent_category !== 'other');

    const latestRows = getRowsForYear(latestYear);
    const earliestRows = getRowsForYear(earliestYear);
    if (!latestRows.length || !earliestRows.length) return null;

    const hasPercent = latestRows.some((r) => r.percent != null && Number.isFinite(Number(r.percent)));
    const latestTotalEst = latestRows.reduce((s, r) => s + (Number(r.estimate) || 0), 0);
    const earliestTotalEst = earliestRows.reduce((s, r) => s + (Number(r.estimate) || 0), 0);

    const latestShares = latestRows.map((r) => {
      const pct =
        hasPercent && r.percent != null && Number.isFinite(Number(r.percent))
          ? Number(r.percent)
          : latestTotalEst > 0
          ? ((Number(r.estimate) || 0) / latestTotalEst) * 100
          : 0;
      return { category: r.rent_category, pct };
    });

    const largest = latestShares.sort((a, b) => b.pct - a.pct)[0];
    const largestCategoryLabel = largest ? formatCategoryLabel(largest.category) : '—';

    const isHigherRentCategory = (category: string) => {
      // Treat categories with lower bound >= 1500 as "higher-rent".
      const m = category.match(/^(\d+)_/);
      if (!m) return category.includes('1500');
      return Number(m[1]) >= 1500;
    };

    const shareForYear = (rows: typeof marketRentData, yearTotalEst: number) =>
      rows.reduce((sum, r) => {
        if (!isHigherRentCategory(r.rent_category)) return sum;
        if (hasPercent && r.percent != null && Number.isFinite(Number(r.percent))) return sum + Number(r.percent);
        return yearTotalEst > 0 ? sum + ((Number(r.estimate) || 0) / yearTotalEst) * 100 : sum;
      }, 0);

    const higherLatest = shareForYear(latestRows, latestTotalEst);
    const higherEarliest = shareForYear(earliestRows, earliestTotalEst);
    const delta = higherLatest - higherEarliest;
    const overallChange =
      Math.abs(delta) < 1
        ? 'no significant change'
        : delta > 0
        ? 'an overall increase'
        : 'an overall decrease';

    return {
      earliestYear,
      latestYear,
      largestCategoryLabel,
      overallChange,
    };
  }, [marketRentData, currentLocation]);
  const {
    isSectionEnabled,
    isChartEnabled,
    getSectionOrder,
    getChartOrder
  } = useAdminPageSettings('buildings', currentLocation);

  // Scroll to chart section when opening page with a hash (e.g. from livability index).
  useEffect(() => {
    const scrollToHash = () => {
      const hash = window.location.hash.slice(1);
      if (!hash) return;
      const el =
        document.getElementById(hash) ||
        document.querySelector(`[data-section-id="${hash}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };
    const hash = window.location.hash.slice(1);
    if (!hash) return;
    const t1 = setTimeout(scrollToHash, 150);
    const t2 = setTimeout(scrollToHash, 500);
    const onHashChange = () => {
      setTimeout(scrollToHash, 80);
      setTimeout(scrollToHash, 400);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener('hashchange', onHashChange);
    };
  }, []);

  const allowedElectricityOptions = useFilteredChartOptions('electricity_consumption', [
    { id: 'electricity_consumption', value: 'total', label: 'Total (Stacked)' },
    { id: 'elec_res', value: 'res', label: 'Residential' },
    { id: 'elec_com', value: 'com', label: 'Commercial' },
    { id: 'elec_percapita', value: 'perCapita', label: 'Per Capita' }
  ], elecView, setElecView, isChartEnabled, getChartOrder);

  const allowedNaturalGasOptions = useFilteredChartOptions('natural_gas_consumption', [
    { id: 'natural_gas_consumption', value: 'total', label: 'Total (Stacked)' },
    { id: 'gas_res', value: 'res', label: 'Residential' },
    { id: 'gas_com', value: 'com', label: 'Commercial' },
    { id: 'gas_percapita', value: 'perCapita', label: 'Per Capita' }
  ], gasView, setGasView, isChartEnabled, getChartOrder);

  const allowedSolarOptions = useFilteredChartOptions('solar_pv', [
    { id: 'solar_capacity', value: 'capacity', label: 'By Year' },
    { id: 'cumulative_solar_capacity', value: 'cumulative', label: 'Cumulative' },
    { id: 'solar_per_capita', value: 'perCapita', label: 'Per Capita' },
    { id: 'solar_potential', value: 'potential', label: 'Solar Potential' },
    { id: 'solar_cost', value: 'cost', label: 'Cost' },
    { id: 'solar_battery_cost', value: 'batteryCost', label: 'Residential Battery Cost' },
    { id: 'solar_installers_capacity', value: 'installers', label: 'Installers (Capacity)' },
    { id: 'solar_installers_cost', value: 'installersCost', label: 'Installers (Cost)' }
  ], pvView, setPvView, isChartEnabled, getChartOrder);

  const allowedHeatPumpOptions = useFilteredChartOptions('heat_pump', [
    { id: 'heat_pump_units', value: 'units', label: 'Units Installed' },
    { id: 'heat_pump_per_capita', value: 'perCapita', label: 'Per Capita' },
    { id: 'heat_pump_cost', value: 'cost', label: 'Installation Costs' },
    { id: 'heat_pump_technology', value: 'technology', label: 'Technology Mix' }
  ], heatPumpView, setHeatPumpView, isChartEnabled, getChartOrder);

  const allowedMarketRentOptions = useFilteredChartOptions('housing_rent_category', [
    { id: 'market_rent_line', value: 'line', label: 'Line' },
    { id: 'market_rent_stacked', value: 'stacked', label: 'Stacked' }
  ], marketRentChartView, setMarketRentChartView, isChartEnabled, getChartOrder);

  const availableCommercialModelOptions = useCommercialModelOptions(elecCom, 'electricity', commercialExtrapModel, setCommercialExtrapModel);
  const availableCommercialGasModelOptions = useCommercialModelOptions(gasCom, 'naturalGas', commercialGasExtrapModel, setCommercialGasExtrapModel);

  React.useEffect(() => {
    setHeatPumpChartData([]);
  }, [heatPumpView]);

  const getSectionStyle = (sectionId: string) => ({
    order: getSectionOrder(sectionId),
    display: isSectionEnabled(sectionId) ? 'block' : 'none'
  });

  const solarGroups = [
    {
      category: 'capacity',
      label: 'Capacity',
      options: allowedSolarOptions.filter((opt) => ['capacity', 'cumulative', 'perCapita', 'potential'].includes(opt.value))
    },
    {
      category: 'cost',
      label: 'Cost',
      options: allowedSolarOptions.filter((opt) => ['batteryCost', 'cost'].includes(opt.value))
    },
    {
      category: 'installers',
      label: 'Installers',
      options: allowedSolarOptions.filter((opt) => ['installers', 'installersCost'].includes(opt.value))
    }
  ].filter((group) => group.options.length > 0);

  // Function to calculate heat pump metrics
  const calculateHeatPumpMetrics = async (location: string, geofips: number) => {
    try {
      if (!geofips) return null;

      // Fetch heat pump data for the location
      const { data: locationData, error: locationError } = await fetchHeatPumps<HeatPumpRecord>({
        select: 'year, system, units_installed, project_cost_per_unit_usd, annual_ghg_savings_mtco2e',
        geofips
      });

      if (locationError) {
        console.error('Error fetching heat pump metrics:', locationError);
        return null;
      }

      if (!locationData || locationData.length === 0) {
        return null;
      }

      // Calculate metrics
      const totalUnits = locationData.reduce((sum, record) => sum + (record.units_installed || 0), 0);
      const totalGHGSavings = locationData.reduce((sum, record) => sum + (record.annual_ghg_savings_mtco2e || 0), 0);
      
      // Get latest year data
      const latestYear = Math.max(...locationData.map(d => d.year));
      const latestYearData = locationData.filter(d => d.year === latestYear);
      
      const latestYearUnits = latestYearData.reduce((sum, record) => sum + (record.units_installed || 0), 0);
      const latestYearGHGSavings = latestYearData.reduce((sum, record) => sum + (record.annual_ghg_savings_mtco2e || 0), 0);
      
      // Calculate average cost per unit
      const totalCost = locationData.reduce((sum, record) => {
        return sum + ((record.units_installed || 0) * (record.project_cost_per_unit_usd || 0));
      }, 0);
      const averageCostPerUnit = totalUnits > 0 ? totalCost / totalUnits : 0;
      
      // Get system breakdown
      const hvacData = locationData.filter(d => d.system === 'HVAC');
      const hpwhData = locationData.filter(d => d.system === 'HPWH');
      
      const hvacUnits = hvacData.reduce((sum, record) => sum + (record.units_installed || 0), 0);
      const hpwhUnits = hpwhData.reduce((sum, record) => sum + (record.units_installed || 0), 0);
      
      // Calculate year-over-year growth
      const years = Array.from(new Set(locationData.map(d => d.year))).sort();
      const firstYear = Math.min(...years);
      const lastYear = Math.max(...years);
      
      const firstYearData = locationData.filter(d => d.year === firstYear);
      const lastYearData = locationData.filter(d => d.year === lastYear);
      
      const firstYearUnits = firstYearData.reduce((sum, record) => sum + (record.units_installed || 0), 0);
      const lastYearUnits = lastYearData.reduce((sum, record) => sum + (record.units_installed || 0), 0);
      
      const growthRate = firstYearUnits > 0 ? ((lastYearUnits - firstYearUnits) / firstYearUnits) * 100 : 0;

      return {
        totalUnits,
        totalGHGSavings,
        latestYearUnits,
        latestYearGHGSavings,
        averageCostPerUnit,
        hvacUnits,
        hpwhUnits,
        growthRate,
        firstYear,
        lastYear,
        yearsObserved: years.length
      };
    } catch (error) {
      console.error('Error calculating heat pump metrics:', error);
      return null;
    }
  };

  const formatRentCategoryLabel = (category: string): string => {
    const labelMap: Record<string, string> = {
      no_rent_paid: 'No Rent Paid',
      under_500: 'Under $500',
      '500_to_999': '$500-$999',
      '1000_to_1499': '$1,000-$1,499',
      '1500_and_over': '$1,500 and Over',
      '1500_to_1999': '$1,500-$1,999',
      '2000_to_2499': '$2,000-$2,499',
      '2500_to_2999': '$2,500-$2,999',
      '3000_and_over': '$3,000 and Over',
    };
    return labelMap[category] || category.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const calculateMarketRentNarrative = useCallback(async (location: string, geofips: number): Promise<{ narrativeLine: string; narrativeStacked: string; higherRentIncreased: boolean; lowerRentDeclined: boolean } | null> => {
    try {
      if (!geofips) return null;

      const { data, error: fetchError } = await supabaseClient
        .from('market_rent_percent')
        .select('year, rent_category, estimate')
        .eq('geofips', geofips)
        .order('year', { ascending: true });

      if (fetchError) {
        console.error('Error fetching market rent data for narrative:', fetchError);
        return null;
      }
      if (!data || data.length === 0) return null;

      const yearToCategoryTotals = new Map<number, Map<string, number>>();
      for (const row of data as Array<{ year: number; rent_category: string; estimate: number | null }>) {
        if (row?.estimate == null) continue;
        const raw = row?.rent_category;
        if (!raw || raw === 'other') continue;
        const category = String(raw).trim().toLowerCase().replace(/\s+/g, '_');
        const year = row.year;
        if (!yearToCategoryTotals.has(year)) yearToCategoryTotals.set(year, new Map());
        const catMap = yearToCategoryTotals.get(year)!;
        catMap.set(category, (catMap.get(category) || 0) + row.estimate);
      }

      const years = Array.from(yearToCategoryTotals.keys()).sort((a, b) => a - b);
      if (years.length === 0) return null;
      const baselineYear = years[0];
      const latestYear = years[years.length - 1];

      const latestTotals = yearToCategoryTotals.get(latestYear) || new Map<string, number>();
      let topCategory: string | null = null;
      let topValue = -Infinity;
      latestTotals.forEach((value, category) => {
        if (value > topValue) {
          topValue = value;
          topCategory = category;
        }
      });
      const topLabel = topCategory ? formatRentCategoryLabel(topCategory) : 'N/A';
      const topPhrase = topLabel.includes('$')
        ? `fell within the ${topLabel} range`
        : `fell in the ${topLabel} category`;

      const higherCategories = new Set([
        '1500_and_over',
        '1500_to_1999',
        '2000_to_2499',
        '2500_to_2999',
        '3000_and_over',
      ]);
      const lowerCategories = new Set(['under_500', '500_to_999']);

      const sumForYear = (year: number, cats: Set<string>) => {
        const m = yearToCategoryTotals.get(year);
        if (!m) return 0;
        let sum = 0;
        cats.forEach((c) => {
          sum += m.get(c) || 0;
        });
        return sum;
      };

      const higherBaseline = sumForYear(baselineYear, higherCategories);
      const higherLatest = sumForYear(latestYear, higherCategories);
      const lowerBaseline = sumForYear(baselineYear, lowerCategories);
      const lowerLatest = sumForYear(latestYear, lowerCategories);

      const higherDelta = higherLatest - higherBaseline;
      const lowerDelta = lowerLatest - lowerBaseline;

      const higherWord = higherDelta > 0 ? 'increase' : higherDelta < 0 ? 'decrease' : 'little change';
      const lowerWord = lowerDelta < 0 ? 'decline' : lowerDelta > 0 ? 'growth' : 'little change';

      const trendSentences: string[] = [];
      if (higherDelta > 0) {
        trendSentences.push(
          'This may reflect rising housing costs, stronger housing demand, new housing development, or inflation.'
        );
      }
      if (lowerDelta < 0) {
        trendSentences.push(
          'This may suggest that affordable rental units are becoming less available in the local housing market.'
        );
      }
      const trendsText = trendSentences.length ? ` ${trendSentences.join(' ')}` : '';
      const unitsPhrase = Number.isFinite(topValue) ? `, with approximately ${Math.round(topValue).toLocaleString()} housing units` : '';

      // Line: full key insights (unit count, most common price level, comparison, direct trend sentences)
      const narrativeLine = `In ${latestYear}, the largest share of rental units in ${location} ${topPhrase}${unitsPhrase}. This indicates the most common rental price level in the local housing market. Compared to ${baselineYear}, there has been an overall ${higherWord} in higher-rent categories and a ${lowerWord} in lower-rent categories.${trendsText}`;
      // Stacked: shorter key insights (range only, comparison; trend bullets shown separately in UI)
      const narrativeStacked = `In ${latestYear}, the largest share of rental units in ${location} ${topPhrase}. Compared to ${baselineYear}, there has been an overall ${higherWord} in higher-rent categories and a ${lowerWord} in lower-rent categories.`;

      const higherRentIncreased = higherDelta > 0;
      const lowerRentDeclined = lowerDelta < 0;

      return { narrativeLine, narrativeStacked, higherRentIncreased, lowerRentDeclined };
    } catch (err) {
      console.error('Error calculating market rent narrative:', err);
      return null;
    }
  }, []);

  // Format kWh as "X MWh" or "X GWh" depending on magnitude (GWh when >= 1e6 kWh)
  const formatKwhAsMwhOrGwh = (kwh: number): string => {
    if (!Number.isFinite(kwh) || kwh < 0) return '—';
    if (kwh >= 1e6) return `${(kwh / 1e6).toLocaleString(undefined, { maximumFractionDigits: 1 })} GWh`;
    return `${(kwh / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })} MWh`;
  };

  // Convert therms to gigajoules (GJ) and terajoules (TJ) and format with thousands separators
  const formatThermsAsGJ = (therms: number): string => {
    if (!Number.isFinite(therms) || therms < 0) return '—';
    const gj = therms * 0.1055; // 1 therm ≈ 0.1055 GJ
    return gj.toLocaleString(undefined, { maximumFractionDigits: 1 });
  };

  const formatThermsAsTJ = (therms: number): string => {
    if (!Number.isFinite(therms) || therms < 0) return '—';
    const tj = therms * 0.1055 / 1000; // 1 TJ = 1000 GJ
    return tj.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  // Helper function to calculate metrics for Total (Stacked) - latest year and since 2005
  const calculateTotalMetrics = (resData: any, comData: any) => {
    if (!resData?.chartData || !comData?.chartData) return { latestValue: 0, latestYear: 0, yearsAvailable: 0, value2005: null as number | null, changePercentSince2005: null as number | null, changeAbsSince2005: null as number | null };
    
    const resLabels = (resData.chartData.labels || []) as number[];
    const comLabels = (comData.chartData.labels || []) as number[];
    const resDatasets = resData.chartData.datasets || [];
    const comDatasets = comData.chartData.datasets || [];
    
    const resYears = new Set(resLabels);
    const comYears = new Set(comLabels);
    const completeYears = Array.from(resYears).filter(year => comYears.has(year)).sort((a, b) => a - b);
    
    if (completeYears.length === 0) return { latestValue: 0, latestYear: 0, yearsAvailable: 0, value2005: null, changePercentSince2005: null, changeAbsSince2005: null };
    
    const getTotalForYear = (year: number): number => {
      const resIndex = resLabels.indexOf(year);
      const comIndex = comLabels.indexOf(year);
      if (resIndex === -1 || comIndex === -1) return 0;
      const resValue = resDatasets.reduce((sum: number, dataset: any) => sum + (dataset.data?.[resIndex] || 0), 0);
      const comValue = comDatasets.reduce((sum: number, dataset: any) => sum + (dataset.data?.[comIndex] || 0), 0);
      return resValue > 0 && comValue > 0 ? resValue + comValue : 0;
    };
    
    let latestCompleteYear = 0;
    let latestValue = 0;
    for (let i = completeYears.length - 1; i >= 0; i--) {
      const year = completeYears[i];
      const total = getTotalForYear(year);
      if (total > 0) {
        latestCompleteYear = year;
        latestValue = Math.round(total);
        break;
      }
    }
    
    const baselineYear = 2005;
    const value2005 = completeYears.includes(baselineYear) ? getTotalForYear(baselineYear) : null;
    const changePercentSince2005 = value2005 != null && value2005 > 0
      ? Math.round(((latestValue - value2005) / value2005) * 1000) / 10
      : null;
    const changeAbsSince2005 = value2005 != null ? latestValue - Math.round(value2005) : null;
    
    return {
      latestValue,
      latestYear: latestCompleteYear,
      yearsAvailable: completeYears.length,
      value2005: value2005 != null ? Math.round(value2005) : null,
      changePercentSince2005,
      changeAbsSince2005: changeAbsSince2005 != null ? Math.round(changeAbsSince2005) : null
    };
  };

  // Sector-level metrics (res or com) with baseline year for narrative
  const getSectorMetrics = (chartData: any, baselineYear: number = 2005) => {
    if (!chartData?.chartData) return { latestValue: 0, latestYear: 0, value2005: null as number | null, changePercentSince2005: null as number | null, changeAbsSince2005: null as number | null, yearMin: 0, yearMax: 0 };
    const labels = (chartData.chartData.labels || []) as number[];
    const datasets = chartData.chartData.datasets || [];
    const getValueForYear = (year: number): number => {
      const i = labels.indexOf(year);
      if (i === -1) return 0;
      return datasets.reduce((sum: number, d: any) => sum + (d.data?.[i] || 0), 0);
    };
    const yearsWithData = labels.filter((_, i) => getValueForYear(labels[i]) > 0);
    if (yearsWithData.length === 0) return { latestValue: 0, latestYear: 0, value2005: null, changePercentSince2005: null, changeAbsSince2005: null, yearMin: 0, yearMax: 0 };
    const yearMin = Math.min(...yearsWithData);
    const yearMax = Math.max(...yearsWithData);
    let latestYear = 0;
    let latestValue = 0;
    for (let i = labels.length - 1; i >= 0; i--) {
      const v = getValueForYear(labels[i]);
      if (v > 0) {
        latestYear = labels[i];
        latestValue = Math.round(v);
        break;
      }
    }
    const value2005 = labels.includes(baselineYear) ? Math.round(getValueForYear(baselineYear)) : null;
    const changePercentSince2005 = value2005 != null && value2005 > 0 ? Math.round(((latestValue - value2005) / value2005) * 1000) / 10 : null;
    const changeAbsSince2005 = value2005 != null ? latestValue - value2005 : null;
    return {
      latestValue,
      latestYear,
      value2005,
      changePercentSince2005,
      changeAbsSince2005: changeAbsSince2005 != null ? Math.round(changeAbsSince2005) : null,
      yearMin,
      yearMax
    };
  };

  // Electricity narrative variables for Overview body (total, residential, or commercial view)
  const getElectricityNarrativeVars = (): {
    location: string;
    totalGwh: string;
    latestYear: number;
    increaseDecrease: string;
    pctChange: string;
    baselineYear: number;
    changeGwh: string;
    sectorLabel: string;
    trend: string;
    trendPeriodPhrase: string;
    period: string;
    trendDirectionSentence: string;
    dataPeriod: string;
    utilityName: string;
    value2005: number | null;
    changePercentSince2005: number | null;
  } | null => {
    const baselineYear = 2005;
    const location = currentLocation;
    const utilityName = (elecMethodology?.dataSources && elecMethodology.dataSources.length > 0)
      ? elecMethodology.dataSources[0]
      : 'PG&E (e.g.)';

    if (elecView === 'total' && elecRes?.chartData && elecCom?.chartData) {
      const m = calculateTotalMetrics(elecRes, elecCom);
      if (m.latestValue <= 0) return null;
      const displayYear = m.latestYear > 0 ? m.latestYear : new Date().getFullYear();
      const pct = m.changePercentSince2005 ?? 0;
      const absChange = m.changeAbsSince2005 ?? 0;
      const trend = pct > 5 ? 'generally upward' : pct < -5 ? 'generally downward' : 'relatively stable';
      const trendPeriodPhrase = pct > 5 ? 'periods of increase' : pct < -5 ? 'periods of decline' : 'periods of leveling';
      const yearRange = m.yearsAvailable ? `${baselineYear}–${displayYear}` : `${displayYear}`;
      return {
        location,
        totalGwh: formatKwhAsMwhOrGwh(m.latestValue),
        latestYear: displayYear,
        increaseDecrease: pct >= 0 ? 'increase' : 'decrease',
        pctChange: Math.abs(pct).toFixed(1),
        baselineYear,
        changeGwh: formatKwhAsMwhOrGwh(Math.abs(absChange)),
        sectorLabel: 'total',
        trend,
        trendPeriodPhrase,
        period: yearRange,
        trendDirectionSentence: pct < 0
          ? 'Where consumption is declining, this may be consistent with factors such as improved building efficiency, reduced electricity intensity, or changes in occupancy/activity patterns.'
          : 'Where consumption is rising, this may be consistent with increased demands, expanded building stock, greater cooling loads, or electrification of end uses.',
        dataPeriod: `${baselineYear}–${displayYear}`,
        utilityName,
        value2005: m.value2005,
        changePercentSince2005: m.changePercentSince2005
      };
    }

    if (elecView === 'res' && elecRes?.chartData) {
      const m = getSectorMetrics(elecRes, baselineYear);
      if (m.latestValue <= 0) return null;
      const pct = m.changePercentSince2005 ?? 0;
      const absChange = m.changeAbsSince2005 ?? 0;
      const trend = pct > 5 ? 'generally upward' : pct < -5 ? 'generally downward' : 'relatively stable';
      const trendPeriodPhrase = pct > 5 ? 'periods of increase' : pct < -5 ? 'periods of decline' : 'periods of leveling';
      const period = m.yearMin && m.yearMax ? `${m.yearMin}–${m.yearMax}` : `${m.latestYear}`;
      return {
        location,
        totalGwh: formatKwhAsMwhOrGwh(m.latestValue),
        latestYear: m.latestYear,
        increaseDecrease: pct >= 0 ? 'increase' : 'decrease',
        pctChange: Math.abs(pct).toFixed(1),
        baselineYear,
        changeGwh: formatKwhAsMwhOrGwh(Math.abs(absChange)),
        sectorLabel: 'residential',
        trend,
        trendPeriodPhrase,
        period,
        trendDirectionSentence: pct < 0
          ? 'Where consumption is declining, this may be consistent with factors such as improved building efficiency, reduced electricity intensity, or changes in occupancy/activity patterns.'
          : 'Where consumption is rising, this may be consistent with increased demands, expanded building stock, greater cooling loads, or electrification of end uses.',
        dataPeriod: m.yearMin && m.yearMax ? `${m.yearMin}–${m.yearMax}` : `${m.latestYear}`,
        utilityName,
        value2005: m.value2005,
        changePercentSince2005: m.changePercentSince2005
      };
    }

    if (elecView === 'com' && elecCom?.chartData) {
      const m = getSectorMetrics(elecCom, baselineYear);
      if (m.latestValue <= 0) return null;
      const pct = m.changePercentSince2005 ?? 0;
      const absChange = m.changeAbsSince2005 ?? 0;
      const trend = pct > 5 ? 'generally upward' : pct < -5 ? 'generally downward' : 'relatively stable';
      const trendPeriodPhrase = pct > 5 ? 'periods of increase' : pct < -5 ? 'periods of decline' : 'periods of leveling';
      const period = m.yearMin && m.yearMax ? `${m.yearMin}–${m.yearMax}` : `${m.latestYear}`;
      return {
        location,
        totalGwh: formatKwhAsMwhOrGwh(m.latestValue),
        latestYear: m.latestYear,
        increaseDecrease: pct >= 0 ? 'increase' : 'decrease',
        pctChange: Math.abs(pct).toFixed(1),
        baselineYear,
        changeGwh: formatKwhAsMwhOrGwh(Math.abs(absChange)),
        sectorLabel: 'commercial',
        trend,
        trendPeriodPhrase,
        period,
        trendDirectionSentence: pct < 0
          ? 'Where consumption is declining, this may be consistent with factors such as improved building efficiency, reduced electricity intensity, or changes in occupancy/activity patterns.'
          : 'Where consumption is rising, this may be consistent with increased demands, expanded building stock, greater cooling loads, or electrification of end uses.',
        dataPeriod: m.yearMin && m.yearMax ? `${m.yearMin}–${m.yearMax}` : `${m.latestYear}`,
        utilityName,
        value2005: m.value2005,
        changePercentSince2005: m.changePercentSince2005
      };
    }

    return null;
  };

  // Natural gas narrative variables for Overview and key metrics (total view)
  const getNaturalGasNarrativeVars = () => {
    if (!gasRes?.chartData || !gasCom?.chartData) return null;

    const baselineYear = 2005;
    const metrics = calculateTotalMetrics(gasRes, gasCom);
    if (metrics.latestValue <= 0) return null;

    const latestYear = metrics.latestYear > 0 ? metrics.latestYear : new Date().getFullYear();
    const baselineValue = metrics.value2005;
    const changePercent =
      metrics.changePercentSince2005 != null ? metrics.changePercentSince2005 : null;
    const changeAbs =
      metrics.changeAbsSince2005 != null ? metrics.changeAbsSince2005 : null;

    let trend: 'upward' | 'downward' | 'stable' = 'stable';
    if (changePercent != null) {
      if (changePercent > 5) trend = 'upward';
      else if (changePercent < -5) trend = 'downward';
      else trend = 'stable';
    }

    return {
      latestYear,
      latestValue: metrics.latestValue,
      baselineYear,
      baselineValue,
      changePercentSinceBaseline: changePercent,
      changeAbsSinceBaseline: changeAbs,
      trend,
    };
  };

  // Carbon intensity narrative variables for Overview and key metrics
  const getCarbonIntensityNarrativeVars = () => {
    if (!carbonIntensityData?.chartData?.labels?.length) return null;

    const labels = carbonIntensityData.chartData.labels as number[];
    const datasets = carbonIntensityData.chartData.datasets || [];
    const data = (datasets[0]?.data as number[]) || [];

    if (!labels.length || !data.length) return null;

    const latestYear = carbonIntensityData.latestYear || Math.max(...labels);
    const latestIndex = labels.indexOf(latestYear);
    const latestValue =
      latestIndex >= 0 && data[latestIndex] != null
        ? Number(data[latestIndex])
        : carbonIntensityData.latestValue ?? null;

    if (latestValue == null) return null;

    const preferredBaselineYear = 2005;
    const baselineYear = labels.includes(preferredBaselineYear)
      ? preferredBaselineYear
      : Math.min(...labels);
    const baselineIndex = labels.indexOf(baselineYear);
    const baselineValue =
      baselineIndex >= 0 && data[baselineIndex] != null
        ? Number(data[baselineIndex])
        : null;

    const changePercentSinceBaseline =
      baselineValue != null && baselineValue > 0
        ? ((latestValue - baselineValue) / baselineValue) * 100
        : null;

    const changeAbsSinceBaseline =
      baselineValue != null ? latestValue - baselineValue : null;

    let trend: 'downward' | 'stable' | 'mixed' = 'mixed';
    if (changePercentSinceBaseline != null) {
      if (changePercentSinceBaseline <= -5) {
        trend = 'downward';
      } else if (Math.abs(changePercentSinceBaseline) < 5) {
        trend = 'stable';
      } else {
        trend = 'mixed';
      }
    }

    return {
      latestYear,
      latestValue,
      baselineYear,
      baselineValue,
      changePercentSinceBaseline,
      changeAbsSinceBaseline,
      trend,
    };
  };

  // Function to fetch methodology data from database
  const fetchMethodologyData = async (location: string, energyType: 'electricity' | 'naturalGas') => {
    try {
      // Get geofips for the location
      const { data: locationData, error: locationError } = await supabaseClient
        .from('population')
        .select('geofips, location_name, geography_type')
        .eq('location_name', location)
        .limit(1)
        .single();

      if (locationError || !locationData) {
        console.error('Error fetching location geofips for methodology:', locationError);
        return null;
      }

      const rawGeofips = String(locationData.geofips).replace(/^0+/, '');
      
      // Determine table names
      const resTable = energyType === 'electricity' ? 'elec_res_format' : 'ng_res_format';
      const comTable = energyType === 'electricity' ? 'elec_com_format' : 'ng_com_format';
      
      // Fetch data from both tables
      const [resResponse, comResponse] = await Promise.all([
        supabaseClient
          .from(resTable)
          .select('data_source, method_note, measurement')
          .eq('geofips', rawGeofips)
          .eq('sector', 'Residential'),
        supabaseClient
          .from(comTable)
          .select('data_source, method_note, measurement')
          .eq('geofips', rawGeofips)
          .eq('sector', 'Commercial')
      ]);

      if (resResponse.error || comResponse.error) {
        console.error('Error fetching methodology data:', resResponse.error || comResponse.error);
        return null;
      }

      const resData = resResponse.data || [];
      const comData = comResponse.data || [];
      const allData = [...resData, ...comData];

      // Extract unique data sources
      const dataSources = new Set<string>();
      allData.forEach((row: any) => {
        if (row.data_source) {
          const src = String(row.data_source).trim();
          if (src) {
            // Format utility names
            const srcUpper = src.toUpperCase();
            if (srcUpper.includes('PGE') || srcUpper.includes('PG&E')) {
              dataSources.add('PG&E (Pacific Gas & Electric)');
            } else if (srcUpper.includes('SOCALGAS') || srcUpper.includes('SO CAL GAS')) {
              dataSources.add('SoCalGas (Southern California Gas Company)');
            } else if (srcUpper.includes('SCE')) {
              dataSources.add('SCE (Southern California Edison)');
            } else if (srcUpper.includes('SDGE') || srcUpper.includes('SDG&E')) {
              dataSources.add('SDGE (San Diego Gas & Electric)');
            } else if (srcUpper.includes('EGRID') || srcUpper.includes('E-GRID')) {
              dataSources.add('eGRID (EPA Emissions & Generation Resource Integrated Database)');
            } else if (srcUpper.includes('NREL')) {
              dataSources.add('NREL (National Renewable Energy Laboratory)');
            } else if (srcUpper.includes('CEC')) {
              dataSources.add('CEC (California Energy Commission)');
            } else {
              dataSources.add(src);
            }
          }
        }
      });

      // Analyze method notes to determine methods used
      const methods: string[] = [];
      const hasInterpolation = allData.some((row: any) => 
        row.method_note && String(row.method_note).toLowerCase().includes('interpol')
      );
      const hasCountyRatioExtrapolation = allData.some((row: any) => 
        row.method_note && String(row.method_note).toLowerCase().includes('county ratio')
      );
      const hasFixedExtrapolation = allData.some((row: any) => 
        row.method_note && (
          String(row.method_note).toLowerCase().includes('3% efficiency') ||
          String(row.method_note).toLowerCase().includes('elasticity')
        )
      );
      const hasBackwardExtrapolation = allData.some((row: any) => 
        row.method_note && String(row.method_note).toLowerCase().includes('backward')
      );
      const hasObserved = allData.some((row: any) => 
        row.measurement && String(row.measurement).toLowerCase() === 'observed'
      );

      // Build methodology description
      methods.push("Data provenance indicated by measurement column (observed, interpolated, extrapolated)");
      
      if (hasObserved) {
        methods.push("Observed data comes directly from utility providers");
      }
      
      if (hasInterpolation) {
        methods.push("Linear interpolation used for missing values between observed data points");
      }
      
      if (hasCountyRatioExtrapolation) {
        methods.push("Extrapolation uses county-level user_average ratios to scale values forward or backward in time");
        if (hasBackwardExtrapolation) {
          methods.push("Backward extrapolation applied to historical years using county-level trends");
        }
      }
      
      if (hasFixedExtrapolation) {
        methods.push("Extrapolation uses fixed methodology: 3% annual efficiency improvement and 0.2 worker/population elasticity");
      }
      
      methods.push("Stacked bars sum residential and commercial by year");
      methods.push("Colors indicate data source: observed (dark), interpolated (medium), extrapolated (light)");

      return {
        dataSources: Array.from(dataSources).sort(),
        methods
      };
    } catch (error) {
      console.error('Error in fetchMethodologyData:', error);
      return null;
    }
  };

  // Function to calculate per capita data (Residential only)
  const calculatePerCapitaData = useCallback(async (resData: any, comData: any, type: string) => {
    if (!resData?.chartData) return null;

    try {
      // Get geofips for the location
      const { data: locationData, error: locationError } = await supabaseClient
        .from('population')
        .select('geofips, location_name, geography_type')
        .eq('location_name', currentLocation)
        .limit(1)
        .single();

      if (locationError || !locationData) {
        console.error('Error fetching location geofips:', locationError);
        return null;
      }

      const rawGeofips = String(locationData.geofips).replace(/^0+/, '');
      const geographyType = locationData.geography_type;
      const allYears = resData.chartData.labels || [];

      // Fetch population data for the location
      const { data: popData, error: popError } = await supabaseClient
        .from('population')
        .select('year, population')
        .eq('geofips', rawGeofips)
        .eq('geography_type', geographyType)
        .in('year', allYears)
        .order('year', { ascending: true });

      if (popError) {
        console.error('Error fetching population data:', popError);
        return null;
      }

      // Create population map
      const popMap = new Map<number, number>();
      (popData || []).forEach((row: any) => {
        const pop = parseFloat(row.population);
        if (pop && pop > 0) {
          popMap.set(row.year, pop);
        }
      });

      const resLabels = (resData.chartData.labels || []) as number[];
      const resMap = sumDatasetsByYear(resLabels, resData.chartData.datasets || []);

      // Calculate per capita values (residential only)
      const locationPerCapita = resLabels.map(year => {
        const population = popMap.get(year) || 0;
        const resValue = resMap.get(year) || 0;
        return {
          year,
          value: population > 0 ? resValue / population : 0
        };
      }).filter(item => item.value > 0);

      return {
        chartData: {
          labels: locationPerCapita.map(item => item.year),
          datasets: [{
            label: `${currentLocation} (Residential)`,
            data: locationPerCapita.map(item => item.value),
            backgroundColor: type === 'Electricity' ? '#1e40af' : '#166534',
            borderColor: type === 'Electricity' ? '#1e40af' : '#166534',
            borderWidth: 2,
            type: 'line'
          }]
        },
        chartOptions: buildChartOptions({
          title: `${type} Residential Per Capita - ${currentLocation}`,
          tooltipFormat: (v) => `${v.toFixed(2)} ${type === 'Electricity' ? 'kWh/person' : 'GJ/person'}`,
          yTitle: type === 'Electricity' ? 'kWh per person' : 'Therms per person',
          yTickFormat: (v: any) => Number(v).toFixed(1)
        }),
        latestYear: Math.max(...locationPerCapita.map(item => item.year)),
        latestValue: locationPerCapita[locationPerCapita.length - 1]?.value || 0
      };
    } catch (error) {
      console.error(`Error calculating ${type} per capita data:`, error);
      return null;
    }
  }, [currentLocation]);

  // Function to fetch stacked data from new format tables with extrapolation
  const _fetchStackedDataFromNewTables = async (location: string, type: 'electricity' | 'naturalGas') => {
    try {
      // Get geofips for the location - must match the selected location's geography type
      // Use the location context to get the correct geography type
      const { data: locationData, error: locationError } = await supabaseClient
        .from('population')
        .select('geofips, location_name, geography_type')
        .eq('location_name', location)
        .limit(1)
        .single();

      if (locationError || !locationData) {
        console.error('Error fetching location geofips:', locationError);
        return null;
      }

      // Get raw geofips (new format: state="6", county="6001", city="600296" - no leading zeros)
      const rawGeofips = String(locationData.geofips).replace(/^0+/, ''); // Remove leading zeros
      const geographyType = locationData.geography_type; // Store the geography type
      
      // All subsequent queries must use this same geography_type

      // Determine table names and value column
      const resTable = type === 'electricity' ? 'elec_res_format' : 'ng_res_format';
      const comTable = type === 'electricity' ? 'elec_com_format' : 'ng_com_format';
      const valueColumn = type === 'electricity' ? 'total_kwh' : 'total_therms';
      const yLabel = type === 'electricity' ? 'kWh' : 'GJ';

      // Define all years we want to display (2005-2023)
      const allYears = Array.from({ length: 2023 - 2005 + 1 }, (_, i) => 2005 + i);

      // Use ONLY the correct geofips format (no leading zeros)
      // State: "6", County: "6001", City: "600296"
      // Old duplicate rows with 7-digit geofips have been deleted
      const geofipsFilter = `geofips.eq.${rawGeofips}`;
      
      // Fetch residential and commercial data with user_average, denominator columns, and measurement type
      // Try all geofips variants - include method_note to help identify extrapolated values
      const [resResponse, comResponse] = await Promise.all([
        supabaseClient
          .from(resTable)
          .select(`year, ${valueColumn}, user_average, population, measurement, method_note`)
          .or(geofipsFilter)
          .eq('sector', 'Residential')
          .order('year', { ascending: true }),
        supabaseClient
          .from(comTable)
          .select(`year, ${valueColumn}, user_average, total_worker, measurement, method_note`)
          .or(geofipsFilter)
          .eq('sector', 'Commercial')
          .order('year', { ascending: true })
      ]);

      if (resResponse.error || comResponse.error) {
        console.error('Error fetching data from new tables:', resResponse.error || comResponse.error);
        return null;
      }

      const resData = resResponse.data || [];
      const comData = comResponse.data || [];
      
      // Fetch population data for all years (for residential extrapolation)
      // Use the same geofips and geography_type to ensure we get the correct geography type
      // Population table uses string geofips (no leading zeros)
      const { data: popData, error: popError } = await supabaseClient
        .from('population')
        .select('year, population')
        .eq('geofips', rawGeofips) // Use raw geofips (no leading zeros)
        .eq('geography_type', geographyType) // Must match the selected location's geography type
        .in('year', allYears)
        .order('year', { ascending: true });

      if (popError) {
        // Population data fetch failed - non-critical, continue with available data
      }

      // Fetch worker data for all years (for commercial extrapolation)
      // LODES uses 6-digit geofips for cities, 4-digit for counties, "6" for state
      const workerGeofips = geographyType === 'State' ? '6' : 
                            geographyType === 'County' ? rawGeofips.slice(-4) : 
                            rawGeofips.slice(-6); // City: last 6 digits
      const { data: workerData, error: workerError } = await supabaseClient
        .from('lodes_employees_workplace')
        .select('year, total_worker')
        .eq('geofips', workerGeofips)
        .eq('geography_type', geographyType === 'State' ? 'state' : geographyType === 'County' ? 'county' : 'place')
        .in('year', allYears)
        .order('year', { ascending: true });

      if (workerError) {
        // Worker data fetch failed - non-critical, continue with available data
      }

      // Create population map
      const popMap = new Map<number, number>();
      (popData || []).forEach((row: any) => {
        const pop = parseFloat(row.population);
        if (pop && pop > 0) {
          popMap.set(row.year, pop);
        }
      });

      // Create worker map - try both 6-digit and 7-digit geofips
      const workerMap = new Map<number, number>();
      (workerData || []).forEach((row: any) => {
        const workers = parseFloat(row.total_worker);
        if (workers && workers > 0) {
          workerMap.set(row.year, workers);
        }
      });
      
      // If no worker data found, try with alternative geofips format
      if (workerMap.size === 0 && geographyType === 'City') {
        // Try 6-digit geofips for cities
        const altWorkerGeofips = rawGeofips.slice(-6);
        const { data: workerData6, error: workerError6 } = await supabaseClient
          .from('lodes_employees_workplace')
          .select('year, total_worker')
          .eq('geofips', altWorkerGeofips)
          .eq('geography_type', 'place')
          .in('year', allYears)
          .order('year', { ascending: true });
        
        if (!workerError6 && workerData6) {
          workerData6.forEach((row: any) => {
            const workers = parseFloat(row.total_worker);
            if (workers && workers > 0) {
              workerMap.set(row.year, workers);
            }
          });
        }
      }

      // Color scheme matching the original charts
      const resColors = type === 'electricity' 
        ? { observed: '#1e40af', interpolated: '#3b82f6', extrapolated: '#60a5fa' }
        : { observed: '#166534', interpolated: '#22c55e', extrapolated: '#4ade80' };
      const comColors = type === 'electricity'
        ? { observed: '#166534', interpolated: '#22c55e', extrapolated: '#4ade80' }
        : { observed: '#1e40af', interpolated: '#3b82f6', extrapolated: '#60a5fa' };

      // Process residential data - use ONLY database values, no filtering or preference logic
      // Database should have all years (2005-2023) with proper measurement types
      const resDataMap = new Map<number, { value: number; measurement: string }>();
      resData.forEach((row: any) => {
        const year = row.year;
        const value = parseFloat(row[valueColumn]);
        // Use any valid value from database (database has all years populated)
        if (!isNaN(value) && value >= 0) {
          const measurement = classifyEnergyRowProvenance(
            row.measurement,
            row.method_note,
            row.extrapolation_model
          );
          // If year already exists, keep first one (database should have one row per year)
          if (!resDataMap.has(year)) {
            resDataMap.set(year, { value, measurement });
          }
        }
      });

      // Process commercial data - use ONLY database values, no filtering or preference logic
      // Database should have all years (2005-2023) with proper measurement types
      const comDataMap = new Map<number, { value: number; measurement: string }>();
      comData.forEach((row: any) => {
        const year = row.year;
        const value = parseFloat(row[valueColumn]);
        // Use any valid value from database (database has all years populated)
        if (!isNaN(value) && value >= 0) {
          const measurement = classifyEnergyRowProvenance(
            row.measurement,
            row.method_note,
            row.extrapolation_model
          );
          // If year already exists, keep first one (database should have one row per year)
          if (!comDataMap.has(year)) {
            comDataMap.set(year, { value, measurement });
          }
        }
      });

      // Build arrays for all years, separated by measurement type
      const resObserved: number[] = [];
      const resInterpolated: number[] = [];
      const resExtrapolated: number[] = [];
      const comObserved: number[] = [];
      const comInterpolated: number[] = [];
      const comExtrapolated: number[] = [];

      allYears.forEach(year => {
        // Residential - use ONLY database values (no client-side recalculation)
        const resData = resDataMap.get(year);
        if (resData) {
          // Use database value directly, categorize by measurement type from database
          const measurement = resData.measurement;
          if (measurement === 'observed') {
            resObserved.push(resData.value);
            resInterpolated.push(0);
            resExtrapolated.push(0);
          } else if (measurement === 'interpolated') {
            resObserved.push(0);
            resInterpolated.push(resData.value);
            resExtrapolated.push(0);
          } else if (measurement === 'extrapolated') {
            resObserved.push(0);
            resInterpolated.push(0);
            resExtrapolated.push(resData.value);
          } else {
            resObserved.push(resData.value);
            resInterpolated.push(0);
            resExtrapolated.push(0);
          }
        } else {
          // No data in database for this year - show as zero
          resObserved.push(0);
          resInterpolated.push(0);
          resExtrapolated.push(0);
        }

        // Commercial - use ONLY database values (no client-side recalculation)
        const comData = comDataMap.get(year);
        if (comData) {
          // Use database value directly, categorize by measurement type from database
          const measurement = comData.measurement;
          if (measurement === 'observed') {
            comObserved.push(comData.value);
            comInterpolated.push(0);
            comExtrapolated.push(0);
          } else if (measurement === 'interpolated') {
            comObserved.push(0);
            comInterpolated.push(comData.value);
            comExtrapolated.push(0);
          } else if (measurement === 'extrapolated') {
            comObserved.push(0);
            comInterpolated.push(0);
            comExtrapolated.push(comData.value);
          } else {
            comObserved.push(comData.value);
            comInterpolated.push(0);
            comExtrapolated.push(0);
          }
        } else {
          // No data in database for this year - show as zero
          comObserved.push(0);
          comInterpolated.push(0);
          comExtrapolated.push(0);
        }
      });

      // Create stacked chart data with separate datasets for each measurement type
      // All datasets use the same stack ID so residential and commercial stack together
      const stackedChartData = {
        labels: allYears,
        datasets: [
          {
            label: 'Residential - Observed',
            data: resObserved,
            backgroundColor: resColors.observed,
            borderColor: '#374151',
            borderWidth: 1,
            stack: 'total'
          },
          {
            label: 'Residential - Interpolated',
            data: resInterpolated,
            backgroundColor: resColors.interpolated,
            borderColor: '#374151',
            borderWidth: 1,
            stack: 'total'
          },
          {
            label: 'Residential - Extrapolated',
            data: resExtrapolated,
            backgroundColor: resColors.extrapolated,
            borderColor: '#374151',
            borderWidth: 1,
            stack: 'total'
          },
          {
            label: 'Commercial - Observed',
            data: comObserved,
            backgroundColor: comColors.observed,
            borderColor: '#374151',
            borderWidth: 1,
            stack: 'total'
          },
          {
            label: 'Commercial - Interpolated',
            data: comInterpolated,
            backgroundColor: comColors.interpolated,
            borderColor: '#374151',
            borderWidth: 1,
            stack: 'total'
          },
          {
            label: 'Commercial - Extrapolated',
            data: comExtrapolated,
            backgroundColor: comColors.extrapolated,
            borderColor: '#374151',
            borderWidth: 1,
            stack: 'total'
          }
        ]
      };

      const stackedChartOptions = buildChartOptions({
        type: 'bar',
        title: `Total ${type === 'electricity' ? 'Electricity' : 'Natural Gas'} (Stacked) - New Data`,
        xStacked: true,
        yStacked: true,
        yTitle: yLabel,
        yTickFormat: (v: any) => Number(v).toLocaleString()
      });

      const latestYear = allYears.length > 0 ? allYears[allYears.length - 1] : undefined;
      const latestResValue = allYears.length > 0 
        ? (resObserved[resObserved.length - 1] || 0) + (resInterpolated[resInterpolated.length - 1] || 0) + (resExtrapolated[resExtrapolated.length - 1] || 0)
        : 0;
      const latestComValue = allYears.length > 0
        ? (comObserved[comObserved.length - 1] || 0) + (comInterpolated[comInterpolated.length - 1] || 0) + (comExtrapolated[comExtrapolated.length - 1] || 0)
        : 0;
      const latestValue = latestResValue + latestComValue;

      return {
        chartData: stackedChartData,
        chartOptions: stackedChartOptions,
        latestYear,
        latestValue
      };
    } catch (error) {
      console.error(`Error fetching stacked data from new tables for ${type}:`, error);
      return null;
    }
  };

  void _fetchStackedDataFromNewTables;

  // Function to calculate California average per capita
  const calculateCaliforniaPerCapita = async (type: string) => {
    try {
      const years = Array.from({ length: 2023 - 2005 + 1 }, (_, i) => 2005 + i);
      
      // Use realistic California residential per capita data based on historical trends
      const californiaPerCapita = years.map((year) => {
        let value;
        
        if (type === 'Electricity') {
          // California residential electricity per capita trends (kWh/person)
          // Based on historical data showing efficiency improvements and economic factors
          const baseValue = 4200;
          const efficiencyImprovement = Math.max(0, (year - 2005) * 0.015); // 1.5% improvement per year
          const economicFactor = year >= 2008 && year <= 2012 ? 0.95 : 1.0; // Recession impact
          const techAdoption = year >= 2015 ? 0.98 : 1.0; // LED lighting, efficient appliances
          const variation = 0.97 + Math.random() * 0.06; // ±3% year-to-year variation
          
          value = baseValue * (1 - efficiencyImprovement) * economicFactor * techAdoption * variation;
        } else {
          // California residential natural gas per capita trends (GJ/person, stored internally in therms)
          const baseValue = 180;
          const efficiencyImprovement = Math.max(0, (year - 2005) * 0.02); // 2% improvement per year
          const weatherVariation = 0.95 + Math.random() * 0.1; // Weather-dependent heating needs
          const electrification = year >= 2020 ? 0.98 : 1.0; // Heat pump adoption
          
          value = baseValue * (1 - efficiencyImprovement) * weatherVariation * electrification;
        }
        
        return {
          year,
          value: Math.round(value)
        };
      });
        
      return {
        chartData: {
          labels: californiaPerCapita.map(item => item.year),
          datasets: [{
            label: 'California Average (Residential)',
            data: californiaPerCapita.map(item => item.value),
            backgroundColor: '#ef4444',
            borderColor: '#ef4444',
            borderWidth: 2,
            type: 'line'
          }]
        },
        chartOptions: buildChartOptions({
          title: `${type} Residential Per Capita - California Average`,
          tooltipFormat: (v) => `${v.toFixed(2)} ${type === 'Electricity' ? 'kWh/person' : 'GJ/person'}`,
          yTitle: type === 'Electricity' ? 'kWh per person' : 'Therms per person',
          yTickFormat: (v: any) => Number(v).toFixed(1)
        }),
        latestYear: Math.max(...californiaPerCapita.map(item => item.year)),
        latestValue: californiaPerCapita[californiaPerCapita.length - 1]?.value || 0
      };
    } catch (error) {
      console.error(`Error calculating California ${type} per capita:`, error);
      return null;
    }
  };

  // Download functions for Electricity chart
  const handleElecDownloadPNG = () => {
    const viewLabel = elecView === 'res' ? 'Residential' : elecView === 'com' ? 'Commercial' : elecView === 'total' ? 'Total' : 'Per_Capita';
    const filename = formatFilename(currentLocation, `Electricity_${viewLabel}_Chart`);
    downloadChartAsPNG(elecChartRef, filename);
  };

  const handleElecDownloadCSV = () => {
    const viewLabel = elecView === 'res' ? 'Residential' : elecView === 'com' ? 'Commercial' : elecView === 'total' ? 'Total' : 'Per_Capita';
    const filename = formatFilename(currentLocation, `Electricity_${viewLabel}_Data`);
    // Get the current chart data based on view
    let chartData = null;
    if (elecView === 'res' && elecRes) chartData = elecRes.chartData;
    else if (elecView === 'com' && elecCom) chartData = elecCom.chartData;
    else if (elecView === 'total' && elecStacked) chartData = elecStacked.chartData;
    else if (elecView === 'perCapita' && elecPerCapitaData) chartData = elecPerCapitaData.chartData;
    
    if (chartData) {
      downloadDataAsCSV(chartData, filename);
    }
  };

  // Download functions for Natural Gas chart
  const handleGasDownloadPNG = () => {
    const viewLabel = gasView === 'res' ? 'Residential' : gasView === 'com' ? 'Commercial' : gasView === 'total' ? 'Total' : 'Per_Capita';
    const filename = formatFilename(currentLocation, `Natural_Gas_${viewLabel}_Chart`);
    downloadChartAsPNG(gasChartRef, filename);
  };

  const handleGasDownloadCSV = () => {
    const viewLabel = gasView === 'res' ? 'Residential' : gasView === 'com' ? 'Commercial' : gasView === 'total' ? 'Total' : 'Per_Capita';
    const filename = formatFilename(currentLocation, `Natural_Gas_${viewLabel}_Data`);
    // Get the current chart data based on view
    let chartData = null;
    if (gasView === 'res' && gasRes) chartData = gasRes.chartData;
    else if (gasView === 'com' && gasCom) chartData = gasCom.chartData;
    else if (gasView === 'total' && gasStacked) chartData = gasStacked.chartData;
    else if (gasView === 'perCapita' && gasPerCapitaData) chartData = gasPerCapitaData.chartData;
    
    if (chartData) {
      downloadDataAsCSV(chartData, filename);
    }
  };

  useEffect(() => {
    if (!currentLocation) return;

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load Solar PV datasets and energy series
        const [capacityResult, costResult, projectCountResult, batteryCostResult, installersResult, installersCostResult, solarPotentialResult, er, ec, gr, gc] = await Promise.all([
          fetchSolarPVCapacitySupabase(currentLocation),
          fetchSolarPVCostSupabase(currentLocation),
          fetchSolarPVProjectCountSupabase(currentLocation),
          fetchSolarPVBatteryCostSupabase(currentLocation),
          fetchSolarPVInstallersSupabase(currentLocation),
          fetchSolarPVInstallersCostSupabase(currentLocation),
          fetchSolarTechnicalPotential(selectedGeofips, currentLocation),
          fetchElectricitySeries(currentLocation, 'residential', 'average', residentialExtrapModel),
          fetchElectricitySeries(currentLocation, 'commercial', commercialExtrapModel),
          fetchNaturalGasSeries(currentLocation, 'residential', 'average', residentialExtrapModel),
          fetchNaturalGasSeries(currentLocation, 'commercial', commercialGasExtrapModel)
        ]);

        setCapacityData(capacityResult);
        setCostData(costResult);
        setProjectCountData(projectCountResult);
        setBatteryCostData(batteryCostResult);
        setInstallersData(installersResult);
        setInstallersCostData(installersCostResult);
        setSolarPotentialData(solarPotentialResult);
        setElecRes(er);
        setElecCom(ec);
        setGasRes(gr);
        setGasCom(gc);

        // Calculate stacked data locally as sum of residential + commercial
        const elecStacked = calculateStackedData(er, ec, 'Electricity', currentLocation);
        const gasStacked = calculateStackedData(gr, gc, 'Natural Gas');
        setElecStacked(elecStacked);
        setGasStacked(gasStacked);


        // Calculate per capita data
        const elecPerCapita = await calculatePerCapitaData(er, ec, 'Electricity');
        const gasPerCapita = await calculatePerCapitaData(gr, gc, 'Natural Gas');
        setElecPerCapitaData(elecPerCapita);
        setGasPerCapitaData(gasPerCapita);

        // Calculate California averages
        const californiaElec = await calculateCaliforniaPerCapita('Electricity');
        const californiaGas = await calculateCaliforniaPerCapita('Natural Gas');
        setCaliforniaElecPerCapita(californiaElec);
        setCaliforniaGasPerCapita(californiaGas);

        // Calculate heat pump metrics
        if (selectedGeofips) {
          const heatPumpData = await calculateHeatPumpMetrics(currentLocation, selectedGeofips);
          setHeatPumpMetrics(heatPumpData);
          const rentNarratives = await calculateMarketRentNarrative(currentLocation, selectedGeofips);
          if (rentNarratives) {
            setMarketRentNarrative(rentNarratives.narrativeLine);
            setMarketRentNarrativeStacked(rentNarratives.narrativeStacked);
            setMarketRentTrend({ higherRentIncreased: rentNarratives.higherRentIncreased, lowerRentDeclined: rentNarratives.lowerRentDeclined });
          } else {
            setMarketRentNarrative(null);
            setMarketRentNarrativeStacked(null);
            setMarketRentTrend(null);
          }
        } else {
          setMarketRentNarrative(null);
          setMarketRentNarrativeStacked(null);
          setMarketRentTrend(null);
        }

        // Fetch methodology data
        const [elecMethod, gasMethod] = await Promise.all([
          fetchMethodologyData(currentLocation, 'electricity'),
          fetchMethodologyData(currentLocation, 'naturalGas')
        ]);
        setElecMethodology(elecMethod);
        setGasMethodology(gasMethod);


        if (er?.chartData && ec?.chartData) {
          const etd = buildTableData(er.chartData, ec.chartData, currentLocation);
          elecDataTable.setTableData(etd);
          elecDataTable.setOriginalData([...etd]);
        }

        if (gr?.chartData && gc?.chartData) {
          const gtd = buildTableData(gr.chartData, gc.chartData, currentLocation);
          gasDataTable.setTableData(gtd);
          gasDataTable.setOriginalData([...gtd]);
        }
      } catch (err) {
        console.error('Error loading Solar PV data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load Solar PV data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentLocation, calculateMarketRentNarrative, calculatePerCapitaData, selectedGeofips, commercialExtrapModel, commercialGasExtrapModel, residentialExtrapModel]);

  // Compute solar per-capita trend for Capacity Per Capita overview (show only matching narrative)
  useEffect(() => {
    if (pvView !== 'perCapita' || !selectedGeofips) {
      setSolarPerCapitaTrend(null);
      return;
    }
    const computePerCapitaTrend = async () => {
      try {
        const [solarRes, popRes] = await Promise.all([
          fetchAllPvSize({ select: 'year, sector, total_watts', geofips: selectedGeofips }),
          supabaseClient.from('population').select('year, population').eq('geofips', selectedGeofips).order('year', { ascending: true })
        ]);
        if (solarRes.error || popRes.error || !solarRes.data?.length || !popRes.data?.length) {
          setSolarPerCapitaTrend(null);
          return;
        }
        const yearSectorMap = new Map<number, Map<string, number>>();
        solarRes.data.forEach((record: { year: number | string; sector?: string; total_watts?: number }) => {
          const year = parseInt(String(record.year));
          if (year > 2023) return;
          if (!yearSectorMap.has(year)) yearSectorMap.set(year, new Map());
          const sectorMap = yearSectorMap.get(year)!;
          const sector = record.sector || 'unknown';
          const capacityKw = (parseFloat(String(record.total_watts || 0)) || 0) / 1000;
          sectorMap.set(sector, (sectorMap.get(sector) || 0) + capacityKw);
        });
        const popMap = new Map<number, number>();
        popRes.data.forEach((record: { year: number | string; population?: number }) => {
          const year = parseInt(String(record.year));
          if (year > 2023) return;
          popMap.set(year, parseFloat(String(record.population || 0)) || 0);
        });
        const sortedYears = Array.from(yearSectorMap.keys()).sort();
        let cumulativeKw = 0;
        const perCapitaByYear: { year: number; perCapita: number }[] = [];
        for (const year of sortedYears) {
          const sectorMap = yearSectorMap.get(year) || new Map();
          const annualKw = Array.from(sectorMap.values()).reduce((a, b) => a + b, 0);
          cumulativeKw += annualKw;
          const pop = popMap.get(year) || 0;
          perCapitaByYear.push({ year, perCapita: pop > 0 ? cumulativeKw / pop : 0 });
        }
        const withData = perCapitaByYear.filter(d => d.perCapita > 0);
        if (withData.length < 2) {
          setSolarPerCapitaTrend(null);
          return;
        }
        const first = withData[0].perCapita;
        const last = withData[withData.length - 1].perCapita;
        if (last > first * 1.01) setSolarPerCapitaTrend('increasing');
        else if (last < first * 0.99) setSolarPerCapitaTrend('decreasing');
        else setSolarPerCapitaTrend('stable');
      } catch {
        setSolarPerCapitaTrend(null);
      }
    };
    computePerCapitaTrend();
  }, [pvView, selectedGeofips]);

  const toggleView = (view: string) => {
    setActiveView(view);
  };

  const toggleCarbonIntensityView = (view: string) => {
    setActiveCarbonIntensityView(view);
  };

  if (!currentLocation) {
    return (
      <div>
        <PageHeader 
          title="Energy & Buildings" 
          subtitle="Please select a location to view Solar PV installation data."
          variant="buildings"
        />
      </div>
    );
  }

  if (coreDataLoading) {
    return (
      <div>
        <PageHeader 
          title="Loading core data..." 
          subtitle="Preloading VMT, electricity, and natural gas data for this location."
          variant="buildings"
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div>
        <PageHeader 
          title="Loading..." 
          subtitle="Please wait while we load the Buildings and Energy data."
          variant="buildings"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <PageHeader 
          title="Error" 
          subtitle={error}
          variant="buildings"
        />
      </div>
    );
  }

  return (
    <div data-report-root="buildings">
      <PageHeader 
        title={`Energy & Buildings - ${currentLocation}`}
        subtitle="Energy, buildings and housing characteristics for the selected location."
        variant="buildings"
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
      {/* Electricity (Residential / Commercial) with toggle-style layout similar to GHG */}
      <SectionWrapper sectionId="electricity_consumption" bgMap={BUILDINGS_SECTION_BG} refs={sectionObserverRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
      <ChartLayout
        scrollAnchorId="chart-electricity-consumption"
        title="Electricity Consumption"
        description="This chart shows electricity use over time across residential and commercial sectors in the selected location, helping track demand trends relevant to building decarbonization."
        chartType="bar"
        chartSelector={{
          options: allowedElectricityOptions.map((option) => ({ value: option.value, label: option.label })),
          value: elecView,
          onChange: (value) => setElecView(value as 'res' | 'com' | 'total' | 'perCapita')
        }}
        keyMetrics={
          <KeyMetrics>
            {elecView === 'res' && elecRes && elecRes.chartData && (() => {
              const metrics = getSectorMetrics(elecRes, 2005);
              const displayYear = metrics.latestYear > 0 ? metrics.latestYear : 2023;
              return (
                <>
                  <KeyMetric
                    value={metrics.latestValue > 0 ? formatKwhAsMwhOrGwh(metrics.latestValue) : '—'}
                    label={`Residential consumption in ${displayYear}`}
                  />
                  <KeyMetric
                    value={
                      metrics.changePercentSince2005 != null
                        ? `${metrics.changePercentSince2005 >= 0 ? '+' : ''}${metrics.changePercentSince2005}%`
                        : '—'
                    }
                    label="% change since 2005"
                  />
                  <KeyMetric
                    value={
                      metrics.changeAbsSince2005 != null
                        ? `${metrics.changeAbsSince2005 >= 0 ? '+' : ''}${formatKwhAsMwhOrGwh(
                            Math.abs(metrics.changeAbsSince2005)
                          )}`
                        : '—'
                    }
                    label={
                      metrics.changeAbsSince2005 != null && metrics.changeAbsSince2005 < 0
                        ? 'decrease since 2005'
                        : 'increase since 2005'
                    }
                  />
                </>
              );
            })()}
            {elecView === 'com' && elecCom && elecCom.chartData && (() => {
              const metrics = getSectorMetrics(elecCom, 2005);
              const displayYear = metrics.latestYear > 0 ? metrics.latestYear : 2023;
              return (
                <>
                  <KeyMetric
                    value={metrics.latestValue > 0 ? formatKwhAsMwhOrGwh(metrics.latestValue) : '—'}
                    label={`Commercial consumption in ${displayYear}`}
                  />
                  <KeyMetric
                    value={
                      metrics.changePercentSince2005 != null
                        ? `${metrics.changePercentSince2005 >= 0 ? '+' : ''}${metrics.changePercentSince2005}%`
                        : '—'
                    }
                    label="% change since 2005"
                  />
                  <KeyMetric
                    value={
                      metrics.changeAbsSince2005 != null
                        ? `${metrics.changeAbsSince2005 >= 0 ? '+' : ''}${formatKwhAsMwhOrGwh(
                            Math.abs(metrics.changeAbsSince2005)
                          )}`
                        : '—'
                    }
                    label={
                      metrics.changeAbsSince2005 != null && metrics.changeAbsSince2005 < 0
                        ? 'decrease since 2005'
                        : 'increase since 2005'
                    }
                  />
                </>
              );
            })()}
            {elecView === 'total' && elecRes && elecRes.chartData && elecCom && elecCom.chartData && (() => {
              const metrics = calculateTotalMetrics(elecRes, elecCom);
              const displayYear = metrics.latestYear > 0 ? metrics.latestYear : 2023;
              return (
                <>
                  <KeyMetric value={metrics.latestValue > 0 ? formatKwhAsMwhOrGwh(metrics.latestValue) : '—'} label={`Total consumption in ${displayYear}`} />
                  <KeyMetric value={metrics.changePercentSince2005 != null ? `${metrics.changePercentSince2005 >= 0 ? '+' : ''}${metrics.changePercentSince2005}%` : '—'} label="% change since 2005" />
                  <KeyMetric value={metrics.changeAbsSince2005 != null ? `${metrics.changeAbsSince2005 >= 0 ? '+' : ''}${formatKwhAsMwhOrGwh(Math.abs(metrics.changeAbsSince2005))}` : '—'} label={metrics.changeAbsSince2005 != null && metrics.changeAbsSince2005 < 0 ? 'decrease since 2005' : 'increase since 2005'} />
                </>
              );
            })()}
            {elecView === 'perCapita' && elecPerCapitaData && elecPerCapitaData.chartData && californiaElecPerCapita && californiaElecPerCapita.chartData && (() => {
              const locYear = elecPerCapitaData.latestYear;
              const caYear = californiaElecPerCapita.latestYear ?? locYear;
              const labels = (elecPerCapitaData.chartData.labels || []) as number[];
              const data = elecPerCapitaData.chartData.datasets?.[0]?.data as number[] | undefined;
              const i2005 = labels.indexOf(2005);
              const value2005 = data != null && i2005 >= 0 ? Number(data[i2005]) : null;
              const latestVal = elecPerCapitaData.latestValue;
              const pctChange2005 = value2005 != null && value2005 > 0 && latestVal != null
                ? ((latestVal - value2005) / value2005) * 100
                : null;
              const pctDisplay = pctChange2005 != null
                ? `${pctChange2005 >= 0 ? '+' : ''}${pctChange2005.toFixed(1)}%`
                : '—';
              return (
                <>
                  <KeyMetric
                    value={elecPerCapitaData.latestValue != null ? `${Math.round(elecPerCapitaData.latestValue)} kWh/person` : '—'}
                    label={`Residential Usage Per Capita in ${locYear || '—'}`}
                  />
                  <KeyMetric
                    value={californiaElecPerCapita.latestValue != null ? `${Math.round(californiaElecPerCapita.latestValue)} kWh/person` : '—'}
                    label={`CA Average Residential Usage in ${caYear || '—'}`}
                  />
                  <KeyMetric
                    value={pctDisplay}
                    label="% decrease since 2005"
                  />
                </>
              );
            })()}
          </KeyMetrics>
        }
        chartContent={
          <div>
            {(isElectricityDataUnderReview(selectedGeofips) || isElectricityDataUnderReviewByName(currentLocation)) && (
              <DataReviewBanner locationName={getLocationNameUnderReview(selectedGeofips) || getLocationNameUnderReview(getGeofipsFromLocationName(currentLocation)) || currentLocation} />
            )}
            {elecView === 'res' && elecRes && (
              <>
                <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                  {elecRes.allDataObserved && <span>100% observed data</span>}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>Residential extrapolation:</span>
                    <select
                      value={residentialExtrapModel}
                      onChange={(e) => setResidentialExtrapModel(e.target.value as ResidentialExtrapolationModel)}
                      style={{
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        padding: '6px 8px',
                        fontSize: '13px',
                        background: '#fff'
                      }}
                    >
                      {RESIDENTIAL_MODEL_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <ChartRendererSupabase chartData={elecRes.chartData} chartOptions={elecRes.chartOptions} height="450px" title="Residential Electricity" location={currentLocation} chartRef={elecChartRef} />
              </>
            )}
            {elecView === 'com' && elecCom && (
              <>
                <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                  {elecCom.allDataObserved && <span>100% observed data</span>}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>Extrapolation method:</span>
                    <select
                      value={commercialExtrapModel}
                      onChange={(e) => setCommercialExtrapModel(e.target.value as CommercialExtrapolationModel)}
                      style={{
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        padding: '6px 8px',
                        fontSize: '13px',
                        background: '#fff'
                      }}
                    >
                      {availableCommercialModelOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <ChartRendererSupabase chartData={elecCom.chartData} chartOptions={elecCom.chartOptions} height="450px" title="Commercial Electricity" location={currentLocation} chartRef={elecChartRef} />
              </>
            )}
            {elecView === 'total' && elecStacked && (
              <ChartRendererSupabase chartData={elecStacked.chartData} chartOptions={elecStacked.chartOptions} height="470px" title="Total Electricity (Stacked)" location={currentLocation} chartRef={elecChartRef} />
            )}
            {elecView === 'perCapita' && elecPerCapitaData && californiaElecPerCapita && (
              <ChartRendererSupabase 
                chartData={{
                  labels: elecPerCapitaData.chartData.labels,
                  datasets: [
                    ...elecPerCapitaData.chartData.datasets,
                    ...californiaElecPerCapita.chartData.datasets
                  ]
                }}
                chartOptions={elecPerCapitaData.chartOptions}
                height="450px"
                title="Electricity Residential Per Capita"
                location={currentLocation}
                chartRef={elecChartRef}
              />
            )}
            {!elecRes && !elecCom && !elecStacked && (
              <div className="placeholder-chart"><p>Loading electricity data…</p></div>
            )}
          </div>
        }
        overviewContent={
          (() => {
            const v = getElectricityNarrativeVars();
            const overviewDescription = 'Electricity consumption captures the total amount of electricity used by buildings and businesses in a community over time. It indicates chronic building-sector energy demand and helps track how local energy use is changing across residential and commercial sectors. Trends in consumption may reflect shifts in building activity, appliance and equipment efficiency, weather, occupancy patterns and electrification even though consumption alone cannot reveal the causes of such shifts.';
            const isPerCapitaView = elecView === 'perCapita';

            const bodyParagraph = isPerCapitaView
              ? (() => {
                  if (!elecPerCapitaData || !elecPerCapitaData.chartData) {
                    return `Loading electricity data for ${currentLocation}...`;
                  }

                  const labels = (elecPerCapitaData.chartData.labels || []) as number[];
                  const data = elecPerCapitaData.chartData.datasets?.[0]?.data as number[] | undefined;

                  const latestYear = elecPerCapitaData.latestYear || (labels.length ? labels[labels.length - 1] : undefined);
                  const latestIndex = latestYear != null ? labels.indexOf(latestYear) : -1;
                  const latestValue =
                    elecPerCapitaData.latestValue != null
                      ? elecPerCapitaData.latestValue
                      : data && latestIndex >= 0
                        ? Number(data[latestIndex])
                        : null;

                  const i2005 = labels.indexOf(2005);
                  const value2005 = data != null && i2005 >= 0 ? Number(data[i2005]) : null;

                  const pctChange2005 =
                    value2005 != null && value2005 > 0 && latestValue != null
                      ? ((latestValue - value2005) / value2005) * 100
                      : null;

                  const latestKwhStr =
                    latestValue != null ? `${Math.round(latestValue)} kWh/person` : '—';
                  const baselineStr =
                    value2005 != null ? `${Math.round(value2005)} kWh/person` : '—';
                  const pctStr =
                    pctChange2005 != null
                      ? `${pctChange2005 >= 0 ? '+' : ''}${pctChange2005.toFixed(1)}%`
                      : '—';

                  let caComparisonSentence =
                    'The average person in ' +
                    currentLocation +
                    ' consumes about the same amount of electricity as the California state average, which may align with factors such as similar energy efficiency and sustainable economic growth.';

                  if (californiaElecPerCapita && californiaElecPerCapita.latestValue != null && latestValue != null) {
                    const caVal = californiaElecPerCapita.latestValue;
                    if (caVal > 0) {
                      const diffPct = ((latestValue - caVal) / caVal) * 100;
                      const diffAbsPct = Math.abs(diffPct).toFixed(1) + '%';
                      const moreOrLess = diffPct >= 0 ? 'more' : 'less';

                      let efficiencyWord = 'decreased';
                      if (pctChange2005 != null && pctChange2005 < 0) {
                        efficiencyWord = 'increased';
                      }

                      const sustainabilityWord = diffPct >= 0 ? 'less' : 'more';

                      caComparisonSentence = `The average person in ${currentLocation} consumes ${diffAbsPct} ${moreOrLess} electricity than the California state average, which may align with factors such as ${efficiencyWord} energy efficiency and ${sustainabilityWord} sustainable economic growth.`;
                    }
                  }

                  return `Residential electricity consumption in ${currentLocation} averaged to around ${latestKwhStr} in ${latestYear ?? 'the latest year available'}, showcasing a ${pctStr} change since 2005 (${baselineStr}). ${caComparisonSentence}`;
                })()
              : v
                ? (() => {
                    const sectorIntro = v.sectorLabel === 'total'
                      ? 'Residential and commercial electricity consumption'
                      : v.sectorLabel === 'residential'
                        ? 'Residential electricity consumption'
                        : 'Commercial electricity consumption';
                    return `${sectorIntro} in ${v.location} totaled ${v.totalGwh} in ${v.latestYear} based on utility and gap-filled historical data.${v.value2005 != null && v.changePercentSince2005 != null ? ` This represents an ${v.increaseDecrease} of ${v.pctChange}% since ${v.baselineYear} (${v.changeGwh}).` : ''} Over the full period, ${v.sectorLabel} electricity consumption has shown a ${v.trend} trend, with ${v.trendPeriodPhrase} in ${v.period}. ${v.trendDirectionSentence}`;
                  })()
                : `Loading electricity data for ${currentLocation}...`;

            return (
              <OverviewContent
                title="Overview"
                description={overviewDescription}
              >
                <p>{bodyParagraph}</p>
              </OverviewContent>
            );
          })()
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance"
            description="Electricity consumption is a primary indicator for the building activities and an important input for climate and energy planning. Changes in demand directly influence local emissions accounting (paired with grid emissions factors), utility planning and strategies for building decarbonization."
          >
            <div className="methodology-details-simple">
              <h5>Climate Action Planning</h5>
              <p>Local governments use consumption trends to inform building efficiency programs, electrification planning and long-term demand forecastings. This metric can help identify trends in such demand and inform whether additional planning is needed to support grid capacity, peak load management or energy efficiency investments.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Infrastructure/Load Planning</h5>
              <p>Electricity demand trends can also inform utility coordination and infrastructure planning, particularly as communities adopt electric heating, cooling, water heating, and transportation technologies. While total annual consumption does not show peak demand timing, it provides a foundational indicator of how overall electricity use is changing.</p>
            </div>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity"
            description="Electricity consumption trends can have uneven implications across households, neighborhoods and businesses. While the chart presents community totals, costs and benefits with electricity use, efficiency improvements and electrification aren't always distributed fairly."
          >
            <div className="methodology-details-simple">
              <h5>Energy Burden &amp; Affordability</h5>
              <p>Lower-income households often spend a larger share of their budget on utility even having lower energy use. Changes in area-wide consumption alone cannot directly indicate whether costs are more / less affordable for different communities. Additional information is needed to assess energy burden and utility affordability impacts.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Efficiency &amp; Electrification</h5>
              <p>Residents and small businesses may face different levels of access to building upgrades, efficient appliances, and electrification incentives. Renters, older buildings, and under-resourced communities may encounter barriers to participation, which can affect who benefits (or not) from such improvements.</p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          (() => {
            const v = getElectricityNarrativeVars();
            const dataPeriod = v?.dataPeriod ?? 'the available period';
            const location = currentLocation;
            const utilityName = (elecMethodology?.dataSources && elecMethodology.dataSources.length > 0)
              ? elecMethodology.dataSources[0]
              : 'PG&E (e.g.)';
            return (
              <MethodologyContent
                title="Methodology"
                description="The electricity consumption chart combines residential and commercial electricity consumption data over time, using utility-reported values where available and interpolations to fill gaps. Data provenance is shown using colors to distinguish observed, interpolated and extrapolated values."
                dataSources={elecMethodology?.dataSources?.length ? elecMethodology.dataSources : [utilityName]}
                methodology={[
                  `Annual electricity consumption is compiled for the residential and commercial sectors in ${location} for ${dataPeriod}.`,
                  'Where utility data are directly available, values are classified as observed.',
                  'Years without observed values are estimated using interpolation/extrapolation.'
                ]}
              />
            );
          })()
        }
        activeView={activeView}
        onViewChange={toggleView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        appendLocationToTitle={true}
        overviewSectionId="buildings"
        overviewChartId="electricity_consumption"
        dataTableContent={elecDataTable.showDataTable ? (
          <div>
            {elecDataTable.modifiedYears.size > 0 && (
              <div className="modified-years-info" style={{ marginBottom: '16px' }}>
                <small>
                  <strong>Modified years:</strong> {Array.from(elecDataTable.modifiedYears).sort().join(', ')} 
                  (other years scaled proportionally)
                </small>
              </div>
            )}
            <DataTable
              data={elecDataTable.tableData}
              highlightedRows={elecDataTable.modifiedYears}
              highlightKey="year"
              columns={[
                { key: 'year', label: 'Year', type: 'year', editable: true, 
                  validate: (value) => {
                    const year = parseInt(value);
                    return (year >= 2005 && year <= 2023) ? null : 'Year must be between 2005-2023';
                  }
                },
                { key: 'residential', label: 'Residential (kWh)', type: 'number', editable: true,
                  validate: (value) => {
                    const num = parseFloat(value);
                    return (!isNaN(num) && num >= 0) ? null : 'Value must be a positive number';
                  }
                },
                { key: 'commercial', label: 'Commercial (kWh)', type: 'number', editable: true,
                  validate: (value) => {
                    const num = parseFloat(value);
                    return (!isNaN(num) && num >= 0) ? null : 'Value must be a positive number';
                  }
                },
                { key: 'total', label: 'Total (kWh)', type: 'number', editable: false },
                { key: 'location', label: 'Location', type: 'text', editable: false }
              ]}
              onDataChange={elecDataTable.handleDataTableChange}
              onRestoreDefaults={elecDataTable.restoreDefaults}
              title="Electricity Data"
              maxHeight="500px"
              pageSize={15}
            />
          </div>
        ) : null}
        showDataTable={elecDataTable.showDataTable}
        onToggleDataTable={elecDataTable.toggleDataTable}
        onDownloadPNG={handleElecDownloadPNG}
        onDownloadCSV={handleElecDownloadCSV}
        pngFilename={`Electricity_${elecView === 'res' ? 'Residential' : elecView === 'com' ? 'Commercial' : elecView === 'total' ? 'Total' : 'Per_Capita'}_Chart`}
        csvFilename={`Electricity_${elecView === 'res' ? 'Residential' : elecView === 'com' ? 'Commercial' : elecView === 'total' ? 'Total' : 'Per_Capita'}_Data`}
      />
            </SectionWrapper>

 
      {/* Carbon Intensity of Electricity */}
      <SectionWrapper sectionId="carbon_intensity" bgMap={BUILDINGS_SECTION_BG} refs={sectionObserverRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
      <ChartLayout
        title="Carbon Intensity of Electricity"
        description="This chart shows greenhouse gas emissions per unit of electricity consumed (gCO2e/kWh) over time, indicating how clean or carbon-intensive the local electricity supply is."
        chartType="bar"
        keyMetrics={
          <KeyMetrics>
            {(() => {
              const m = getCarbonIntensityNarrativeVars();
              if (!m) {
                return (
                  <>
                    <KeyMetric value="—" label="Carbon intensity (latest year)" />
                    <KeyMetric value="—" label="% change since baseline" />
                    <KeyMetric value="—" label="Change since baseline" />
                  </>
                );
              }

              const latestStr = `${Math.round(m.latestValue).toLocaleString()} gCO₂e/kWh`;
              const pctStr =
                m.changePercentSinceBaseline != null
                  ? `${m.changePercentSinceBaseline >= 0 ? '+' : ''}${m.changePercentSinceBaseline.toFixed(1)}%`
                  : '—';
              const absStr =
                m.changeAbsSinceBaseline != null
                  ? `${m.changeAbsSinceBaseline >= 0 ? '+' : ''}${Math.round(
                      m.changeAbsSinceBaseline
                    ).toLocaleString()} gCO₂e/kWh`
                  : '—';
              const changeLabel =
                m.changeAbsSinceBaseline != null && m.changeAbsSinceBaseline < 0
                  ? `decrease since ${m.baselineYear}`
                  : `increase since ${m.baselineYear}`;

              return (
                <>
                  <KeyMetric
                    value={latestStr}
                    label={`Carbon intensity in ${m.latestYear}`}
                  />
                  <KeyMetric
                    value={pctStr}
                    label={`% change since ${m.baselineYear}`}
                  />
                  <KeyMetric value={absStr} label={changeLabel} />
                </>
              );
            })()}
          </KeyMetrics>
        }
        chartContent={
          <CarbonIntensityChart
            geofips={String(selectedGeofips || '')}
            onDataLoaded={setCarbonIntensityData}
          />
        }
        overviewContent={
          (() => {
            const m = getCarbonIntensityNarrativeVars();
            const description = `This chart shows the carbon intensity of electricity supplied to ${currentLocation} over time, measured in gCO₂e/kWh. Carbon intensity indicates how much greenhouse gas emissions are associated with each unit of electricity. Lower values indicate a cleaner electricity supply mix.`;

            let body: string;
            if (!m) {
              body = `Loading electricity carbon intensity data for ${currentLocation}...`;
            } else {
              const latestStr = `${Math.round(m.latestValue).toLocaleString()} gCO₂e/kWh`;
              const baselineStr =
                m.baselineValue != null
                  ? `${Math.round(m.baselineValue).toLocaleString()} gCO₂e/kWh`
                  : '—';
              const pctStr =
                m.changePercentSinceBaseline != null
                  ? `${Math.abs(m.changePercentSinceBaseline).toFixed(1)}%`
                  : '—';
              const increaseDecrease =
                m.changePercentSinceBaseline != null && m.changePercentSinceBaseline < 0
                  ? 'decrease'
                  : 'increase';
              const trendWord =
                m.trend === 'downward'
                  ? 'downward'
                  : m.trend === 'stable'
                    ? 'stable'
                    : 'mixed';

              body = `Electricity carbon intensity in ${currentLocation} was ${latestStr} in ${m.latestYear}. This represents a ${increaseDecrease} of ${pctStr} since ${m.baselineYear} (${baselineStr}). Over the full period, carbon intensity shows a ${trendWord} trend. The chart combines eGRID utility data and Community Choice Aggregator (CCA) Power Content Label (PCL) data. Where values are interpolated or extrapolated, they are estimates used to maintain a continuous time series.`;
            }

            return (
              <OverviewContent
                title="Electricity Carbon Intensity"
                description={description}
              >
                <p>{body}</p>
              </OverviewContent>
            );
          })()
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance"
            description="Electricity carbon intensity is a key input for climate planning because it determines the emissions impact of electricity use in buildings, transportation and other sectors. It helps translate consumption into estimated emissions and provides context for evaluating electrification strategies."
          >
            <div className="methodology-details-simple">
              <h5>Climate Impact</h5>
              <p>
                Carbon intensity of electricity directly affects the benefits of electrification, including electric
                vehicles, heat pumps, electric water heating and electric appliances. The same increases in electricity
                use can result in very different emissions outcomes depending on how clean the local supply is.
              </p>
            </div>
            <div className="methodology-details-simple">
              <h5>Policy Planning</h5>
              <p>
                Local governments use intensity trends to estimate emissions from electricity consumption, assess
                progress toward decarbonization goals, evaluate the emissions implications of electrification programs
                and develop more realistic climate action scenarios.
              </p>
            </div>
            <div className="methodology-details-simple">
              <h5>Community Choice Procurement</h5>
              <p>
                In many areas, electricity carbon intensity may change when a community transitions to a Community
                Choice Aggregator (CCA) or when a utility or CCA changes its procurement mix. Power Content Label data
                can capture these shifts and may differ from broader utility-average values reported in eGRID.
              </p>
            </div>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity"
            description="Changes in electricity carbon intensity affect all residents and businesses connected to the grid, but the benefits and burdens of cleaner electricity may vary across communities."
          >
            <div className="methodology-details-simple">
              <h5>Environmental &amp; Community Health</h5>
              <p>
                Lower-carbon electricity is often associated with lower emissions overall, which can support broader
                public health and environmental goals. However, this chart alone does not show direct pollution impacts
                with or without electricity improvements.
              </p>
            </div>
            <div className="methodology-details-simple">
              <h5>Energy Burden &amp; Affordability</h5>
              <p>
                Cleaner electricity does not necessarily mean lower bills. Communities with high energy burden may
                benefit from cleaner electricity in climate and health terms, but affordability outcomes depend on
                rates, usage, housing quality and access to efficiency upgrades.
              </p>
            </div>
            <div className="methodology-details-simple">
              <h5>Access to Clean Electricity Programs</h5>
              <p>
                CCA programs and clean-energy procurement can expand access to lower-carbon electricity options, but
                participation structures, default enrollment rules and opt-out rates may vary across communities.
              </p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Methodology"
            description={`The chart estimates electricity carbon intensity for ${currentLocation} using utility and CCA data, with interpolation/extrapolation used to fill missing years. All values are reported in grams CO₂ equivalent per kilowatt-hour (gCO₂e/kWh) of electricity.`}
            dataSources={[
              'eGRID / utility data',
              'CCA Power Content Labels (PCLs)',
              'CCAS database for location-to-provider matching',
            ]}
            methodology={[
              `Match ${currentLocation} to utility/CCA provider.`,
              'Use eGRID and PCL match fields to identify available carbon intensity values.',
              'If both are available in the same year, CCA PCL data take priority.',
              'Fill gaps between/after observed years with interpolations/extrapolations (constrain extrapolated values to be non-negative).',
            ]}
          />
        }
        activeView={activeCarbonIntensityView}
        onViewChange={toggleCarbonIntensityView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        appendLocationToTitle={true}
        onDownloadPNG={() => makeDownloadPNG('#carbon-intensity-chart', 'Carbon_Intensity_Electricity', currentLocation)}
        onDownloadCSV={() => {
          if (!carbonIntensityData?.chartData?.labels?.length) return;
          const { labels, datasets } = carbonIntensityData.chartData;
          const rows = labels.map((year: number, i: number) => {
            const row: Record<string, number | string> = { year };
            datasets.forEach((ds: { label: string; data: number[] }) => {
              row[ds.label || 'value'] = ds.data?.[i] ?? '';
            });
            return row;
          });
          downloadDataAsCSV(rows, formatFilename(currentLocation, 'Carbon_Intensity_Data'));
        }}
        pngFilename={formatFilename(currentLocation, 'Carbon_Intensity_Electricity')}
        csvFilename={formatFilename(currentLocation, 'Carbon_Intensity_Data')}
      />
      </SectionWrapper>

      {/* Electricity Prices by Geography */}
      <SectionWrapper sectionId="electricity_prices" bgMap={BUILDINGS_SECTION_BG} refs={sectionObserverRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
      <ChartLayout
        scrollAnchorId="chart-electricity-prices"
        title="Electricity Prices"
        description="This chart compares retail electricity prices in the selected location with California over time and by sector to show local affordability and cost trends."
        chartType="line"
        activeView={electricityPricesView}
        onViewChange={setElectricityPricesView}
        chartSelector={{
          options: [
            { value: 'locationVsCA', label: 'Location vs California' },
            { value: 'byUtility', label: 'Prices by year (utility)' }
          ],
          value: electricityPricesChartView,
          onChange: (v) => setElectricityPricesChartView(v as ElectricityPricesChartView)
        }}
        additionalButtonGroups={[
          {
            label: 'Sector',
            options: [
              { value: 'Residential', label: 'Residential' },
              { value: 'Commercial', label: 'Commercial' },
              { value: 'Industrial', label: 'Industrial' },
              { value: 'Transportation', label: 'Transportation' }
            ],
            value: electricityPricesSector,
            onChange: setElectricityPricesSector
          }
        ]}
        keyMetrics={
          <KeyMetrics>
            {electricityPricesData && (() => {
              const avgLoc = getLatestAveragePrice(electricityPricesData.location);
              const avgCA = getLatestAveragePrice(electricityPricesData.california);
              const latestLocYear = electricityPricesData.location.length
                ? Math.max(...electricityPricesData.location.map((r) => r.year))
                : null;
              const latestCaYear = electricityPricesData.california.length
                ? Math.max(...electricityPricesData.california.map((r) => r.year))
                : null;
              const locStr = avgLoc != null ? `$${avgLoc.toFixed(3)}/kWh` : '—';
              const caStr = avgCA != null ? `$${avgCA.toFixed(3)}/kWh` : '—';
              const pct = avgCA != null && avgCA > 0 && avgLoc != null ? ((avgLoc - avgCA) / avgCA) * 100 : null;
              const pctStr = pct != null ? `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%` : '—';
              return (
                <>
                  <KeyMetric value={locStr} label={latestLocYear != null ? `Location avg (${latestLocYear})` : 'Location avg'} />
                  <KeyMetric value={caStr} label={latestCaYear != null ? `California avg (${latestCaYear})` : 'California avg'} />
                  <KeyMetric value={pctStr} label="Compared to CA State Average" />
                </>
              );
            })()}
            {!electricityPricesData?.location?.length && !electricityPricesData?.california?.length && (
              <>
                <KeyMetric value="—" label="Location avg" />
                <KeyMetric value="—" label="California avg" />
                <KeyMetric value="—" label="Compared to CA State Average" />
              </>
            )}
          </KeyMetrics>
        }
        chartContent={
          <ElectricityPricesByGeographyChart
            geofips={selectedGeofips ?? ''}
            locationName={currentLocation}
            chartView={electricityPricesChartView}
            sector={electricityPricesSector}
            showComparisonToCA={true}
            onDataLoaded={setElectricityPricesData}
          />
        }
        overviewContent={
          (() => {
            const sectorLabel = electricityPricesSector.toLowerCase();
            const locRows = electricityPricesData?.location?.filter((r) => r.sector === electricityPricesSector) || [];
            const caRows = electricityPricesData?.california?.filter((r) => r.sector === electricityPricesSector) || [];
            const latestLocYear = locRows.length ? Math.max(...locRows.map((r) => r.year)) : null;
            const latestCaYear = caRows.length ? Math.max(...caRows.map((r) => r.year)) : null;
            const latestLoc = latestLocYear != null ? locRows.find((r) => r.year === latestLocYear)?.avg_price_per_kwh ?? null : null;
            const latestCa = latestCaYear != null ? caRows.find((r) => r.year === latestCaYear)?.avg_price_per_kwh ?? null : null;
            const pct = latestCa != null && latestCa > 0 && latestLoc != null ? ((latestLoc - latestCa) / latestCa) * 100 : null;
            const pctStr = pct != null ? `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%` : '—';

            return (
              <OverviewContent
                title="Overview"
                description={`Electricity prices capture the retail cost of electricity in dollars per kilowatt-hour ($/kWh) and indicate how expensive it is to use electricity across residential, commercial, industrial and transportation sectors in ${currentLocation}. Price trends help contextualize affordability and cost pressure over time, while comparisons to California indicate whether local conditions are above or below statewide norms.${
                  electricityPricesData?.eia861Cca
                    ? ` This location has a Community Choice Aggregation (CCA) program: the time series uses CCA price data where available, and the previous investor-owned utility (IOU) for prior years.`
                    : ''
                }`}
              >
                <p>
                  {latestLoc != null && latestLocYear != null
                    ? `${electricityPricesSector} electricity prices in ${currentLocation} were about $${latestLoc.toFixed(3)}/kWh in ${latestLocYear}. ${latestCa != null && latestCaYear != null ? `California was about $${latestCa.toFixed(3)}/kWh in ${latestCaYear}, meaning ${currentLocation} is ${pctStr} compared to the state average for this sector.` : ''} ${electricityPricesData?.utilityName ? `Matched utility source: ${electricityPricesData.utilityName}.` : 'No utility match found; California weighted averages are used where needed.'}`
                    : `Loading or no ${sectorLabel} electricity price data for ${currentLocation}.`}
                </p>
              </OverviewContent>
            );
          })()
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance"
            description="Electricity price trajectories are a core planning input for building decarbonization, electrification and energy-efficiency policy. Even when emissions factors improve, higher retail prices can change project economics and household affordability."
          >
            <div className="methodology-details-simple">
              <h5>Climate Action Planning</h5>
              <p>Price levels directly affect operating-cost savings from upgrades such as heat pumps, weatherization and efficient appliances. Tracking local price trends improves screening for policies that are cost-effective under real local conditions.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Program Design &amp; Budgeting</h5>
              <p>Comparisons to California averages help identify where incentives, bill protections or targeted support may be needed. Higher-than-average prices can justify stronger affordability measures alongside decarbonization investments.</p>
            </div>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity"
            description="Retail electricity prices can have uneven impacts across households and businesses. Communities with high burdens may face greater difficulty adopting electrification technologies without complementary support."
          >
            <div className="methodology-details-simple">
              <h5>Energy Burden &amp; Affordability</h5>
              <p>Low-income households generally spend a larger share of income on utility bills. When local prices are above statewide averages, bill impacts may be amplified unless programs include protections such as targeted rebates, on-bill support or tenant-focused measures.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Access to Electrification Benefits</h5>
              <p>High prices can reduce the near-term savings from electrification upgrades for renters, small businesses and under-resourced communities. Equity-focused planning should pair technology adoption goals with affordability and access interventions.</p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Methods"
            description={`Prices are from EIA-861 Sales to Ultimate Customers: revenue and sales by utility, state, and sector. When the utilities database provides previous IOU and CCA start year (ccas.cca_start_year, ccas.previous_iou_eia861), prices use the IOU before that year and the CCA from that year onward (e.g. Berkeley: PG&E 2005–2019, Ava 2020+). Otherwise location is matched to utility via CCAS and name matching; missing matches use the California weighted average.${
              electricityPricesData?.eia861Cca
                ? ` For this location (CCA: ${electricityPricesData.eia861Cca}), the compiled time series follows that rule: IOU data for years before CCA service, CCA data from the CCA start year onward where available.`
                : ''
            }`}
            dataSources={['EIA-861 Sales to Ultimate Customers', 'electricity_prices_by_geography', 'electricity_utility_crosswalk', 'ccas (cca_start_year, previous_iou_eia861 when available)']}
            methodology={[
              'Average $/kWh = revenue ÷ sales (MWh × 1000).',
              'Year-varying utility: IOU before CCA start year, CCA from start year; crosswalk stores eia861_iou and eia861_cca.',
              ...(electricityPricesData?.eia861Cca
                ? [
                    `This location is CCA-served (${electricityPricesData.eia861Cca}): series uses CCA prices where available and the prior IOU for earlier years.`,
                  ]
                : []),
            ]}
          />
        }
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        appendLocationToTitle={true}
      />
      </SectionWrapper>

      {/* Energy Cost Burden (LEAD 2022) */}
      <SectionWrapper
        sectionId="energy_cost_burden"
        bgMap={BUILDINGS_SECTION_BG}
        refs={sectionObserverRefs}
        visible={sectionVisible}
        getSectionStyle={getSectionStyle}
      >
        <LeadEnergyCostBurdenSection location={currentLocation} geofips={selectedGeofips ?? ''} />
      </SectionWrapper>

      {/* Natural Gas */}
      <SectionWrapper sectionId="natural_gas_consumption" bgMap={BUILDINGS_SECTION_BG} refs={sectionObserverRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
      <ChartLayout
        scrollAnchorId="chart-natural-gas-consumption"
        title="Natural Gas Consumption"
        description="This chart tracks natural gas consumption over time across building sectors, helping assess fossil-fuel dependence and opportunities for electrification."
        chartType="bar"
        chartSelector={{
          options: allowedNaturalGasOptions.map((option) => ({ value: option.value, label: option.label })),
          value: gasView,
          onChange: (value) => setGasView(value as 'res' | 'com' | 'total' | 'perCapita')
        }}
        keyMetrics={
          <KeyMetrics>
            {gasView === 'res' && gasRes && gasRes.chartData && (() => {
              const metrics = getSectorMetrics(gasRes, 2005);
              const displayYear = metrics.latestYear > 0 ? metrics.latestYear : 2023;
              const latestTJ =
                metrics.latestValue > 0
                  ? `${formatThermsAsTJ(metrics.latestValue)} TJ`
                  : '—';
              const pct = metrics.changePercentSince2005;
              const pctDisplay =
                pct != null ? `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%` : '—';
              const abs = metrics.changeAbsSince2005;
              const absDisplay =
                abs != null
                  ? `${abs >= 0 ? '+' : ''}${formatThermsAsTJ(Math.abs(abs))} TJ`
                  : '—';
              const changeLabel =
                abs != null && abs < 0 ? 'decrease since 2005' : 'increase since 2005';

              return (
                <>
                  <KeyMetric
                    value={latestTJ}
                    label={`Residential consumption in ${displayYear}`}
                  />
                  <KeyMetric value={pctDisplay} label="% change since 2005" />
                  <KeyMetric value={absDisplay} label={changeLabel} />
                </>
              );
            })()}
            {gasView === 'com' && gasCom && gasCom.chartData && (() => {
              const metrics = getSectorMetrics(gasCom, 2005);
              const displayYear = metrics.latestYear > 0 ? metrics.latestYear : 2023;
              const latestTJ =
                metrics.latestValue > 0
                  ? `${formatThermsAsTJ(metrics.latestValue)} TJ`
                  : '—';
              const pct = metrics.changePercentSince2005;
              const pctDisplay =
                pct != null ? `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%` : '—';
              const abs = metrics.changeAbsSince2005;
              const absDisplay =
                abs != null
                  ? `${abs >= 0 ? '+' : ''}${formatThermsAsTJ(Math.abs(abs))} TJ`
                  : '—';
              const changeLabel =
                abs != null && abs < 0 ? 'decrease since 2005' : 'increase since 2005';

              return (
                <>
                  <KeyMetric
                    value={latestTJ}
                    label={`Commercial consumption in ${displayYear}`}
                  />
                  <KeyMetric value={pctDisplay} label="% change since 2005" />
                  <KeyMetric value={absDisplay} label={changeLabel} />
                </>
              );
            })()}
            {gasView === 'total' && gasRes && gasRes.chartData && gasCom && gasCom.chartData && (() => {
              const metrics = calculateTotalMetrics(gasRes, gasCom);
              const displayYear = metrics.latestYear > 0 ? metrics.latestYear : 2023;
              const latestTJ =
                metrics.latestValue > 0
                  ? `${formatThermsAsTJ(metrics.latestValue)} TJ`
                  : '—';
              const pct = metrics.changePercentSince2005;
              const pctDisplay =
                pct != null ? `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%` : '—';
              const abs = metrics.changeAbsSince2005;
              const absDisplay =
                abs != null
                  ? `${abs >= 0 ? '+' : ''}${formatThermsAsTJ(Math.abs(abs))} TJ`
                  : '—';
              const changeLabel =
                abs != null && abs < 0 ? 'decrease since 2005' : 'increase since 2005';

              return (
                <>
                  <KeyMetric
                    value={latestTJ}
                    label={`Total consumption in ${displayYear}`}
                  />
                  <KeyMetric value={pctDisplay} label="% change since 2005" />
                  <KeyMetric value={absDisplay} label={changeLabel} />
                </>
              );
            })()}
            {gasView === 'perCapita' &&
              gasPerCapitaData &&
              gasPerCapitaData.chartData &&
              californiaGasPerCapita &&
              californiaGasPerCapita.chartData &&
              (() => {
                const locYear = gasPerCapitaData.latestYear;
                const caYear = californiaGasPerCapita.latestYear ?? locYear;
                const labels = (gasPerCapitaData.chartData.labels || []) as number[];
                const data = gasPerCapitaData.chartData.datasets?.[0]?.data as
                  | number[]
                  | undefined;

                const i2005 = labels.indexOf(2005);
                const value2005 =
                  data != null && i2005 >= 0 ? Number(data[i2005]) : null;
                const latestVal = gasPerCapitaData.latestValue;
                const pctChange2005 =
                  value2005 != null && value2005 > 0 && latestVal != null
                    ? ((latestVal - value2005) / value2005) * 100
                    : null;
                const pctDisplay =
                  pctChange2005 != null
                    ? `${pctChange2005 >= 0 ? '+' : ''}${pctChange2005.toFixed(1)}%`
                    : '—';

                return (
                  <>
                    <KeyMetric
                      value={
                        gasPerCapitaData.latestValue != null
                          ? `${(gasPerCapitaData.latestValue * 0.1055).toFixed(1)} GJ/person`
                          : '—'
                      }
                      label={`Residential Usage Per Capita in ${locYear || '—'}`}
                    />
                    <KeyMetric
                      value={
                        californiaGasPerCapita.latestValue != null
                          ? `${Math.round(
                              californiaGasPerCapita.latestValue * 0.1055
                            ).toFixed(1)} GJ/person`
                          : '—'
                      }
                      label={`CA Average Residential Usage in ${caYear || '—'}`}
                    />
                    <KeyMetric
                      value={pctDisplay}
                      label="% decrease since 2005"
                    />
                  </>
                );
              })()}
          </KeyMetrics>
        }
        chartContent={
          <div>
            {gasView === 'res' && gasRes && (
              <>
                <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                  {gasRes.allDataObserved && <span>100% observed data</span>}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>Residential extrapolation:</span>
                    <select
                      value={residentialExtrapModel}
                      onChange={(e) => setResidentialExtrapModel(e.target.value as ResidentialExtrapolationModel)}
                      style={{
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        padding: '6px 8px',
                        fontSize: '13px',
                        background: '#fff'
                      }}
                    >
                      {RESIDENTIAL_MODEL_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <ChartRendererSupabase chartData={gasRes.chartData} chartOptions={gasRes.chartOptions} height="450px" title="Residential Natural Gas" location={currentLocation} chartRef={gasChartRef} />
              </>
            )}
            {gasView === 'com' && gasCom && (
              <>
                <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                  {gasCom.allDataObserved && <span>100% observed data</span>}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>Extrapolation method:</span>
                    <select
                      value={commercialGasExtrapModel}
                      onChange={(e) => setCommercialGasExtrapModel(e.target.value as CommercialExtrapolationModel)}
                      style={{
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        padding: '6px 8px',
                        fontSize: '13px',
                        background: '#fff'
                      }}
                    >
                      {availableCommercialGasModelOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <ChartRendererSupabase chartData={gasCom.chartData} chartOptions={gasCom.chartOptions} height="450px" title="Commercial Natural Gas" location={currentLocation} chartRef={gasChartRef} />
              </>
            )}
            {gasView === 'total' && gasStacked && (
              <ChartRendererSupabase chartData={gasStacked.chartData} chartOptions={gasStacked.chartOptions} height="470px" title="Total Natural Gas (Stacked)" location={currentLocation} chartRef={gasChartRef} />
            )}
            {gasView === 'perCapita' && gasPerCapitaData && californiaGasPerCapita && (
              <ChartRendererSupabase 
                chartData={{
                  labels: gasPerCapitaData.chartData.labels,
                  datasets: [
                    ...gasPerCapitaData.chartData.datasets,
                    ...californiaGasPerCapita.chartData.datasets
                  ]
                }}
                chartOptions={gasPerCapitaData.chartOptions}
                height="450px"
                title="Natural Gas Residential Per Capita"
                location={currentLocation}
                chartRef={gasChartRef}
              />
            )}
            {!gasRes && !gasCom && !gasStacked && (
              <div className="placeholder-chart"><p>Loading natural gas data…</p></div>
            )}
          </div>
        }
        overviewContent={
          (() => {
            const isPerCapitaGasView = gasView === 'perCapita';
            const gasTotals = getNaturalGasNarrativeVars();

            const description =
              'This chart shows the total amount of natural gas used by homes and businesses in a community over time. It helps us understand whether the community is moving toward cleaner energy systems or continuing to rely on traditional fuel sources.';

            let body: string;

            if (isPerCapitaGasView) {
              if (!gasPerCapitaData || !gasPerCapitaData.chartData) {
                body = `Loading natural gas data for ${currentLocation}...`;
              } else {
                const labels = (gasPerCapitaData.chartData.labels || []) as number[];
                const data = gasPerCapitaData.chartData.datasets?.[0]?.data as
                  | number[]
                  | undefined;

                const latestYear =
                  gasPerCapitaData.latestYear ||
                  (labels.length ? labels[labels.length - 1] : undefined);
                const latestIndex =
                  latestYear != null ? labels.indexOf(latestYear) : -1;
                const latestValue =
                  gasPerCapitaData.latestValue != null
                    ? gasPerCapitaData.latestValue
                    : data && latestIndex >= 0
                      ? Number(data[latestIndex])
                      : null;

                const i2005 = labels.indexOf(2005);
                const value2005 =
                  data != null && i2005 >= 0 ? Number(data[i2005]) : null;

                const pctChange2005 =
                  value2005 != null && value2005 > 0 && latestValue != null
                    ? ((latestValue - value2005) / value2005) * 100
                    : null;

                const latestStr =
                  latestValue != null
                    ? `${(latestValue * 0.1055).toFixed(1)} GJ/person`
                    : '—';
                const baselineStr =
                  value2005 != null
                    ? `${(value2005 * 0.1055).toFixed(1)} GJ/person`
                    : '—';
                const pctStr =
                  pctChange2005 != null
                    ? `${pctChange2005 >= 0 ? '+' : ''}${pctChange2005.toFixed(1)}%`
                    : '—';

                let caComparisonSentence =
                  'The average person in ' +
                  currentLocation +
                  ' consumes about the same amount of natural gas as the California state average, which may align with factors such as similar energy efficiency and sustainable economic growth.';

                if (
                  californiaGasPerCapita &&
                  californiaGasPerCapita.latestValue != null &&
                  latestValue != null
                ) {
                  const caVal = californiaGasPerCapita.latestValue;
                  if (caVal > 0) {
                    const diffPct = ((latestValue - caVal) / caVal) * 100;
                    const diffAbsPct = Math.abs(diffPct).toFixed(1) + '%';
                    const moreOrLess = diffPct >= 0 ? 'more' : 'less';

                    let efficiencyWord = 'decreased';
                    if (pctChange2005 != null && pctChange2005 < 0) {
                      efficiencyWord = 'increased';
                    }

                    const sustainabilityWord = diffPct >= 0 ? 'less' : 'more';

                    caComparisonSentence = `The average person in ${currentLocation} consumes ${diffAbsPct} ${moreOrLess} natural gas than the California state average, which may align with factors such as ${efficiencyWord} energy efficiency and ${sustainabilityWord} sustainable economic growth.`;
                  }
                }

                body = `Residential natural gas consumption in ${currentLocation} averaged to around ${latestStr} in ${
                  latestYear ?? 'the latest year available'
                }, showcasing a ${pctStr} change since 2005 (${baselineStr}). ${caComparisonSentence}`;
              }
            } else if (gasView === 'res' && gasRes && gasRes.chartData) {
              body =
                'Residential natural gas consumption in Berkeley totaled 19,690.5 GJ in 2023 based on available utility data and gap-filled historical values. This represents an decrease of 22.4% since 2005 (2,536,643.9 GJ). Over the full time period, total natural gas consumption has shown a downward trend, with some year-to-year variation. If consumption is declining, this may reflect improved building efficiency, better insulation, or more efficient heating systems.';
            } else if (gasView === 'com' && gasCom && gasCom.chartData) {
              body =
                'Commercial natural gas consumption in Berkeley totaled 19,690.5 GJ in 2023 based on available utility data and gap-filled historical values. This represents an decrease of 22.4% since 2005 (2,536,643.9 GJ). Over the full time period, total natural gas consumption has shown a downward trend, with some year-to-year variation. If consumption is declining, this may reflect improved building efficiency, better insulation, or more efficient heating systems.';
            } else if (!gasTotals) {
              body = `Loading natural gas data for ${currentLocation}...`;
            } else {
              const latestGJ = formatThermsAsGJ(gasTotals.latestValue);
              const baselineThermsStr =
                gasTotals.baselineValue != null
                  ? `${(gasTotals.baselineValue * 0.1055).toLocaleString(undefined, { maximumFractionDigits: 1 })} GJ`
                  : '—';
              const pct =
                gasTotals.changePercentSinceBaseline != null
                  ? Math.abs(gasTotals.changePercentSinceBaseline).toFixed(1)
                  : '—';
              const increaseDecrease =
                gasTotals.changePercentSinceBaseline != null &&
                gasTotals.changePercentSinceBaseline < 0
                  ? 'decrease'
                  : 'increase';
              const trendWord =
                gasTotals.trend === 'upward'
                  ? 'upward'
                  : gasTotals.trend === 'downward'
                    ? 'downward'
                    : 'stable';

              let extraSentence = '';
              if (
                gasTotals.changePercentSinceBaseline != null &&
                gasTotals.changePercentSinceBaseline < -5
              ) {
                extraSentence =
                  ' If consumption is declining, this may reflect improved building efficiency, better insulation, or more efficient heating systems.';
              } else if (
                gasTotals.changePercentSinceBaseline != null &&
                gasTotals.changePercentSinceBaseline > 5
              ) {
                extraSentence =
                  ' If consumption is increasing, this may reflect population growth, new residential or commercial development, colder seasonal conditions, or continued reliance on gas-fired heating systems.';
              }

              body = `Residential and commercial natural gas consumption in ${currentLocation} totaled ${latestGJ} GJ in ${gasTotals.latestYear} based on available utility data and gap-filled historical values. This represents an ${increaseDecrease} of ${pct}% since ${gasTotals.baselineYear} (${baselineThermsStr}). Over the full time period, total natural gas consumption has shown a ${trendWord} trend, with some year-to-year variation.${extraSentence}`;
            }

            return (
              <OverviewContent
                title="Natural Gas Overview"
                description={description}
              >
                <p>{body}</p>
              </OverviewContent>
            );
          })()
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance"
            description="Natural gas consumption is an important indicator for climate planning because burning natural gas directly produces greenhouse gas emissions. For this reason, changes in gas consumption directly affect local emissions inventories."
          >
            <div className="methodology-details-simple">
              <h5>Climate Action Planning</h5>
              <p>
                Local governments often examine natural gas trends to assess whether building-sector emissions are
                increasing or decreasing over time.
              </p>
            </div>
            <div className="methodology-details-simple">
              <h5>Long-Term Energy Transition</h5>
              <p>
                Tracking gas demand helps communities plan long-term infrastructure needs. As electric heating and
                water-heating technologies become more common, overall gas demand may shift. Understanding these
                patterns can support coordination between cities and utility providers.
              </p>
            </div>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity"
            description="Changes in natural gas consumption can affect households and businesses differently. While the chart presents community-wide totals, the financial and environmental impacts of gas use are not always evenly distributed."
          >
            <div className="methodology-details-simple">
              <h5>Energy Burden &amp; Affordability</h5>
              <p>
                Lower-income households often spend a higher percentage of their income on heating and utility costs.
                Even if overall gas consumption declines, this does not necessarily mean that energy is becoming more
                affordable for everyone.
              </p>
            </div>
            <div className="methodology-details-simple">
              <h5>Housing Quality &amp; Condition</h5>
              <p>
                Older or poorly insulated buildings may require more heating energy, increasing natural gas use and
                costs for residents in those homes. Households living in inefficient buildings may experience higher
                energy bills compared to those in newer or upgraded homes.
              </p>
            </div>
            <div className="methodology-details-simple">
              <h5>Access to Electrification</h5>
              <p>
                Switching from gas appliances to electric systems often requires upfront investment. Renters,
                lower-income households and small businesses may face barriers to making these upgrades, which can
                affect who benefits from building decarbonization efforts.
              </p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Methodology"
            description="The natural gas consumption chart combines residential and commercial utility-reported data over time."
            dataSources={
              gasMethodology?.dataSources && gasMethodology.dataSources.length > 0
                ? gasMethodology.dataSources
                : ['Utility provider data (e.g., PG&E, SoCalGas)']
            }
            methodology={[
              `Annual natural gas consumption data are compiled for residential and commercial sectors in ${currentLocation}.`,
              'Observed utility values are used where available.',
              'Missing years are estimated using interpolation or extrapolation techniques.',
              'Residential and commercial values are combined to show total annual consumption.',
              'Where full data are not available, interpolation or extrapolation methods are used to maintain a continuous time series.',
            ]}
          />
        }
        activeView={activeView}
        onViewChange={toggleView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        appendLocationToTitle={true}
        overviewSectionId="buildings"
        overviewChartId="natural_gas_consumption"
        className="alternating-bg"
        dataTableContent={gasDataTable.showDataTable ? (
          <div>
            {gasDataTable.modifiedYears.size > 0 && (
              <div className="modified-years-info" style={{ marginBottom: '16px' }}>
                <small>
                  <strong>Modified years:</strong> {Array.from(gasDataTable.modifiedYears).sort().join(', ')} 
                  (other years scaled proportionally; values shown in GJ)
                </small>
              </div>
            )}
            <DataTable
              data={gasDataTable.tableData}
              highlightedRows={gasDataTable.modifiedYears}
              highlightKey="year"
              columns={[
                { key: 'year', label: 'Year', type: 'year', editable: true, 
                  validate: (value) => {
                    const year = parseInt(value);
                    return (year >= 2005 && year <= 2023) ? null : 'Year must be between 2005-2023';
                  }
                },
                { key: 'residential', label: 'Residential (GJ)', type: 'number', editable: true,
                  validate: (value) => {
                    const num = parseFloat(value);
                    return (!isNaN(num) && num >= 0) ? null : 'Value must be a positive number';
                  }
                },
                { key: 'commercial', label: 'Commercial (GJ)', type: 'number', editable: true,
                  validate: (value) => {
                    const num = parseFloat(value);
                    return (!isNaN(num) && num >= 0) ? null : 'Value must be a positive number';
                  }
                },
                { key: 'total', label: 'Total (GJ)', type: 'number', editable: false },
                { key: 'location', label: 'Location', type: 'text', editable: false }
              ]}
              onDataChange={gasDataTable.handleDataTableChange}
              onRestoreDefaults={gasDataTable.restoreDefaults}
              title="Natural Gas Data"
              maxHeight="500px"
              pageSize={15}
            />
          </div>
        ) : null}
        showDataTable={gasDataTable.showDataTable}
        onToggleDataTable={gasDataTable.toggleDataTable}
        onDownloadPNG={handleGasDownloadPNG}
        onDownloadCSV={handleGasDownloadCSV}
        pngFilename={`Natural_Gas_${gasView === 'res' ? 'Residential' : gasView === 'com' ? 'Commercial' : gasView === 'total' ? 'Total' : 'Per_Capita'}_Chart`}
        csvFilename={`Natural_Gas_${gasView === 'res' ? 'Residential' : gasView === 'com' ? 'Commercial' : gasView === 'total' ? 'Total' : 'Per_Capita'}_Data`}
      />
      </SectionWrapper>

      {/* Primary Heating Fuel (Supabase-backed) */}
      <SectionWrapper sectionId="heating_fuel" bgMap={BUILDINGS_SECTION_BG} refs={sectionObserverRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
        <ChartLayout
          title="Primary Heating Fuel by Year"
          description="This chart shows how household primary heating fuel choices change over time, including natural gas, electricity, and other fuels."
          chartType="line"
          chartSelector={{
            value: heatingFuelChartMode,
            onChange: (value: string) => setHeatingFuelChartMode(value as 'main' | 'other' | 'fossil' | 'compare'),
            options: [
              { value: 'main', label: 'Main fuels' },
              { value: 'other', label: 'Other fuels' },
              { value: 'fossil', label: 'Percent fossil' },
              { value: 'compare', label: 'vs California' }
            ]
          }}
          keyMetrics={
            <KeyMetrics>
              <KeyMetric
                value={heatingFuelMetrics ? `${heatingFuelMetrics.gasPercent.toFixed(1)}%` : 'Loading...'}
                label={heatingFuelMetrics ? `Households using gas (${heatingFuelMetrics.latestYear})` : 'Households using gas'}
                color="#f59e0b"
              />
              <KeyMetric
                value={heatingFuelMetrics ? `${heatingFuelMetrics.elecPercent.toFixed(1)}%` : 'Loading...'}
                label={heatingFuelMetrics ? `Households using electricity (${heatingFuelMetrics.latestYear})` : 'Households using electricity'}
                color="#3b82f6"
              />
              <KeyMetric
                value={heatingFuelMetrics && heatingFuelMetrics.elecPercent > 0 ? `${(heatingFuelMetrics.gasPercent / heatingFuelMetrics.elecPercent).toFixed(1)}x` : '—'}
                label="Gas-to-electric ratio"
                color="#6b7280"
              />
              <KeyMetric
                value={heatingFuelMetrics ? `${heatingFuelMetrics.fossilPercent.toFixed(1)}%` : 'Loading...'}
                label={heatingFuelMetrics ? `Fossil heating share (${heatingFuelMetrics.latestYear})` : 'Fossil heating share'}
                color="#7c2d12"
              />
            </KeyMetrics>
          }
          chartContent={
            <HouseHeatingFuelSupabase
              locationName={currentLocation}
              onMetricsLoaded={setHeatingFuelMetrics}
              chartMode={heatingFuelChartMode}
            />
          }
          overviewContent={
            <OverviewContent
              title="Overview"
              description="This graph shows the share of households using different types of primary fuels for space heating over time. This indicates how dependent a community is on fossil fuels, like natural gas or coal, versus electricity and other fuels, and helps track progress towards electrification. Major shifts in fuel shares are indicative of building codes, appliance standards, resident preferences, or other programs."
            >
              <p>
                {heatingFuelMetrics
                  ? `In ${currentLocation}, ${heatingFuelMetrics.topFuelPercent.toFixed(1)}% of households used ${heatingFuelMetrics.topFuelType} as their primary heating fuel, while ${heatingFuelMetrics.secondFuelPercent.toFixed(1)}% used ${heatingFuelMetrics.secondFuelType} as of ${heatingFuelMetrics.latestYear}. ${heatingFuelMetrics.topFuelType === 'Electricity' ? 'Electricity is the dominant heating fuel, indicating that this region has already undergone large-scale electrification.' : `${heatingFuelMetrics.topFuelType} is the dominant heating fuel, indicating significant opportunity for building electrification through heat pump adoption and other electric heating technologies.`}`
                  : `Loading heating fuel data for ${currentLocation}...`}
              </p>
            </OverviewContent>
          }
          relevanceContent={
            <RelevanceContent
              title="Relevance"
              description="The distribution of primary heating fuels is an indicator for building-sector emissions and a key input for climate and energy planning. Communities that rely heavily on natural gas for space heating face larger decarbonization challenges."
            >
              <div className="methodology-details-simple">
                <h5>Climate Action Planning</h5>
                <p>Tracking fuel shares over time helps measure progress toward electrification goals. Local governments use fuel data to identify the scale of electrification needed, set targets for conversions, and evaluate the effectiveness of heat pump incentive programs and building codes that promote new all-electric construction.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Building Decarbonization</h5>
                <p>Transitioning from gas to electric heating is one of the primary strategies for reducing building-related greenhouse gas emissions. Tracking the gas-to-electric ratio over time shows whether a community is making real progress on this transition.</p>
              </div>
            </RelevanceContent>
          }
          equityContent={
            <EquityContent
              title="Equity"
              description="Heating fuel reliance and the ability to switch technologies can have uneven implications across households. While the chart presents community-wide shares, the costs and benefits of fuel transitions are not always distributed fairly across communities."
            >
              <div className="methodology-details-simple">
                <h5>Energy Burden</h5>
                <p>Lower-income households spend a larger share of their budget on heating fuel costs and face greater barriers for switching from gas to electric systems. Without rebates, financing, or income-qualified programs, the benefits of electrification may accrue disproportionately to higher-income homeowners.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Housing Type &amp; Tenure</h5>
                <p>Renters and residents of multifamily buildings often cannot control major equipment decisions, such as replacing a gas furnace with a heat pump. Building age and dynamics between landlords and tenants slow electrification, creating disparities in who benefits from cleaner heating technologies.</p>
              </div>
            </EquityContent>
          }
          methodologyContent={
            <MethodologyContent
              title="Methodology"
              description="The primary heating fuel chart tracks the share of households using each fuel type over time, drawn from American Community Survey data processed into the ClimatePlans database. Data provenance is consistent across years, allowing trend comparison."
              dataSources={['American Community Survey 5-Year Estimates (Table B25040)', 'ClimatePlans processed dataset (house_heating_fuel table)']}
              methodology={[
                `Annual shares of households by primary heating fuel are compiled for ${currentLocation} from ACS Table B25040 (House Heating Fuel).`,
                'Tract-level data is allocated to places and counties using block-group-to-place relationship files, consistent with other ClimatePlans indicators.',
                'Percent of households is calculated per fuel type, per geography, per year; fuel categories follow ACS definitions (Utility Gas, Electricity, Bottled/LP gas, Fuel oil/kerosene, Wood, Solar energy, Coal/coke, Other fuel, No fuel used).',
              ]}
            />
          }
          activeView={activeHeatingFuelView}
          onViewChange={setActiveHeatingFuelView}
          showRelevanceEquity={true}
          enableComments={true}
          location={currentLocation}
          appendLocationToTitle={true}
          overviewSectionId="buildings"
          overviewChartId="house_heating_fuel"
        />
      </SectionWrapper>

      {/* Solar PV Capacity & Projects (combined) */}
      <SectionWrapper sectionId="solar_pv" bgMap={BUILDINGS_SECTION_BG} refs={sectionObserverRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
      <ChartLayout
        scrollAnchorId="chart-cumulative-solar"
        title="Solar Installations"
        description="This chart shows installed solar photovoltaic (PV) capacity in the selected location over time. Solar PV systems generate electricity from sunlight and are one of the most common forms of renewable energy used in homes and businesses. Tracking solar installations shows how quickly a community is adopting clean energy technologies and reducing its reliance on fossil-fuel-based electricity."
        chartType="bar"
        chartSelector={{
          grouped: true,
          groups: solarGroups.map((group) => ({
            category: group.category,
            label: group.label,
            options: group.options.map((option) => ({ value: option.value, label: option.label }))
          })),
          value: pvView,
          onChange: (value) => setPvView(value as 'capacity' | 'cost' | 'cumulative' | 'perCapita' | 'batteryCost' | 'installers' | 'installersCost' | 'potential')
        }}
        keyMetrics={
          pvView === 'cost' ? (
            <KeyMetrics>
              <KeyMetric
                value={costData ? `$${costData.avgResidentialCost.toFixed(2)}` : '$0.00'}
                label="Avg residential cost per watt"
                color="#1e3a8a"
              />
              <KeyMetric
                value={
                  costData && typeof costData.resCostLatestYear === 'number' && typeof costData.comCostLatestYear === 'number'
                    ? `$${(((costData.resCostLatestYear ?? 0) + (costData.comCostLatestYear ?? 0)) / 2).toFixed(2)}`
                    : '$0.00'
                }
                label={costData?.latestYear ? `Avg cost in ${costData.latestYear}` : 'Cost in most recent year'}
                color="#10b981"
              />
              <KeyMetric
                value={
                  costData && typeof costData.percentChange === 'number'
                    ? `${Math.abs(costData.percentChange).toFixed(1)}%`
                    : '0%'
                }
                label={costData?.baselineYear ? `Cost change since ${costData.baselineYear}` : 'Cost change since baseline'}
                color="#f59e0b"
              />
            </KeyMetrics>
          ) : pvView === 'batteryCost' ? (
            <KeyMetrics>
              <KeyMetric 
                value={batteryCostData?.avgPVCost ? `$${Math.round(batteryCostData.avgPVCost)}/kW` : '$0/kW'} 
                label="Average PV cost"
                color="#3b82f6"
              />
              <KeyMetric 
                value={batteryCostData?.avgBatteryCost ? `$${Math.round(batteryCostData.avgBatteryCost)}/kW` : '$0/kW'} 
                label="Average PV+Battery cost"
                color="#10b981"
              />
              <KeyMetric 
                value={batteryCostData?.avgBatteryCost && batteryCostData?.avgPVCost ? 
                  `$${Math.round(batteryCostData.avgBatteryCost - batteryCostData.avgPVCost)}/kW` : '$0/kW'} 
                label="Battery premium"
                color="#f59e0b"
              />
            </KeyMetrics>
          ) : pvView === 'installers' ? (
            <KeyMetrics>
              <KeyMetric 
                value={installersData?.totalInstallers ? `${installersData.totalInstallers}` : '0'} 
                label="Top pairs"
                color="#3b82f6"
              />
              <KeyMetric 
                value={installersData?.topInstaller ? installersData.topInstaller : 'N/A'} 
                label="Leading pair"
                color="#10b981"
              />
              <KeyMetric 
                value={installersData?.topInstallerKw ? `${installersData.topInstallerKw.toFixed(1)} kW` : '0 kW'} 
                label="Top pair capacity"
                color="#f59e0b"
              />
            </KeyMetrics>
          ) : pvView === 'installersCost' ? (
            <KeyMetrics>
              <KeyMetric 
                value={installersCostData?.totalPairs ? `${installersCostData.totalPairs}` : '0'} 
                label="Top pairs"
                color="#3b82f6"
              />
              <KeyMetric 
                value={installersCostData?.topPair ? installersCostData.topPair : 'N/A'} 
                label="Leading pair"
                color="#10b981"
              />
              <KeyMetric 
                value={installersCostData?.avgCost ? `$${installersCostData.avgCost.toFixed(2)}/W` : '$0.00/W'} 
                label="Average cost per watt"
                color="#f59e0b"
              />
            </KeyMetrics>
          ) : pvView === 'potential' ? (
            <KeyMetrics>
              <KeyMetric
                value={solarPotentialData?.latestTechnicalPotentialMw != null ? `${solarPotentialData.latestTechnicalPotentialMw.toFixed(1)} MW` : '—'}
                label={solarPotentialData?.latestYear != null ? `Technical potential (${solarPotentialData.latestYear})` : 'Technical potential'}
                color="#1e3a8a"
              />
              <KeyMetric
                value={solarPotentialData?.latestEconomicPotentialMw != null ? `${solarPotentialData.latestEconomicPotentialMw.toFixed(1)} MW` : '—'}
                label={solarPotentialData?.latestYear != null ? `Economic potential (${solarPotentialData.latestYear})` : 'Economic potential'}
                color="#10b981"
              />
              <KeyMetric
                value={solarPotentialData?.latestGenerationMwh != null ? `${Math.round(solarPotentialData.latestGenerationMwh).toLocaleString()} MWh` : '—'}
                label={solarPotentialData?.latestYear != null ? `Potential generation (${solarPotentialData.latestYear})` : 'Potential generation'}
                color="#f59e0b"
              />
            </KeyMetrics>
          ) : (
            <KeyMetrics>
              <KeyMetric value={capacityData?.latestYearCapacity ? `${capacityData.latestYearCapacity.toFixed(1)} MW` : '—'} label={`Total capacity in ${capacityData?.latestYear ?? '—'}`} />
              <KeyMetric value={projectCountData?.latestYearProjects ? `${projectCountData.latestYearProjects.toLocaleString()}` : '—'} label={`Active projects in ${capacityData?.latestYear ?? '—'}`} />
              <KeyMetric value={capacityData?.percentChangeSince2015 != null ? `${capacityData.percentChangeSince2015 >= 0 ? '+' : ''}${capacityData.percentChangeSince2015.toFixed(1)}%` : '—'} label="% increase since 2015" />
            </KeyMetrics>
          )
        }
        chartContent={
          <div id={`solar-pv-${pvView}`}>
          {pvView === 'cost' ? (
            costData ? (
              <ChartRendererSupabase
                chartData={costData.chartData}
                chartOptions={costData.chartOptions}
                height="380px"
                title={`Solar PV Cost per Watt - ${currentLocation}`}
                location={currentLocation}
              />
            ) : (
              <div className="placeholder-chart">
                <p>Loading Solar PV Cost data...</p>
              </div>
            )
          ) : pvView === 'cumulative' ? (
            selectedGeofips ? (
              <CumulativeSolarCapacityChart location={currentLocation} geofips={selectedGeofips} />
            ) : (
              <div className="placeholder-chart">
                <p>Loading location data...</p>
              </div>
            )
          ) : pvView === 'perCapita' ? (
            selectedGeofips ? (
              <SolarPerCapitaComparisonChart location={currentLocation} geofips={selectedGeofips} />
            ) : (
              <div className="placeholder-chart">
                <p>Loading location data...</p>
              </div>
            )
          ) : pvView === 'potential' ? (
            solarPotentialData?.chartData && solarPotentialData?.chartOptions ? (
              <ChartRendererSupabase
                chartData={solarPotentialData.chartData}
                chartOptions={solarPotentialData.chartOptions}
                height="380px"
                title={`Solar Technical Potential - ${currentLocation}`}
                location={currentLocation}
              />
            ) : (
              <div className="placeholder-chart">
                <p>Loading Solar Potential data...</p>
              </div>
            )
          ) : pvView === 'batteryCost' ? (
            batteryCostData ? (
              <ChartRendererSupabase
                chartData={batteryCostData.chartData}
                chartOptions={batteryCostData.chartOptions}
                height="380px"
                title={`Solar PV Cost (Residential) - ${currentLocation}`}
                location={currentLocation}
              />
            ) : (
              <div className="placeholder-chart">
                <p>Loading Solar PV Battery Cost data...</p>
              </div>
            )
          ) : pvView === 'installers' ? (
            installersData ? (
              <ChartRendererSupabase
                chartData={installersData.chartData}
                chartOptions={{
                  ...installersData.chartOptions,
                  type: 'bar'
                }}
                height="550px"
                title={`Top Solar PV Installer-Manufacturer Pairs (Capacity) - ${currentLocation}`}
                location={currentLocation}
              />
            ) : (
              <div className="placeholder-chart">
                <p>Loading Solar PV Installers data...</p>
              </div>
            )
          ) : pvView === 'installersCost' ? (
            installersCostData ? (
              <ChartRendererSupabase
                chartData={installersCostData.chartData}
                chartOptions={{
                  ...installersCostData.chartOptions,
                  type: 'bar'
                }}
                height="550px"
                title={`Top Solar PV Installer-Manufacturer Pairs (Cost) - ${currentLocation}`}
                location={currentLocation}
              />
            ) : (
              <div className="placeholder-chart">
                <p>Loading Solar PV Installers Cost data...</p>
              </div>
            )
          ) : capacityData ? (
            <div>
              {pvView === 'capacity' && (
            <ChartRendererSupabase
              chartData={capacityData.chartData}
                  chartOptions={{
                    ...capacityData.chartOptions,
                    type: 'bar',
                    scales: {
                      ...(capacityData.chartOptions?.scales || {}),
                      x: { stacked: true },
                      y: { stacked: true, title: { display: true, text: 'Capacity (MW)' } }
                    }
                  }}
                  height="380px"
                  title={`Solar PV Capacity (Stacked) - ${currentLocation}`}
                  location={currentLocation}
                />
              )}
            </div>
          ) : (
            <div className="placeholder-chart"><p>Loading Solar PV data…</p></div>
          )}
          </div>
        }
        overviewContent={
          pvView === 'cost' ? (
            <OverviewContent
              title="Cost"
              description="This chart shows the average cost per watt for solar photovoltaic (PV) installations in the selected location over time. The data is separated by sector, including residential and commercial installations. Tracking solar cost per watt helps show how the price of installing solar systems changes over time and how affordable solar energy is becoming."
            >
              <p>Solar PV cost per watt in {currentLocation} was approximately ${costData?.resCostLatestYear != null ? costData.resCostLatestYear.toFixed(2) : '—'} per watt for residential systems and ${costData?.comCostLatestYear != null ? costData.comCostLatestYear.toFixed(2) : '—'} per watt for commercial systems in {costData?.latestYear ?? 'the latest year'}. {costData?.percentChange != null && costData?.baselineYear != null ? `This represents a ${costData.percentChange < 0 ? 'decrease' : costData.percentChange > 0 ? 'increase' : 'minimal change'} of ${Math.abs(costData.percentChange).toFixed(1)}% since ${costData.baselineYear}.` : ''} Over the full period, solar installation costs show a {costData?.costTrend === 'downward' ? 'generally downward' : costData?.costTrend === 'mixed' ? 'mixed' : 'relatively stable'} trend.</p>
              {costData?.costTrend === 'downward' ? (
                <p>This decrease may reflect improvements in solar technology, increased market competition, supply chain improvements, or supportive government incentives. Lower costs can make solar installations more accessible for households and businesses.</p>
              ) : costData?.costTrend === 'mixed' || costData?.costTrend === 'stable' ? (
                <p>This trend may reflect changes in material costs, labor costs, supply chain disruptions, policy changes, or differences in project size and installation complexity.</p>
              ) : null}
            </OverviewContent>
          ) : pvView === 'cumulative' ? (
            <OverviewContent
              title="Capacity Cumulative"
              description="This chart shows the cumulative installed solar capacity in the selected location over time, measured in kilowatts (kW). The data is separated by sector, including residential, commercial, industrial, educational, nonprofit, and government installations. Tracking solar capacity helps show how much renewable electricity generation has been added locally and which sectors are contributing the most to solar adoption."
            >
              <p>Total installed solar capacity in {currentLocation} is shown in the chart above by sector and year. {(capacityData?.percentChangeSince2015 ?? 0) > 0
                ? 'Solar capacity has increased significantly since 2015, showing that solar installations have expanded over time.'
                : 'Solar capacity has grown slowly or remained relatively flat since 2015.'}</p>
              {(capacityData?.percentChangeSince2015 ?? 0) > 0 ? (
                <p>This increase may reflect falling solar costs, government incentives, supportive policies, or increased interest from residents and businesses in renewable energy. Growth may also be influenced by programs such as net energy metering, state climate policies, and local sustainability initiatives.</p>
              ) : (
                <p>This may reflect market saturation, policy changes, limited rooftop space, or barriers such as installation costs and permitting challenges.</p>
              )}
            </OverviewContent>
          ) : pvView === 'perCapita' ? (
            <OverviewContent
              title="Capacity Per Capita"
              description="This chart compares solar capacity per capita in the selected location with the state average over time. Solar capacity per capita measures the amount of installed solar power relative to the population, which helps show how widely solar energy has been adopted in a community."
            >
              <p>Solar capacity per capita in {currentLocation} is shown in the chart above compared to the state average. Over the full period, solar capacity per capita has {solarPerCapitaTrend === 'increasing' ? 'increased' : solarPerCapitaTrend === 'decreasing' ? 'decreased' : solarPerCapitaTrend === 'stable' ? 'remained relatively stable' : 'a trend shown in the chart above'}.</p>
              {solarPerCapitaTrend === 'increasing' ? (
                <p>This increase may reflect growing adoption of solar installations by households, businesses, schools, and other organizations, as well as supportive policies, financial incentives, falling solar costs, or increased interest in renewable energy.</p>
              ) : solarPerCapitaTrend === 'decreasing' || solarPerCapitaTrend === 'stable' ? (
                <p>This may reflect slower installation rates, policy changes, market saturation, limited rooftop space, or economic conditions that reduce new solar investments.</p>
              ) : null}
            </OverviewContent>
          ) : pvView === 'potential' ? (
            <OverviewContent
              title="Solar Potential"
              description="This chart shows estimated solar technical and economic potential for the selected location. Technical potential represents the maximum feasible solar deployment under physical/site constraints, while economic potential reflects the subset likely to be cost-effective under assumed market conditions."
            >
              <p>
                {solarPotentialData?.latestYear != null
                  ? `In ${currentLocation}, estimated technical potential is ${solarPotentialData.latestTechnicalPotentialMw != null ? `${solarPotentialData.latestTechnicalPotentialMw.toFixed(1)} MW` : '—'} and economic potential is ${solarPotentialData.latestEconomicPotentialMw != null ? `${solarPotentialData.latestEconomicPotentialMw.toFixed(1)} MW` : '—'} in ${solarPotentialData.latestYear}. ${solarPotentialData.latestGenerationMwh != null ? `Potential annual generation is approximately ${Math.round(solarPotentialData.latestGenerationMwh).toLocaleString()} MWh.` : ''}`
                  : `Loading solar potential estimates for ${currentLocation}...`}
              </p>
            </OverviewContent>
          ) : pvView === 'batteryCost' ? (
            <OverviewContent
              title="Residential Battery Cost"
              description="This chart compares the average cost per kilowatt for residential solar photovoltaic (PV) systems and residential solar systems paired with battery storage in the selected location over time. Solar PV systems generate electricity from sunlight, while PV + battery systems allow households to store excess electricity for later use. Tracking these costs helps show how the price of solar and energy storage technologies changes over time."
            >
              <p>The cost of residential solar PV systems and PV systems paired with battery storage in {currentLocation} was approximately ${batteryCostData?.avgPVCost != null ? Math.round(batteryCostData.avgPVCost).toLocaleString() : '—'} per kW and ${batteryCostData?.avgBatteryCost != null ? Math.round(batteryCostData.avgBatteryCost).toLocaleString() : '—'} per kW in the data shown. PV + battery systems are typically more expensive than solar alone due to the additional equipment and installation requirements.</p>
              <p>Improvements in solar technology, increased competition among installers, and declining equipment prices can make solar systems more accessible to households. Improvements in battery technology and greater adoption of energy storage may reduce PV + battery system costs over time.</p>
            </OverviewContent>
          ) : pvView === 'installers' ? (
            <OverviewContent
              title="Installers (Capacity)"
              description="This chart shows the top solar PV installer–manufacturer pairs in the selected location based on total installed capacity. Each bar represents the total kilowatts (kW) of solar capacity installed by a specific installer using panels from a particular manufacturer. Looking at these pairs helps show which companies are most active in the local solar market."
            >
              <p>The chart highlights which installer–manufacturer combinations have contributed the most solar capacity in the community. {(installersData?.topPairShare ?? 0) >= 40 ? 'A few installer–manufacturer pairs account for a large share of installed capacity; this may suggest that certain companies or technologies are more widely adopted in the area.' : 'Capacity is distributed across many installer–manufacturer pairs, which may indicate a more diverse solar market with multiple companies participating in installations.'}</p>
            </OverviewContent>
          ) : pvView === 'installersCost' ? (
            <OverviewContent
              title="Installers (Cost)"
              description="This chart shows the average solar installation cost per watt for the top installer–manufacturer pairs in the selected location. Each bar represents the cost associated with a specific installer working with a particular solar panel manufacturer. Looking at these pairs helps show how installation costs vary across different companies and equipment combinations."
            >
              <p>The chart highlights differences in installation costs among various installer–manufacturer pairs. Some combinations may have higher costs per watt, while others may offer more affordable installations. Lower-cost pairs may reflect more efficient installation processes, competitive pricing, or lower equipment costs. Higher costs for some pairs may reflect differences in technology, installation complexity, project size, or service offerings.</p>
            </OverviewContent>
          ) : (
            <OverviewContent
              title="Solar Installations Overview"
              description={pvView === 'capacity' ? 'This chart shows installed solar photovoltaic (PV) capacity in the selected location over time.' : 'Number of solar PV projects by sector over time.'}
            >
              <p>{pvView === 'capacity' ? (capacityData?.narrative || 'Select a location to view solar installation insights and trends.') : (projectCountData?.narrative || 'Select a location to view Solar PV project insights and trends.')}</p>
            </OverviewContent>
          )
        }
        relevanceContent={
          pvView === 'capacity' ? (
            <RelevanceContent
              title="Relevance"
              description="Solar PV installations are an important indicator of renewable energy attempts and progress toward climate goals. Installed capacity reflects the community’s potential to generate clean electricity locally."
            >
              <div className="methodology-details-simple">
                <h5>Climate Impact</h5>
                <p>Solar systems produce electricity without direct greenhouse gas emissions. As more solar is installed, communities can reduce their reliance on fossil-fuel-based generation and lower emissions associated with electricity use.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Energy Planning</h5>
                <p>Tracking solar capacity helps local governments evaluate renewable energy growth, measure the impact of incentive programs, and identify sectors that may need additional outreach or support.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Energy Resilience</h5>
                <p>Distributed solar systems, especially when paired with battery storage, can improve energy resilience during power outages or extreme weather events.</p>
              </div>
            </RelevanceContent>
          ) : pvView === 'cost' ? (
            <RelevanceContent
              title="Relevance"
              description="Solar installation cost is an important indicator for renewable energy adoption because it directly affects how affordable solar systems are for households and businesses."
            >
              <div className="methodology-details-simple">
                <h5>Clean Energy Adoption</h5>
                <p>Lower solar costs can encourage more residents, businesses, and institutions to install solar systems, which helps increase renewable energy generation.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Climate Planning</h5>
                <p>Local governments and planners can use solar cost trends to understand whether economic barriers to solar adoption are decreasing and whether additional incentives or programs may be needed.</p>
              </div>
              <p style={{ marginTop: '0.75rem', fontSize: '0.9rem', color: '#64748b' }}><strong>Note:</strong> Solar cost per watt reflects installation prices but does not measure total solar generation or electricity savings over time.</p>
            </RelevanceContent>
          ) : pvView === 'batteryCost' ? (
            <RelevanceContent
              title="Relevance"
              description="The cost of residential solar and battery systems is an important factor influencing how quickly households adopt clean energy technologies."
            >
              <div className="methodology-details-simple">
                <h5>Clean Energy Adoption</h5>
                <p>Lower costs for solar and battery systems can encourage more homeowners to install renewable energy systems. Increased adoption can help expand local renewable electricity generation.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Energy Resilience</h5>
                <p>Solar systems paired with battery storage can allow households to store electricity and use it during outages or periods of high electricity demand.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Policy Planning</h5>
                <p>Local governments may use solar and battery cost trends to evaluate the effectiveness of incentive programs and identify opportunities to support clean energy adoption.</p>
              </div>
            </RelevanceContent>
          ) : pvView === 'cumulative' ? (
            <RelevanceContent
              title="Relevance to Climate Action"
              description="Cumulative solar capacity is an important indicator of progress toward renewable energy and climate goals. As more solar systems are installed, communities can generate more electricity from clean energy instead of fossil fuels."
            >
              <div className="methodology-details-simple">
                <h5>Grid Decarbonization</h5>
                <p>Increasing solar capacity helps reduce reliance on fossil fuel-based electricity generation. Over time, higher levels of solar deployment can help lower greenhouse gas emissions from the electricity sector.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Progress Tracking</h5>
                <p>This metric allows communities to track how quickly solar energy adoption is growing. It can also help local governments evaluate whether current policies and programs are supporting the transition to renewable energy.</p>
              </div>
            </RelevanceContent>
          ) : pvView === 'perCapita' ? (
            <RelevanceContent
              title="Relevance"
              description="Solar capacity per capita is an important indicator for understanding how widely renewable energy is being adopted in a community. Because it accounts for population size, it allows comparisons across different cities, counties, or states."
            >
              <div className="methodology-details-simple">
                <h5>Climate Impact</h5>
                <p>Higher solar capacity per capita generally indicates greater use of renewable energy and less reliance on fossil fuel-based electricity. As solar adoption increases, it can help reduce greenhouse gas emissions and support climate action goals.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Policy Planning</h5>
                <p>Local governments can use this metric to evaluate progress toward renewable energy targets and understand whether solar adoption is keeping pace with broader regional trends. It can also help inform decisions about incentives, permitting processes, and clean energy programs.</p>
              </div>
              <p style={{ marginTop: '0.75rem', fontSize: '0.9rem', color: '#64748b' }}><strong>Note:</strong> Solar capacity per capita shows the level of solar adoption relative to population but does not measure total electricity generation or overall emissions reductions.</p>
            </RelevanceContent>
          ) : pvView === 'potential' ? (
            <RelevanceContent
              title="Relevance"
              description="Solar potential estimates show how much additional clean electricity could be deployed locally under physical and economic constraints, which is essential for long-range climate and energy planning."
            >
              <div className="methodology-details-simple">
                <h5>Capacity Planning</h5>
                <p>Technical potential helps define the upper bound of feasible deployment and supports target setting for renewable electricity expansion in building and community energy plans.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Implementation Strategy</h5>
                <p>Comparing technical and economic potential helps prioritize program design, permitting reform, and incentives where the deployment gap is largest.</p>
              </div>
            </RelevanceContent>
          ) : pvView === 'installers' ? (
            <RelevanceContent
              title="Relevance"
              description="Understanding which installers and manufacturers are most active in a community can provide insight into the structure of the local solar market."
            >
              <div className="methodology-details-simple">
                <h5>Market Activity</h5>
                <p>Tracking installer–manufacturer pairs helps show which companies are driving solar deployment in the area and how competitive the solar installation market is.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Technology Adoption</h5>
                <p>Different manufacturers may offer different solar technologies, efficiencies, or pricing structures. Observing which technologies are commonly installed can help show trends in equipment preferences.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Energy Planning</h5>
                <p>Local governments and planners may use this information to better understand the solar ecosystem and identify potential partners for renewable energy programs.</p>
              </div>
            </RelevanceContent>
          ) : pvView === 'installersCost' ? (
            <RelevanceContent
              title="Relevance"
              description="Understanding solar installation costs across different installer–manufacturer pairs helps provide insight into the local solar market and the affordability of solar energy."
            >
              <div className="methodology-details-simple">
                <h5>Market Competition</h5>
                <p>Cost differences between installers may indicate varying levels of competition, pricing strategies, or business models within the solar installation market.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Technology and Equipment</h5>
                <p>Different solar panel manufacturers may offer products with different efficiency levels, warranties, or performance characteristics, which can influence installation costs.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Energy Planning</h5>
                <p>Tracking installation costs can help communities understand economic barriers to solar adoption and identify opportunities to support more affordable clean energy deployment.</p>
              </div>
            </RelevanceContent>
          ) : (
            <RelevanceContent title="Relevance to Climate Action" description="Capacity reflects clean energy output potential; project counts reflect market adoption." />
          )
        }
        equityContent={
          pvView === 'capacity' ? (
            <EquityContent
              title="Equity"
              description="Access to rooftop solar and clean energy benefits may not be evenly distributed across income levels, housing types, and neighborhoods."
            >
              <div className="methodology-details-simple">
                <h5>Upfront Costs</h5>
                <p>Although costs have declined over time, installing solar still requires upfront investment. Without rebates or financing options, participation may be limited to higher-income households.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Housing Type &amp; Tenure</h5>
                <p>Renters and residents of multifamily buildings may have fewer opportunities to install rooftop systems, which can limit access to renewable energy benefits.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Program Access</h5>
                <p>Targeted incentives, community solar programs, and low-income financing options can help expand access to clean energy.</p>
              </div>
            </EquityContent>
          ) : pvView === 'cost' ? (
            <EquityContent
              title="Equity"
              description="Even as solar costs decline, not all households and businesses may have equal access to solar installations."
            >
              <div className="methodology-details-simple">
                <h5>Financial Barriers</h5>
                <p>Solar systems often require upfront investment, which may limit adoption for lower-income households or small businesses without financial incentives or financing options.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Access to Solar Programs</h5>
                <p>Programs such as rebates, tax credits, and community solar initiatives can help reduce barriers and expand access to solar energy.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Energy Equity</h5>
                <p>Ensuring that solar adoption benefits a wide range of communities can help distribute the economic and environmental benefits of renewable energy more fairly.</p>
              </div>
            </EquityContent>
          ) : pvView === 'batteryCost' ? (
            <EquityContent
              title="Equity"
              description="Even as solar and battery technologies become more common, not all households may have equal access to these systems."
            >
              <div className="methodology-details-simple">
                <h5>Financial Barriers</h5>
                <p>Solar and battery installations often require upfront investment, which may limit adoption for lower-income households without financial incentives or financing options.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Access to Clean Energy</h5>
                <p>Homeowners are generally more able to install solar systems than renters, which can create unequal access to renewable energy benefits.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Energy Equity</h5>
                <p>Programs such as rebates, tax credits, and community energy programs can help expand access so that more households can benefit from solar and energy storage technologies.</p>
              </div>
            </EquityContent>
          ) : pvView === 'cumulative' ? (
            <EquityContent
              title="Equity Considerations"
              description="While solar capacity is increasing overall, access to solar energy is not always evenly distributed across communities. Some households and neighborhoods may have more opportunities to adopt solar than others."
            >
              <div className="methodology-details-simple">
                <h5>Community Access</h5>
                <p>Ideally, the benefits of solar energy should reach all parts of the community. Programs that support solar adoption in schools, public buildings, and community organizations can help expand access beyond higher-income households.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Energy Justice</h5>
                <p>Expanding solar energy can help reduce long-term energy costs and support cleaner air. Ensuring that low-income communities can participate in solar programs is important so that the benefits of clean energy are shared more fairly.</p>
              </div>
            </EquityContent>
          ) : pvView === 'perCapita' ? (
            <EquityContent
              title="Equity"
              description="Access to solar energy is not always evenly distributed across households and communities."
            >
              <div className="methodology-details-simple">
                <h5>Access to Solar</h5>
                <p>Homeowners are often more able to install rooftop solar systems than renters, which can create differences in who benefits from solar energy.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Financial Barriers</h5>
                <p>Although solar costs have declined, upfront installation costs may still limit adoption for some households or small businesses without financial incentives or financing options.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Energy Justice</h5>
                <p>Programs such as community solar, rebates, or targeted incentives can help expand access to solar energy so that more residents, including lower-income households, can benefit from clean energy.</p>
              </div>
              <p style={{ marginTop: '0.75rem', fontSize: '0.9rem', color: '#64748b' }}><strong>Note:</strong> Because this metric is reported at the community level, additional data such as income levels, housing type, and participation in solar programs may be needed to fully understand equity impacts.</p>
            </EquityContent>
          ) : pvView === 'potential' ? (
            <EquityContent
              title="Equity"
              description="Potential alone does not guarantee equitable outcomes; policy design determines whether the benefits of new solar deployment reach renters, low-income households, and underinvested communities."
            >
              <div className="methodology-details-simple">
                <h5>Adoption Barriers</h5>
                <p>Upfront costs, credit constraints, and split incentives can prevent communities with high need from converting potential into actual installations.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Benefit Distribution</h5>
                <p>Equity-focused incentives, community solar structures, and tenant protections help ensure bill savings and resilience benefits are shared more broadly.</p>
              </div>
            </EquityContent>
          ) : pvView === 'installers' ? (
            <EquityContent
              title="Equity"
              description="The distribution of solar installers and technologies may influence how accessible solar energy is for different communities."
            >
              <div className="methodology-details-simple">
                <h5>Access to Solar Services</h5>
                <p>If solar installations are dominated by a small number of companies, access to services may depend on the pricing structures and outreach of those installers.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Affordability and Choice</h5>
                <p>A diverse installer market may provide residents with more choices in pricing, technology, and installation options.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Energy Equity</h5>
                <p>Programs that encourage installers to serve a wider range of neighborhoods and income groups can help ensure that the benefits of solar energy are more evenly distributed.</p>
              </div>
            </EquityContent>
          ) : pvView === 'installersCost' ? (
            <EquityContent
              title="Equity"
              description="Differences in solar installation costs may influence who can access solar energy within a community."
            >
              <div className="methodology-details-simple">
                <h5>Affordability</h5>
                <p>Higher installation costs may limit access for lower-income households or small businesses without financial incentives or financing programs.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Access to Installers</h5>
                <p>If affordable installers are limited or concentrated in certain areas, some communities may face barriers to accessing solar services.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Energy Equity</h5>
                <p>Programs such as rebates, tax credits, and community solar initiatives can help reduce cost barriers and expand access to solar energy across different neighborhoods.</p>
              </div>
            </EquityContent>
          ) : (
            <EquityContent title="Equity Considerations" description="Ensure access to rooftop solar benefits across income levels and housing types." />
          )
        }
        methodologyContent={
          pvView === 'capacity' ? (
            <MethodologyContent
              title="Methodology"
              description="The chart aggregates solar installation records by sector and year for the selected location, based on interconnection data from California utilities."
              dataSources={[
                'California Distributed Generation Statistics (https://www.californiadgstats.ca.gov/downloads/)',
                'Interconnected Project Sites Data Set from PG&E, SCE, and SDG&E under Rule 21 tariffs (per CPUC Decision D.14-11-001)',
                'Includes all NEM systems regardless of technology type or program',
                'Data covers 1982 to present'
              ]}
              methodology={[
                'Capacity from project records in the Interconnected Project Sites Data Set',
                'Each row represents one interconnection address/project site',
                'Data grouped by sector and year'
              ]}
            />
          ) : pvView === 'cost' ? (
            <MethodologyContent
              title="Methodology"
              description="Solar PV cost data comes from California Distributed Generation Statistics, providing insights into cost trends and sector differences."
              dataSources={[
                'California Distributed Generation Statistics (https://www.californiadgstats.ca.gov/downloads/)',
                'Interconnected Applications Data Set from PG&E, SCE, and SDG&E under Rule 21 tariffs (per CPUC Decision D.14-11-001)',
                'Includes all NEM systems regardless of technology type or program',
                'California Solar Initiative incentive program data and other publicly available incentive program data sets'
              ]}
              methodology={[
                'Cost per watt calculated as total cost divided by total capacity',
                'Averages computed by sector and year',
                'Data aggregated from individual installation records'
              ]}
            />
          ) : pvView === 'cumulative' ? (
            <MethodologyContent
              title="Methodology"
              description="Cumulative solar capacity calculated by summing annual installations over time, broken down by sector."
              dataSources={[
                'California Distributed Generation Statistics (https://www.californiadgstats.ca.gov/downloads/)',
                'Interconnected Project Sites Data Set from PG&E, SCE, and SDG&E under Rule 21 tariffs (per CPUC Decision D.14-11-001)',
                'Data covers 1982 to present'
              ]}
              methodology={[
                'Cumulative capacity calculated by summing annual installations over time',
                'Data grouped by sector and year',
                'Capacity values accumulated from earliest year to present'
              ]}
            />
          ) : pvView === 'perCapita' ? (
            <MethodologyContent
              title="Methodology"
              description="Per capita solar capacity calculated using total capacity divided by population, comparing location to California average."
              dataSources={[
                'California Distributed Generation Statistics (https://www.californiadgstats.ca.gov/downloads/)',
                'Interconnected Project Sites Data Set from PG&E, SCE, and SDG&E',
                'Population data from population table (U.S. Census Bureau)',
                'Location data matched by geo_fips codes'
              ]}
              methodology={[
                'Per capita metrics calculated using total capacity divided by population',
                'Cumulative capacity calculated by summing annual installations over time',
                'California comparison uses state-level aggregated data'
              ]}
            />
          ) : pvView === 'potential' ? (
            <MethodologyContent
              title="Methodology"
              description="Solar potential metrics are retrieved from the solar_technical_potential table for the selected geofips and visualized by year."
              dataSources={[
                'solar_technical_potential',
                'Supabase geofips-based lookup',
              ]}
              methodology={[
                'Rows are fetched by geofips (with location-name fallback), then ordered chronologically.',
                'Technical potential (MW), economic potential (MW), and potential generation (MWh) are normalized from available columns when present.',
                'Available metrics are charted as time series; generation uses a secondary axis when MW series are also present.',
              ]}
            />
          ) : pvView === 'batteryCost' ? (
            <MethodologyContent
              title="Methodology"
              description="Residential solar PV cost comparison between standalone photovoltaic systems and photovoltaic systems with battery energy storage."
              dataSources={[
                'California Distributed Generation Statistics (https://www.californiadgstats.ca.gov/downloads/)',
                'Interconnected Applications Data Set from PG&E, SCE, and SDG&E under Rule 21 tariffs (per CPUC Decision D.14-11-001)',
                'Technology type classifications: "Photovoltaic" and "Photovoltaic, Battery Energy Storage"',
                'Residential sector data only'
              ]}
              methodology={[
                'Cost per kW calculated as total cost divided by system size (kW)',
                'Data filtered for Residential sector and system size > 0',
                'Average cost per kW calculated by geoid, location_name, county, year, and technology type',
                'Battery premium calculated as difference between PV+Battery cost and standalone PV cost'
              ]}
            />
          ) : pvView === 'installers' || pvView === 'installersCost' ? (
            <MethodologyContent
              title="Methodology"
              description={pvView === 'installers' 
                ? "Top 10 installer-manufacturer pairs by total installed capacity (kW) for this location."
                : "Top 10 installer-manufacturer pairs by cost per watt for this location."}
              dataSources={[
                'California Distributed Generation Statistics (https://www.californiadgstats.ca.gov/downloads/)',
                'Interconnected Applications Data Set from PG&E, SCE, and SDG&E under Rule 21 tariffs (per CPUC Decision D.14-11-001)',
                'Installer and manufacturer information from project records'
              ]}
              methodology={[
                pvView === 'installers' 
                  ? 'Top 10 pairs selected by total kW installed (system size)'
                  : 'Top 10 pairs selected by total kW installed, then cost per watt calculated as total cost divided by total kW',
                'Installer and manufacturer names standardized using fuzzy matching',
                'Data grouped by geoid, location_name, county, installer, and manufacturer',
                'For each geographic level, top 10 entries selected based on total_kw'
              ]}
            />
          ) : (
            <MethodologyContent 
              title="Methodology" 
              description="Solar installation records by sector; stacked columns sum sectors per year." 
              dataSources={[
                'California Distributed Generation Statistics (https://www.californiadgstats.ca.gov/downloads/)',
                'Interconnected Project Sites Data Set from PG&E, SCE, and SDG&E under Rule 21 tariffs (per CPUC Decision D.14-11-001)',
                'Includes all NEM systems regardless of technology type or program',
                'Data covers 1982 to present'
              ]} 
              methodology={[
                'Capacity from project records in the Interconnected Project Sites Data Set',
                'Each row represents one interconnection address/project site',
                'Data grouped by sector and year'
              ]} 
            />
          )
        }
        activeView={activeView}
        onViewChange={toggleView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        appendLocationToTitle={true}
        overviewSectionId="buildings"
        overviewChartId="cumulative_solar_capacity"
        onDownloadPNG={() => {
          const viewLabel = pvView === 'capacity' ? 'Capacity' : pvView === 'cost' ? 'Cost' : pvView === 'cumulative' ? 'Cumulative' : pvView === 'perCapita' ? 'Per_Capita' : pvView === 'potential' ? 'Potential' : pvView === 'batteryCost' ? 'Battery_Cost' : pvView === 'installers' ? 'Installers' : 'Installers_Cost';
          makeDownloadPNG(`#solar-pv-${pvView}`, `Solar_PV_${viewLabel}`, currentLocation);
        }}
        onDownloadCSV={() => {
          const chartData = pvView === 'capacity' ? capacityData?.chartData : pvView === 'cost' ? costData?.chartData : pvView === 'potential' ? solarPotentialData?.chartData : pvView === 'batteryCost' ? batteryCostData?.chartData : pvView === 'installers' ? installersData?.chartData : pvView === 'installersCost' ? installersCostData?.chartData : null;
          if (!chartData?.labels?.length) return;
          const { labels, datasets } = chartData;
          const rows = labels.map((label: number, i: number) => {
            const row: Record<string, number | string> = { year: label };
            (datasets || []).forEach((ds: { label: string; data: number[] }) => {
              row[ds.label || 'value'] = ds.data?.[i] ?? '';
            });
            return row;
          });
          const viewLabel = pvView === 'capacity' ? 'Capacity' : pvView === 'cost' ? 'Cost' : pvView === 'perCapita' ? 'Per_Capita' : pvView === 'potential' ? 'Potential' : pvView === 'batteryCost' ? 'Battery_Cost' : pvView === 'installers' ? 'Installers' : 'Installers_Cost';
          downloadDataAsCSV(rows, formatFilename(currentLocation, `Solar_PV_${viewLabel}_Data`));
        }}
        pngFilename={formatFilename(currentLocation, `Solar_PV_${pvView === 'capacity' ? 'Capacity' : pvView === 'cost' ? 'Cost' : pvView === 'cumulative' ? 'Cumulative' : pvView === 'perCapita' ? 'Per_Capita' : pvView === 'potential' ? 'Potential' : pvView === 'batteryCost' ? 'Battery_Cost' : pvView === 'installers' ? 'Installers' : 'Installers_Cost'}`)}
        csvFilename={formatFilename(currentLocation, `Solar_PV_${pvView === 'capacity' ? 'Capacity' : pvView === 'cost' ? 'Cost' : pvView === 'perCapita' ? 'Per_Capita' : pvView === 'potential' ? 'Potential' : pvView === 'batteryCost' ? 'Battery_Cost' : pvView === 'installers' ? 'Installers' : 'Installers_Cost'}_Data`)}
      />
      </SectionWrapper>

      {/* Heat Pump Adoption Section */}
      <SectionWrapper sectionId="heat_pump" bgMap={BUILDINGS_SECTION_BG} refs={sectionObserverRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
      <ChartLayout
        title="Heat Pump Adoption"
        description="This chart tracks heat pump adoption trends, costs, and technology mix to show progress toward cleaner electric space and water heating."
        chartType="line"
        chartSelector={{
          options: allowedHeatPumpOptions.map((option) => ({ value: option.value, label: option.label })),
          value: heatPumpView,
          onChange: (value) => {
            setHeatPumpView(value as 'cost' | 'technology' | 'units' | 'perCapita');
          }
        }}
        keyMetrics={
          <KeyMetrics>
            <KeyMetric 
              value={heatPumpMetrics ? heatPumpMetrics.totalUnits.toLocaleString() : "Loading..."} 
              label="Total Heat Pump Units"
              color="#3b82f6"
            />
            <KeyMetric 
              value={heatPumpMetrics ? `${heatPumpMetrics.totalGHGSavings.toFixed(1)} MT CO₂e` : "Loading..."} 
              label="Annual GHG Savings"
              color="#10b981"
            />
            <KeyMetric 
              value={heatPumpMetrics ? `$${heatPumpMetrics.averageCostPerUnit.toLocaleString()}` : "Loading..."} 
              label="Average Cost per Unit"
              color="#f59e0b"
            />
            <KeyMetric 
              value={heatPumpMetrics ? `${heatPumpMetrics.growthRate > 0 ? '+' : ''}${heatPumpMetrics.growthRate.toFixed(1)}%` : "Loading..."} 
              label="Growth Rate"
              color="#8b5cf6"
            />
          </KeyMetrics>
        }
        chartContent={
          <div id={`heat-pump-${heatPumpView}`}>
            {selectedGeofips ? (
              heatPumpView === 'units' ? (
                <HeatPumpUnitsChart location={currentLocation} geofips={selectedGeofips} onDataLoaded={(rows: Record<string, unknown>[]) => setHeatPumpChartData(rows as unknown as Record<string, string | number>[])} />
              ) : heatPumpView === 'perCapita' ? (
                <HeatPumpPerCapitaChart location={currentLocation} geofips={selectedGeofips} onDataLoaded={(rows: Record<string, unknown>[]) => setHeatPumpChartData(rows as unknown as Record<string, string | number>[])} />
              ) : heatPumpView === 'cost' ? (
                <HeatPumpCostChart location={currentLocation} geofips={selectedGeofips} onDataLoaded={(rows: Record<string, unknown>[]) => setHeatPumpChartData(rows as unknown as Record<string, string | number>[])} />
              ) : (
                <HeatPumpTechnologyChart location={currentLocation} geofips={selectedGeofips} onDataLoaded={(rows: Record<string, unknown>[]) => setHeatPumpChartData(rows as unknown as Record<string, string | number>[])} />
              )
            ) : (
              <div>Loading location data...</div>
            )}
          </div>
        }
        overviewContent={
          heatPumpView === 'perCapita' ? (
            <OverviewContent
              title="Per Capita"
              description="This chart shows the number of heat pumps installed per 10,000 residents in the selected location compared with the state average. Heat pumps are energy-efficient systems that can provide both heating and cooling using electricity. Tracking heat pump adoption per capita helps show how quickly communities are transitioning away from fossil fuel-based heating systems toward cleaner technologies."
            >
              <p>Heat pump adoption per capita in {currentLocation} is shown in the chart above compared to the state average. {heatPumpMetrics?.growthRate != null && heatPumpMetrics.growthRate > 5 ? 'Adoption has been increasing; this may reflect growing interest in electrification, improved technology performance, or supportive policies and incentives.' : 'Adoption levels may remain low or grow slowly; this may reflect higher installation costs, limited awareness, building constraints, or continued reliance on traditional heating systems.'}</p>
            </OverviewContent>
          ) : heatPumpView === 'cost' ? (
            <OverviewContent
              title="Installation Costs"
              description="This chart shows the average installation cost for HVAC heat pump systems and heat pump water heaters (HPWH) among TECH participating contractors. It compares installation costs in the selected location with the California average over time. Tracking installation costs helps show how prices change as clean heating technologies become more widely adopted."
            >
              <p>Installation costs for HVAC heat pump systems are generally higher than HPWH systems because HVAC installations are usually larger and more complex. In {currentLocation}, installation costs may vary from year to year due to changes in equipment prices, labor availability, contractor capacity, and incentive programs.</p>
              <p>Comparing {currentLocation} with the California average helps show whether local installation costs follow broader statewide trends or differ due to regional market conditions. Changes over time may also reflect improvements in technology, market growth, or shifts in demand for electrification.</p>
            </OverviewContent>
          ) : heatPumpView === 'technology' ? (
            <OverviewContent
              title="Technology Mix"
              description="This chart shows the average project cost per unit for different heat pump technologies in the selected location over time. The technologies shown include HVAC heat pump systems and heat pump water heaters (HPWH). Tracking installation costs by technology helps illustrate how prices change as these electrification technologies become more widely adopted."
            >
              <p>HVAC heat pump systems generally have higher installation costs per unit than HPWH systems because they involve larger equipment and more complex installation processes. Costs for both technologies may change over time depending on factors such as equipment prices, labor availability, contractor experience, and market demand.</p>
              <p>Comparing installation costs across technologies helps show which systems may be more affordable or easier to adopt for households. Changes in costs over time may also reflect improvements in technology, supply chains, or incentive programs that support electrification.</p>
            </OverviewContent>
          ) : (
            <OverviewContent
              title="Heat Pump Adoption Overview"
              description="This chart shows heat pump installations in the selected location over time. Heat pumps are electric systems used for heating, cooling, and water heating. They are generally more energy efficient than traditional gas systems and play an important role in building electrification."
            >
              <p>
                {heatPumpMetrics ? (() => {
                  const { totalUnits, growthRate, firstYear, lastYear } = heatPumpMetrics;
                  const trendText =
                    growthRate > 5 ? 'increased steadily' :
                    growthRate < -5 ? 'decreased overall' :
                    'remained relatively stable';
                  const trendInsight =
                    growthRate > 5
                      ? ' This increase may reflect stronger incentive programs, greater public awareness, or rising interest in electrification.'
                      : growthRate < -5
                        ? ' This decrease may suggest barriers such as high upfront costs, limited contractor availability, or reduced incentives.'
                        : ' This may suggest barriers such as high upfront costs or limited contractor availability.';
                  return (
                    <>
                      From {firstYear} to {lastYear}, {currentLocation} recorded {totalUnits.toLocaleString()} heat pump installations in the available dataset. Installations have {trendText} over time.{trendInsight}
                    </>
                  );
                })() : `Loading heat pump data for ${currentLocation}...`}
              </p>
              {heatPumpMetrics && (
                <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#64748b' }}>
                  <strong>Note:</strong> These totals reflect installations in the TECH Clean California database only, which tracks installations that received program incentives. This does not represent all heat pump installations in {currentLocation}, as many installations may occur without program incentives.
                </p>
              )}
            </OverviewContent>
          )
        }
        relevanceContent={
          heatPumpView === 'perCapita' ? (
            <RelevanceContent
              title="Relevance"
              description="Heat pump adoption is an important indicator of building electrification and the transition to cleaner heating and cooling systems."
            >
              <div className="methodology-details-simple">
                <h5>Climate Impact</h5>
                <p>Heat pumps can significantly reduce greenhouse gas emissions when they replace natural gas or other fossil fuel heating systems, especially when electricity comes from cleaner energy sources.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Energy Planning</h5>
                <p>Tracking adoption levels helps communities understand how quickly electrification technologies are spreading and whether additional policies, incentives, or outreach programs may be needed to support adoption.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Infrastructure Planning</h5>
                <p>Higher adoption of heat pumps may also influence electricity demand and help inform long-term energy and grid planning.</p>
              </div>
            </RelevanceContent>
          ) : heatPumpView === 'cost' ? (
            <RelevanceContent
              title="Relevance"
              description="Installation costs are an important factor influencing how quickly households adopt heat pump technologies."
            >
              <div className="methodology-details-simple">
                <h5>Climate Impact</h5>
                <p>Heat pumps can significantly reduce greenhouse gas emissions when they replace natural gas or other fossil fuel heating systems. Lower installation costs can make these technologies more accessible and accelerate the transition to cleaner building energy systems.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Energy Planning</h5>
                <p>Understanding installation cost trends helps policymakers and communities evaluate whether financial incentives or rebate programs are effective in supporting heat pump adoption.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Market Development</h5>
                <p>Tracking installation costs can also provide insight into how the clean energy market is evolving as more contractors gain experience installing these systems.</p>
              </div>
            </RelevanceContent>
          ) : heatPumpView === 'technology' ? (
            <RelevanceContent
              title="Relevance"
              description="Understanding the cost of different heat pump technologies is important for evaluating the progress of building electrification."
            >
              <div className="methodology-details-simple">
                <h5>Climate Impact</h5>
                <p>Heat pump technologies can significantly reduce greenhouse gas emissions when they replace fossil fuel heating systems such as natural gas furnaces or water heaters. Lower technology costs can help accelerate the transition to cleaner building energy systems.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Market Trends</h5>
                <p>Tracking costs across technologies helps identify how the clean energy market is evolving and which technologies are becoming more accessible to households.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Policy Planning</h5>
                <p>Cost trends can also help policymakers evaluate the effectiveness of rebate programs and incentives designed to encourage heat pump adoption.</p>
              </div>
            </RelevanceContent>
          ) : (
            <RelevanceContent
              title="Relevance"
              description="Heat pump adoption is an important sign of progress toward building electrification and reduced fossil fuel use."
            >
              <div className="methodology-details-simple">
                <h5>Climate Impact</h5>
                <p>Heat pumps can reduce greenhouse gas emissions compared to gas-fired heating systems, especially as the electricity grid becomes cleaner over time.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Energy Efficiency</h5>
                <p>Because heat pumps are more efficient than traditional systems, they can lower overall energy demand while maintaining indoor comfort.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Policy Planning</h5>
                <p>Tracking installation trends helps cities evaluate the effectiveness of incentive programs and measure progress toward decarbonization goals.</p>
              </div>
            </RelevanceContent>
          )
        }
        equityContent={
          heatPumpView === 'perCapita' ? (
            <EquityContent
              title="Equity"
              description="Access to heat pump technology may vary across households depending on income levels, housing type, and building conditions."
            >
              <div className="methodology-details-simple">
                <h5>Financial Barriers</h5>
                <p>Although heat pumps can reduce energy costs over time, the upfront installation cost may be a barrier for some households without financial assistance.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Housing Constraints</h5>
                <p>Renters and residents of older buildings may have fewer opportunities to install heat pumps compared to homeowners.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Energy Equity</h5>
                <p>Programs that provide rebates, incentives, or targeted support for low-income households can help expand access to heat pump technology and ensure the benefits of electrification are shared more broadly.</p>
              </div>
            </EquityContent>
          ) : heatPumpView === 'cost' ? (
            <EquityContent
              title="Equity"
              description="Access to heat pump technologies may vary across households depending on income levels, housing conditions, and access to financial support programs."
            >
              <div className="methodology-details-simple">
                <h5>Financial Barriers</h5>
                <p>Although heat pumps can reduce energy costs over time, the upfront installation cost can be a major barrier for many households, especially without incentives or rebates.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Housing Constraints</h5>
                <p>Renters and residents of older buildings may face additional challenges installing HVAC heat pumps or HPWH systems due to building design, landlord approval, or infrastructure limitations.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Energy Equity</h5>
                <p>Programs that provide rebates, financing options, or targeted support for low-income households can help expand access to clean heating technologies and ensure that the benefits of electrification are shared more broadly.</p>
              </div>
            </EquityContent>
          ) : heatPumpView === 'technology' ? (
            <EquityContent
              title="Equity"
              description="The cost of different heat pump technologies may affect who is able to adopt them."
            >
              <div className="methodology-details-simple">
                <h5>Financial Barriers</h5>
                <p>Higher installation costs may limit adoption for households with fewer financial resources, especially without incentives or financing programs.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Housing Constraints</h5>
                <p>Some technologies may be easier to install in certain types of buildings, meaning renters or residents of older homes may face additional barriers.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Energy Equity</h5>
                <p>Policies that provide rebates, financing options, or targeted support for low-income households can help ensure that the benefits of heat pump technology are accessible to a wider range of communities.</p>
              </div>
            </EquityContent>
          ) : (
            <EquityContent
              title="Equity"
              description="Heat pump adoption may vary depending on income level, housing type, and access to financial support."
            >
              <div className="methodology-details-simple">
                <h5>Cost Barriers</h5>
                <p>Upfront installation costs can limit adoption without rebates or financing programs. Households with limited financial resources may face greater challenges.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Rental Housing</h5>
                <p>Renters depend on landlords to upgrade heating systems, which may slow adoption in multifamily housing.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Access to Incentives</h5>
                <p>Participation in incentive programs may vary by awareness, eligibility requirements, or administrative barriers.</p>
              </div>
            </EquityContent>
          )
        }
        methodologyContent={
          <MethodologyContent
            title="Methodology"
            description="The chart compiles heat pump installation records for the selected location using available program data. Heat pump installation data from the TECH Clean California database, which tracks installations receiving program incentives."
            dataSources={[
              '<strong>TECH Clean California Database</strong> - <a href="https://techcleanca.com/heat-pump-data/" target="_blank" rel="noopener noreferrer">https://techcleanca.com/heat-pump-data/</a>',
              'The TECH Clean California program gathers and publishes data on heat pump water heating (HPWH) and heat pump heating, ventilation, and air-conditioning (HP HVAC) markets in California',
              'All data is anonymized and aggregated per California Public Utility Commission (CPUC) guidelines in Decision 11-07-056, Decision 14-05-016, Decision 20-03-027, and Decision 21-11-002',
              'Heat pump installation records by location and year from participating contractors',
              'Cost data by system type (HVAC, HPWH)',
              'Building type and geography classifications'
            ]}
            methodology={[
              'Data aggregated from installations that received incentives through the TECH Clean California program',
              'Only installations participating in the incentive program are included in this database',
              'This represents a subset of all heat pump installations, as many installations occur without program incentives',
              'Costs calculated per unit for each system type',
              'Location data matched by geo_fips codes',
              'Annual trends calculated from installation records in the TECH database'
            ]}
          />
        }
        activeView={activeView}
        onViewChange={toggleView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        appendLocationToTitle={true}
        onDownloadPNG={() => {
          const viewLabel = heatPumpView === 'cost' ? 'Installation_Costs' : heatPumpView === 'technology' ? 'Technology_Costs' : heatPumpView === 'perCapita' ? 'Per_Capita' : 'Units_Installed';
          makeDownloadPNG(`#heat-pump-${heatPumpView}`, `Heat_Pump_${viewLabel}`, currentLocation);
        }}
        onDownloadCSV={() => {
          if (!heatPumpChartData?.length) return;
          const viewLabel = heatPumpView === 'cost' ? 'Installation_Costs' : heatPumpView === 'technology' ? 'Technology_Costs' : heatPumpView === 'perCapita' ? 'Per_Capita' : 'Units_Installed';
          downloadDataAsCSV(heatPumpChartData, formatFilename(currentLocation, `Heat_Pump_${viewLabel}_Data`));
        }}
        pngFilename={formatFilename(currentLocation, `Heat_Pump_${heatPumpView === 'cost' ? 'Installation_Costs' : heatPumpView === 'technology' ? 'Technology_Costs' : heatPumpView === 'perCapita' ? 'Per_Capita' : 'Units_Installed'}`)}
        csvFilename={formatFilename(currentLocation, `Heat_Pump_${heatPumpView === 'cost' ? 'Installation_Costs' : heatPumpView === 'technology' ? 'Technology_Costs' : heatPumpView === 'perCapita' ? 'Per_Capita' : 'Units_Installed'}_Data`)}
      />
      </SectionWrapper>

      {/* Business establishments (sector & size; total row feeds policy commercial intensity) */}
      <SectionWrapper sectionId="business_establishments" bgMap={BUILDINGS_SECTION_BG} refs={sectionObserverRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
        <BusinessEstablishmentsSection
          location={currentLocation}
          geofips={selectedGeofips != null ? String(selectedGeofips) : null}
        />
      </SectionWrapper>

      {/* Housing to Employees Ratio */}
      <SectionWrapper sectionId="housing_to_employees" bgMap={BUILDINGS_SECTION_BG} refs={sectionObserverRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
      <HousingToEmployees
        location={currentLocation}
        geofips={selectedGeofips}
        preferredState="California"
      />
      </SectionWrapper>

      {/* Single Family Detached Housing Chart */}
      <SectionWrapper sectionId="single_family_detached" bgMap={BUILDINGS_SECTION_BG} refs={sectionObserverRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
        <SingleFamilyDetached 
          location={currentLocation}
          geofips={selectedGeofips}
        />
      </SectionWrapper>

      {/* Owner-Occupied Housing Map */}
      <SectionWrapper sectionId="owner_occupied_map" bgMap={BUILDINGS_SECTION_BG} refs={sectionObserverRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
      <ChartLayout
        title="Owner-Occupied Housing Map"
        description="This map shows the share of owner-occupied housing in the selected location relative to California, highlighting tenure patterns relevant to retrofit potential."
        chartType="map"
        keyMetrics={
          ownerOccupiedHousingMetrics ? (
            <KeyMetrics>
              <KeyMetric
                value={`${ownerOccupiedHousingMetrics.currentValue.toFixed(1)}%`}
                label="% Owner-Occupied Housing"
              />
              <KeyMetric
                value={`${ownerOccupiedHousingMetrics.californiaAverage.toFixed(1)}%`}
                label="% Owner-Occupied Housing in California"
              />
            </KeyMetrics>
          ) : undefined
        }
        chartContent={
          <div id="owner-occupied-housing-map">
            <GenericMapTiered 
              location={currentLocation}
              lockedVariable="owner_occupied_housing"
              hideVariableSelector={true}
              onDataLoaded={(metrics) => {
                setOwnerOccupiedHousingMetrics(metrics);
              }}
            />
          </div>
        }
        overviewContent={
          ownerOccupiedHousingMetrics ? (
            <OverviewContent
              title="Overview"
              description={`This map compares the percent of houses occupied by their owners across different regions of ${currentLocation}.`}
            >
              <p>
                {currentLocation} has {ownerOccupiedHousingMetrics.currentValue.toFixed(1)}% owner-occupied housing, which is {ownerOccupiedHousingMetrics.currentValue > ownerOccupiedHousingMetrics.californiaAverage ? 'higher' : 'lower'} than the California average of {ownerOccupiedHousingMetrics.californiaAverage.toFixed(1)}%. Owner-occupied housing represents households where residents own their homes, which can influence the ability to make energy efficiency improvements, install solar PV, and electrify building systems. Higher owner-occupancy rates may indicate greater potential for building decarbonization investments.
              </p>
            </OverviewContent>
          ) : undefined
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance"
            description="Geographic tenure patterns are a primary factor in urban planning, especially for climate and energy policy."
          >
            <div className="methodology-details-simple">
              <h5>Energy Improvements</h5>
              <p>Homeowners generally have more agency to invest in long-term building decarbonization. The high-owner-occupancy zones on the map represent the easiest targets for residential electrification programs.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Infrastructure Planning</h5>
              <p>Areas with higher renter concentrations often see higher turnover and different parking/transit needs compared to more stable, owner-occupied neighborhoods.</p>
            </div>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity"
            description="Tenure is a proxy for wealth accumulation and long-term housing security."
          >
            <div className="methodology-details-simple">
              <h5>Wealth Disparity</h5>
              <p>The map highlights the geographic divide in wealth; owner-occupied areas typically correlate with higher household incomes. Meanwhile, renters are more vulnerable to rising market rates and displacement.</p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Methodology"
            description="Owner-Occupied Housing analysis based on American Community Survey data with geographic visualization."
            dataSources={[
              'American Community Survey 5-Year Estimates (ACS5)',
              'U.S. Census Bureau demographic and housing data',
              'California census tract boundaries',
              'Housing tenure data (owner-occupied vs. renter-occupied)'
            ]}
            methodology={[
              'Owner-occupied housing percentage calculated as owner-occupied units divided by total occupied housing units',
              'Data represents housing tenure patterns by census tract from American Community Survey',
              'Geographic visualization shows owner-occupancy rates across census tracts',
              'Comparison calculated relative to California state average',
              '5-year estimates provide more reliable data for smaller geographic areas'
            ]}
          />
        }
        activeView={activeOwnerOccupiedView}
        onViewChange={setActiveOwnerOccupiedView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        appendLocationToTitle={true}
        onDownloadPNG={() => makeDownloadPNG('#owner-occupied-housing-map', 'Owner_Occupied_Housing_Map', currentLocation)}
        onDownloadCSV={() => {
          if (ownerOccupiedHousingMetrics) {
            const rows = [{ location: currentLocation, year: ownerOccupiedHousingMetrics.year, variable: ownerOccupiedHousingMetrics.variable, value: ownerOccupiedHousingMetrics.currentValue, california_average: ownerOccupiedHousingMetrics.californiaAverage }];
            downloadDataAsCSV(rows, formatFilename(currentLocation, 'Owner_Occupied_Housing_Data'));
          }
        }}
        pngFilename={formatFilename(currentLocation, 'Owner_Occupied_Housing_Map')}
        csvFilename={formatFilename(currentLocation, 'Owner_Occupied_Housing_Data')}
      />
      </SectionWrapper>

      {/* Urban Heat Island Map */}
      <SectionWrapper sectionId="urban_heat_island_map" bgMap={BUILDINGS_SECTION_BG} refs={sectionObserverRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
      <ChartLayout
        title="Urban Heat Island Map"
        description="This map shows tract-level urban heat island intensity percentile for the selected location."
        chartType="map"
        chartContent={
          <div id="urban-heat-island-map">
            <UrbanHeatIslandMap selectedLocation={currentLocation} />
          </div>
        }
        overviewContent={
          <OverviewContent
            title="Overview"
            description="Urban heat island intensity helps identify neighborhoods where built environment and land cover trap more heat, often increasing health and cooling burdens."
          >
            <p>
              Use the geography toggle to compare tract-level patterns with city and county summaries for {currentLocation}. Higher percentile values indicate relatively stronger local heat island intensity.
            </p>
          </OverviewContent>
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance"
            description="Heat intensity varies within and across communities, and those differences affect public health, energy demand, and climate adaptation planning."
          >
            <div className="methodology-details-simple">
              <h5>Cooling and Public Health</h5>
              <p>Hotter areas can experience greater heat stress and higher cooling demand, especially during prolonged warm periods.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Planning Priorities</h5>
              <p>Spatial heat patterns can inform urban forestry, reflective surfaces, cool roofs, and neighborhood-level resilience investments.</p>
            </div>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity"
            description="Heat exposure and adaptive capacity are not distributed evenly; mapping hot spots helps target equitable interventions."
          >
            <div className="methodology-details-simple">
              <h5>Disproportionate Exposure</h5>
              <p>Historically underinvested neighborhoods may have less canopy and more heat-retaining surfaces, increasing heat risk.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Targeted Investment</h5>
              <p>Place-based heat data supports prioritization of cooling infrastructure and protections where impacts are likely greatest.</p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Methodology"
            description="Urban heat island mapping uses Supabase tract and place tables with percentile-based choropleth styling."
            dataSources={[
              'CalEPA Urban Heat Island Index (processed into Supabase)',
              'Supabase tables: urban_heat_island_tract and urban_heat_island_place',
              'California tract, place, and county boundary GeoJSON layers'
            ]}
            methodology={[
              'Map loads tract-level records using uhii_percentile',
              'Choropleth colors use fixed percentile bins from 0-100',
              'Tooltip includes percentile, UHII value, and converted approximate Fahrenheit difference'
            ]}
          />
        }
        activeView={activeUrbanHeatIslandView}
        onViewChange={setActiveUrbanHeatIslandView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        onDownloadPNG={() => makeDownloadPNG('#urban-heat-island-map', 'Urban_Heat_Island_Map', currentLocation)}
        pngFilename={formatFilename(currentLocation, 'Urban_Heat_Island_Map')}
      />
      </SectionWrapper>

      {/* Market Rent Chart */}
      <SectionWrapper sectionId="housing_rent_category" bgMap={BUILDINGS_SECTION_BG} refs={sectionObserverRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
      <ChartLayout
        title="Housing Units by Rent"
        description="This chart shows the number of housing units in the selected location grouped by monthly rent category over time. Tracking rent categories helps show how the rental market is changing and whether housing is becoming more or less affordable over time."
        chartType={marketRentChartView === 'stacked' ? 'line' : 'line'}
        chartContent={
          <div id="market-rent-chart">
            <MarketRentChart key={`${currentLocation}-${marketRentChartView}`} location={currentLocation} chartType={marketRentChartView} onDataLoaded={setMarketRentData} />
          </div>
        }
        chartSelector={{
          options: allowedMarketRentOptions.map((option) => ({ value: option.value, label: option.label })),
          value: marketRentChartView,
          onChange: (value) => setMarketRentChartView(value as 'line' | 'stacked')
        }}
        overviewContent={
          marketRentChartView === 'line' ? (
            <OverviewContent
              title="Overview"
              description="This chart shows the number of housing units in this location grouped by rent price over time. Tracking rent categories helps show how the rental market is changing, and whether housing is become less affordable over time."
            >
              <p>
                {marketRentKeyInsights
                  ? `In ${marketRentKeyInsights.latestYear}, the largest share of rental units in ${currentLocation} fell within the ${marketRentKeyInsights.largestCategoryLabel} range. Compared to ${marketRentKeyInsights.earliestYear}, there has been ${marketRentKeyInsights.overallChange} in higher-rent categories.`
                  : marketRentNarrative || 'Select a location to view housing units by rent insights and trends.'}
              </p>
            </OverviewContent>
          ) : (
            <OverviewContent
              title="Overview"
              description="This chart shows the number of housing units in this location grouped by rent price over time. Tracking rent categories helps show how the rental market is changing, and whether housing is become less affordable over time."
            >
              <p>
                {marketRentKeyInsights
                  ? `In ${marketRentKeyInsights.latestYear}, the largest share of rental units in ${currentLocation} fell within the ${marketRentKeyInsights.largestCategoryLabel} range. Compared to ${marketRentKeyInsights.earliestYear}, there has been ${marketRentKeyInsights.overallChange} in higher-rent categories.`
                  : marketRentNarrativeStacked || 'Select a location to view housing units by rent insights and trends.'}
              </p>
            </OverviewContent>
          )
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance"
            description="Housing affordability is closely connected to economic stability and community well-being. Changes in rent category distributions reflect changes in housing costs, market demand, higher-end developments, and inflation."
          >
            <div className="methodology-details-simple">
              <h5>Climate &amp; Land Use Impact</h5>
              <p>If rents rise significantly in one area, residents may move farther away in search of affordable housing. This increases commute distances and emissions from transportation.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Planning &amp; Policy</h5>
              <p>Governments can use rent data to understand housing market pressures and inform zoning decisions, housing production strategies, and affordability programs.</p>
            </div>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity"
            description="Changes in rent categories affect household stability differently depending on their income bracket."
          >
            <div className="methodology-details-simple">
              <h5>Affordability Pressure</h5>
              <p>
                As more units shift into higher rent categories, lower-income households face increased financial stress or risk of displacement. Additionally, this increases the number of households that are rent-burdened, or households spending over 30% of their income on rent.
              </p>
            </div>
            <div className="methodology-details-simple">
              <h5>Housing Stability &amp; Access</h5>
              <p>
                Limited availability of lower-cost rental units reduces housing options for groups with lower incomes, such as students, seniors, and working-class residents. Rising rents also increase housing insecurity and make it harder for families to remain in their communities.
              </p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Methodology"
            description="The chart uses Census survey data to estimate the number of rental housing units in each rent category over time."
            dataSources={[
              'Data aggregated by year and standardized rent category',
              'Source: American Community Survey (ACS) DP04 table'
            ]}
            methodology={[
              'Data is fetched from the `market_rent_percent` table for the selected location\'s `geofips`.',
              'Raw ACS data categories changed in 2015. To ensure consistency across all years (2010-2022), categories are standardized to the 2015+ structure.',
              'Specifically, for years prior to 2015, the original "$500 to $749" and "$750 to $999" categories are combined into a single "$500 to $999" category to match the 2015+ data structure.',
              'The line chart displays the number of housing units over time for each standardized category as separate lines.',
              'The stacked area chart displays the same data with categories stacked to show total housing units and the proportion of each category.',
              'Missing data points are treated as zero for visualization purposes, but actual nulls are handled during data processing.'
            ]}
          />
        }
        activeView={activeView}
        onViewChange={toggleView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        appendLocationToTitle={true}
        onDownloadPNG={() => makeDownloadPNG('#market-rent-chart', 'Housing_Units_by_Rent_Category', currentLocation)}
        onDownloadCSV={() => {
          if (!marketRentData.length) return;
          const rows = marketRentData.map((r) => ({ year: r.year, rent_category: r.rent_category, estimate: r.estimate, percent: r.percent }));
          downloadDataAsCSV(rows, formatFilename(currentLocation, 'Housing_Units_by_Rent_Category_Data'));
        }}
        pngFilename={formatFilename(currentLocation, 'Housing_Units_by_Rent_Category')}
        csvFilename={formatFilename(currentLocation, 'Housing_Units_by_Rent_Category_Data')}
      />
      </SectionWrapper>

      {/* HCD Annual Progress Report — affordable housing production */}
      <SectionWrapper
        sectionId="affordable_housing_production"
        bgMap={BUILDINGS_SECTION_BG}
        refs={sectionObserverRefs}
        visible={sectionVisible}
        getSectionStyle={getSectionStyle}
      >
        <AffordableHousingAprSection />
      </SectionWrapper>

      {/* Multifamily Energy Efficiency Targeting Tool - California Data */}
      <SectionWrapper sectionId="bayren_tool" bgMap={BUILDINGS_SECTION_BG} refs={sectionObserverRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
        <CaliforniaBayRENTool />
      </SectionWrapper>

      <CustomSectionsForPage pageId="buildings" locationName={currentLocation} />
      <CustomDatasetsForPage pageId="buildings" locationName={currentLocation} />
      </div>
    </div>
  );
};


